import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Position,
  MarkerType,
  Handle,
  useReactFlow,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { OPENAI_CHAT_MODEL_OPTIONS, findDeprecatedModelSettings } from '../../constants/openaiModels';
import { CLAUDE_MODEL_OPTIONS, GEMINI_MODEL_OPTIONS } from '../../constants/providerModels';

// Import Material-UI components
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Grid,
  Chip,
  Fab,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  ListSubheader,
  FormHelperText,
  Radio,
  RadioGroup,
  FormLabel,
  Tooltip,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Message as MessageIcon,
  CloudQueue as ApiIcon,
  Input as InputIcon,
  AccountTree as BranchIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  AutoAwesome as GenerateIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';

// Import VariableManager component
import VariableManager from './VariableManager';
import MultiLanguageInput from './MultiLanguageInput';
import EnhancedMessageInput from './EnhancedMessageInput';

// Custom Node Components
const StartNode = ({ data, id }) => (
  <Box sx={{ position: 'relative' }}>
    <Card sx={{ 
      minWidth: 120, 
      backgroundColor: '#10b981', 
      color: 'white',
      boxShadow: 3,
      border: '2px solid #059669',
      cursor: 'pointer',
      '&:hover': {
        boxShadow: 6,
        transform: 'translateY(-2px)'
      }
    }}
    onClick={() => {
      console.log('Start node clicked:', id, data.step);
      if (data.onSelect) {
        data.onSelect(id, data.step);
      }
    }}>
      <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
        <StartIcon sx={{ mb: 1 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          START
        </Typography>
        {/* Show configuration status */}
        {data.step && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
            {data.step.variableMapping?.saveFromInput?.length > 0 ? (
              <Chip label="Variables" size="small" sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: 'white', 
                fontSize: '0.6rem',
                height: 16
              }} />
            ) : (
              <Chip label="Click to configure" size="small" sx={{ 
                backgroundColor: 'rgba(255,255,255,0.1)', 
                color: 'white',
                fontSize: '0.6rem',
                height: 16
              }} />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
    <Handle
      type="source"
      position={Position.Bottom}
      style={{ background: '#059669', border: '2px solid white' }}
    />
  </Box>
);

const EndNode = ({ data, id }) => (
  <Box sx={{ position: 'relative' }}>
    <Handle
      type="target"
      position={Position.Top}
      style={{ background: '#dc2626', border: '2px solid white' }}
    />
    <Card sx={{ 
      minWidth: 120, 
      backgroundColor: '#ef4444', 
      color: 'white',
      boxShadow: 3,
      border: '2px solid #dc2626',
      cursor: 'pointer',
      '&:hover': {
        boxShadow: 6,
        transform: 'translateY(-2px)'
      }
    }}
    onClick={() => {
      console.log('End node clicked:', id, data.step);
      if (data.onSelect) {
        data.onSelect(id, data.step);
      }
    }}>
      <CardContent sx={{ p: 2, textAlign: 'center', '&:last-child': { pb: 2 } }}>
        <EndIcon sx={{ mb: 1 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          END
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

const StepNode = ({ data, id }) => {
  const getNodeStyle = (type) => {
    const styles = {
      message: { backgroundColor: '#10b981', borderColor: '#059669', icon: <MessageIcon /> },
      intermediate_message: { backgroundColor: '#06b6d4', borderColor: '#0891b2', icon: <MessageIcon /> }, // Cyan color for intermediate
      api_call: { backgroundColor: '#3b82f6', borderColor: '#2563eb', icon: <ApiIcon /> },
      collect_input: { backgroundColor: '#8b5cf6', borderColor: '#7c3aed', icon: <InputIcon /> },
      branch: { backgroundColor: '#f97316', borderColor: '#ea580c', icon: <BranchIcon /> },
      variable_operation: { backgroundColor: '#ec4899', borderColor: '#db2777', icon: <Box sx={{ fontSize: 16 }}>⚙️</Box> },
      generate_message: { backgroundColor: '#9c27b0', borderColor: '#7b1fa2', icon: <GenerateIcon /> } // Purple color for AI generation
    };
    return styles[type] || { backgroundColor: '#6b7280', borderColor: '#4b5563', icon: <MessageIcon /> };
  };

  const style = getNodeStyle(data.type);
  const deprecatedModels = findDeprecatedModelSettings(data.step);
  const hasDeprecatedModel = deprecatedModels.length > 0;
  const agentEdited = Boolean(data.step?.agentEdited);
  const highlighted = hasDeprecatedModel || agentEdited;
  const deprecatedModelNames = [...new Set(deprecatedModels.map((item) => item.model))];
  const deprecatedTooltip = deprecatedModels
    .filter((item, index, list) => list.findIndex((other) => other.model === item.model) === index)
    .map((item) => `${item.model} runs as ${item.replacement}`)
    .join('. ');

  return (
    <Box sx={{ position: 'relative' }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: style.borderColor, border: '2px solid white' }}
      />
      
      <Card sx={{ 
        minWidth: 180,
        maxWidth: 250,
        backgroundColor: style.backgroundColor, 
        color: 'white',
        boxShadow: highlighted
          ? '0 0 0 4px rgba(245, 158, 11, 0.55)'
          : data.step?.isNewlyCreated ? 8 : 3,
        border: highlighted
          ? '3px solid #f59e0b'
          : data.step?.isNewlyCreated 
          ? `3px solid #fbbf24` // Golden border for new nodes
          : `2px solid ${style.borderColor}`,
        cursor: 'pointer',
        animation: highlighted
          ? 'deprecatedPulse 2.4s infinite'
          : data.step?.isNewlyCreated ? 'pulse 2s infinite' : 'none',
        '&:hover': {
          boxShadow: highlighted ? '0 0 0 4px rgba(245, 158, 11, 0.85)' : 6,
          transform: 'translateY(-2px)'
        },
        '@keyframes pulse': {
          '0%': {
            boxShadow: `0 0 0 0 rgba(251, 191, 36, 0.7)`
          },
          '70%': {
            boxShadow: `0 0 0 10px rgba(251, 191, 36, 0)`
          },
          '100%': {
            boxShadow: `0 0 0 0 rgba(251, 191, 36, 0)`
          }
        },
        '@keyframes deprecatedPulse': {
          '0%': {
            boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.75)'
          },
          '70%': {
            boxShadow: '0 0 0 10px rgba(245, 158, 11, 0)'
          },
          '100%': {
            boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)'
          }
        }
      }}
      onClick={() => {
        console.log('Node clicked:', id, data.step);
        if (data.onSelect) {
          data.onSelect(id, data.step);
        }
      }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {style.icon}
            <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
              {data.label}
            </Typography>
            <IconButton 
              size="small" 
              sx={{ color: 'white', p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                data.onDuplicate && data.onDuplicate(id, data.step);
              }}
              title="Duplicate Step"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              sx={{ color: 'white', p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                data.onDelete && data.onDelete(id);
              }}
              title="Delete Step"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
          
          {/* Step details */}
          <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
            {data.type === 'intermediate_message' ? 'INTERMEDIATE MESSAGE' : data.type.replace('_', ' ').toUpperCase()}
          </Typography>
          
          {/* Show configuration status */}
          {hasDeprecatedModel && (
            <Tooltip title={`${deprecatedTooltip}. Open this step and choose a current model.`}>
              <Chip
                icon={<WarningAmberIcon sx={{ fontSize: '14px !important', color: '#78350f !important' }} />}
                label={deprecatedModelNames.length === 1 ? deprecatedModelNames[0] : 'Deprecated models'}
                size="small"
                sx={{
                  mt: 1,
                  maxWidth: '100%',
                  height: 20,
                  backgroundColor: '#fbbf24',
                  color: '#78350f',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  '& .MuiChip-label': {
                    px: 0.75,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Tooltip>
          )}
          {agentEdited && (
            <Tooltip title="This step was changed by the flow agent. Save the flow to keep it.">
              <Chip
                label="Agent edit"
                size="small"
                sx={{
                  mt: 1,
                  maxWidth: '100%',
                  height: 20,
                  backgroundColor: '#fbbf24',
                  color: '#78350f',
                  fontWeight: 700,
                  fontSize: '0.65rem'
                }}
              />
            </Tooltip>
          )}

          {data.step && (
            <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {data.step.config && Object.keys(data.step.config).length > 0 ? (
                <Chip label="Configured" size="small" sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  color: 'white', 
                  fontSize: '0.6rem',
                  height: 16
                }} />
              ) : (
                <Chip label="Not Configured" size="small" sx={{ 
                  backgroundColor: 'rgba(239,68,68,0.3)', 
                  color: 'white',
                  fontSize: '0.6rem',
                  height: 16
                }} />
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Conditional handles for branch nodes */}
      {data.type === 'branch' ? (
        <>
          {/* Branch nodes only have left and right handles */}
          <Handle
            type="source"
            position={Position.Right}
            id="branch-true"
            style={{ 
              background: '#10b981', 
              border: '2px solid white',
              right: -8,
              top: '50%'
            }}
          />
          <Handle
            type="source"
            position={Position.Left}
            id="branch-false"
            style={{ 
              background: '#ef4444', 
              border: '2px solid white',
              left: -8,
              top: '50%'
            }}
          />
        </>
      ) : (
        /* Regular nodes have bottom output handle */
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: style.borderColor, border: '2px solid white' }}
        />
      )}
    </Box>
  );
};

// Define custom node types
const nodeTypes = {
  startNode: StartNode,
  endNode: EndNode,
  stepNode: StepNode
};

// Step type options for adding new steps
const stepTypes = [
  { type: 'message', label: 'Send Message', icon: <MessageIcon />, color: '#10b981' },
  { type: 'generate_message', label: 'Generate Message', icon: <GenerateIcon />, color: '#9c27b0' },
  { type: 'knowledge_lookup', label: 'Knowledge Lookup', icon: <Box sx={{ fontSize: 16 }}>🔍</Box>, color: '#0891b2' },
  { type: 'api_call', label: 'API Call', icon: <ApiIcon />, color: '#3b82f6' },
  { type: 'collect_input', label: 'Collect Input', icon: <InputIcon />, color: '#8b5cf6' },
  { type: 'branch', label: 'Branch Logic', icon: <BranchIcon />, color: '#f97316' },
  { type: 'variable_operation', label: 'Variable Operation', icon: <Box sx={{ fontSize: 16 }}>⚙️</Box>, color: '#ec4899' }
];

// Node Configuration Form Component
// Helper function to get operators based on condition type
const getOperatorsForConditionType = (type) => {
  switch (type) {
    case 'variable_value':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Does Not Contain' },
        { value: 'exists', label: 'Exists' },
        { value: 'not_exists', label: 'Does Not Exist' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' }
      ];
    case 'time_date':
      return [
        { value: 'before', label: 'Before' },
        { value: 'after', label: 'After' },
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' }
      ];
    case 'numeric_comparison':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'greater_equal', label: 'Greater Than or Equal' },
        { value: 'less_equal', label: 'Less Than or Equal' }
      ];
    case 'boolean_logic':
      return [
        { value: 'is_true', label: 'Is True' },
        { value: 'is_false', label: 'Is False' }
      ];
    case 'text_pattern':
      return [
        { value: 'matches', label: 'Matches (Regex)' },
        { value: 'contains', label: 'Contains' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'not_contains', label: 'Does Not Contain' }
      ];
    case 'array_length':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'greater_than', label: 'Greater Than' },
        { value: 'less_than', label: 'Less Than' },
        { value: 'greater_equal', label: 'Greater Than or Equal' },
        { value: 'less_equal', label: 'Less Than or Equal' }
      ];
    default:
      return [
        { value: 'equals', label: 'Equals' }
      ];
  }
};

const NodeConfigurationForm = ({ 
  step, 
  onStepChange, 
  availableApiEndpoints = [], 
  availableVariables = [],
  flowSteps = []
}) => {
  // All hooks must be called before any early returns
  // Language state management for message steps
  const messageContent = step?.config?.message?.content || {};
  const isMessageMultiLanguage = typeof messageContent === 'object' && messageContent !== null;
  const messageLanguages = isMessageMultiLanguage ? Object.keys(messageContent) : ['default'];
  const [currentMessageLang, setCurrentMessageLang] = useState(messageLanguages[0] || 'default');

  // Language state management for collect input steps
  const collectInputContent = step?.config?.collectInput?.message?.content || step?.config?.collectInput?.prompt || {};
  const isCollectInputMultiLanguage = typeof collectInputContent === 'object' && collectInputContent !== null;
  const collectInputLanguages = isCollectInputMultiLanguage ? Object.keys(collectInputContent) : ['default'];
  const [currentCollectInputLang, setCurrentCollectInputLang] = useState(collectInputLanguages[0] || 'default');

  // Update language state when step changes
  useEffect(() => {
    if (step?.config?.message?.content) {
      const newMessageLangs = isMessageMultiLanguage ? Object.keys(messageContent) : ['default'];
      if (!newMessageLangs.includes(currentMessageLang)) {
        setCurrentMessageLang(newMessageLangs[0] || 'default');
      }
    }
  }, [step?.config?.message?.content, isMessageMultiLanguage, currentMessageLang]);

  useEffect(() => {
    if (step?.config?.collectInput) {
      const newCollectInputLangs = isCollectInputMultiLanguage ? Object.keys(collectInputContent) : ['default'];
      if (!newCollectInputLangs.includes(currentCollectInputLang)) {
        setCurrentCollectInputLang(newCollectInputLangs[0] || 'default');
      }
    }
  }, [step?.config?.collectInput?.message?.content, step?.config?.collectInput?.prompt, isCollectInputMultiLanguage, currentCollectInputLang]);

  // Safety check: ensure step object is valid (after all hooks)
  if (!step || !step.id || !step.type) {
    console.warn('NodeConfigurationForm received invalid step:', step);
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          Invalid step data. Please try selecting the node again.
        </Typography>
      </Box>
    );
  }



  const handleFieldChange = (field, value) => {
    const updatedStep = { ...step, [field]: value };
    onStepChange(updatedStep);
  };

  const handleConfigChange = (configPath, value) => {
    console.log('🔧 [NodeConfigurationForm] handleConfigChange called:', { configPath, value });
    console.log('🔧 [NodeConfigurationForm] Current step before update:', step);
    
    const updatedStep = { 
      ...step, 
      config: {
        ...step.config,
        [configPath]: {
          ...step.config?.[configPath],
          ...value
        }
      }
    };
    
    console.log('🔧 [NodeConfigurationForm] Updated step after config change:', updatedStep);
    console.log('🔧 [NodeConfigurationForm] New config value:', updatedStep.config[configPath]);
    
    onStepChange(updatedStep);
  };

  const handleVariableMappingChange = (field, value) => {
    const updatedStep = {
      ...step,
      variableMapping: {
        ...step.variableMapping,
        [field]: value
      }
    };
    onStepChange(updatedStep);
  };

  // Helper function to get selected API endpoint
  const getSelectedApiEndpoint = (endpointId) => {
    return availableApiEndpoints.find(endpoint => endpoint._id === endpointId) || null;
  };

  // Helper function to get response fields for endpoint
  const getResponseFieldsForEndpoint = (endpoint) => {
    if (!endpoint || !endpoint.responseMapping) return [];
    
    const fields = [];
    Object.entries(endpoint.responseMapping).forEach(([key, mapping]) => {
      if (mapping.path) {
        fields.push({
          key,
          path: mapping.path,
          name: mapping.name || key,
          description: mapping.description || `Response field: ${key}`,
          type: mapping.type || 'string'
        });
      }
    });
    
    return fields;
  };

  return (
    <Box sx={{ space: 3 }}>
      {/* Basic Information */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
          Basic Information
        </Typography>
        
        <TextField
          fullWidth
          size="small"
          label="Step Name"
          value={step.name || ''}
          onChange={(e) => {
            console.log('📝 [STEP NAME FIELD] onChange triggered with value:', e.target.value);
            
            // 🎯 IMMEDIATE UPDATE APPROACH: Update step through onStepChange callback
            const updatedStep = {
              ...step,
              name: e.target.value
            };
            
            console.log('📝 [STEP NAME FIELD] Immediately updating step via onStepChange with name:', e.target.value);
            onStepChange(updatedStep);
          }}
          sx={{ mb: 2 }}
        />
        
        <Chip 
          label={`Type: ${step.type ? step.type.replace('_', ' ').toUpperCase() : 'UNKNOWN'}`}
          size="small"
          sx={{ backgroundColor: '#e5e7eb', color: '#374151' }}
        />
      </Box>

      {/* Step-specific Configuration */}
      {step.type === 'start' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            Start Node Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2, fontSize: '0.875rem' }}>
            Configure variable extraction from the initial user message that triggers this flow.
          </Typography>

          {/* Variable Extraction Section */}
          <Box sx={{ 
            border: '1px solid #e5e7eb', 
            borderRadius: 1, 
            p: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
              Extract Variables from User Input
            </Typography>
            
            {/* Input Variables List */}
            {(step.variableMapping?.saveFromInput || []).map((inputVar, index) => (
              <Box key={`input-var-${index}`} sx={{ 
                backgroundColor: '#f9fafb', 
                borderRadius: 1, 
                p: 2, 
                mb: 2,
                border: '1px solid #e5e7eb'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                    Variable {index + 1}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                      updatedInputVars.splice(index, 1);
                      handleVariableMappingChange('saveFromInput', updatedInputVars);
                    }}
                    sx={{ color: '#dc2626' }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  label="Variable Name"
                  value={inputVar.variableName || ''}
                  onChange={(e) => {
                    const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                    updatedInputVars[index] = { ...updatedInputVars[index], variableName: e.target.value };
                    handleVariableMappingChange('saveFromInput', updatedInputVars);
                  }}
                  sx={{ mb: 2 }}
                  placeholder="e.g., doctorName"
                />

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Extraction Type</InputLabel>
                  <Select
                    value={inputVar.inputType || 'direct_input'}
                    onChange={(e) => {
                      const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                      updatedInputVars[index] = { ...updatedInputVars[index], inputType: e.target.value };
                      handleVariableMappingChange('saveFromInput', updatedInputVars);
                    }}
                    label="Extraction Type"
                  >
                    <MenuItem value="direct_input">Direct Input (entire message)</MenuItem>
                    <MenuItem value="extracted_value">Extracted Value (regex pattern)</MenuItem>
                    <MenuItem value="choice_selection">Choice Selection (number extraction)</MenuItem>
                    <MenuItem value="ai_extract">🤖 AI Extraction (natural language prompt)</MenuItem>
                  </Select>
                </FormControl>

                {inputVar.inputType === 'extracted_value' && (
                  <TextField
                    fullWidth
                    size="small"
                    label="Extraction Pattern (Regex)"
                    value={inputVar.extractionPattern || ''}
                    onChange={(e) => {
                      const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                      updatedInputVars[index] = { ...updatedInputVars[index], extractionPattern: e.target.value };
                      handleVariableMappingChange('saveFromInput', updatedInputVars);
                    }}
                    sx={{ mb: 2 }}
                    placeholder="e.g., (?:Dr\\.?\\s+|Doctor\\s+)(\\w+)"
                    helperText="Use regex to extract specific parts. Example: Dr\\.?\\s+(\\w+) extracts doctor name from 'Dr. Smith'"
                  />
                )}

                {inputVar.inputType === 'ai_extract' && (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      label="AI Extraction Prompt"
                      value={inputVar.extractionPrompt || ''}
                      onChange={(e) => {
                        const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                        updatedInputVars[index] = { ...updatedInputVars[index], extractionPrompt: e.target.value };
                        handleVariableMappingChange('saveFromInput', updatedInputVars);
                      }}
                      sx={{ mb: 2 }}
                      placeholder="e.g., Extract the doctor's name from the message"
                      helperText="Describe what you want to extract in natural language. AI will intelligently find and extract the information."
                      multiline
                      rows={8}
                    />
                    
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>LLM Model for Extraction</InputLabel>
                      <Select
                        value={inputVar.extractionModel || ''}
                        label="LLM Model for Extraction"
                        onChange={(e) => {
                          const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                          updatedInputVars[index] = { ...updatedInputVars[index], extractionModel: e.target.value };
                          handleVariableMappingChange('saveFromInput', updatedInputVars);
                        }}
                      >
                        <MenuItem value="">Use Project Default</MenuItem>
                        <ListSubheader>OpenAI Models</ListSubheader>
                        {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                          <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                        ))}
                        <ListSubheader>DeepSeek Models</ListSubheader>
                        <MenuItem value="deepseek-chat">DeepSeek Chat</MenuItem>
                        <MenuItem value="deepseek-reasoner">DeepSeek Reasoner</MenuItem>
                        <ListSubheader>Qwen Models</ListSubheader>
                        <MenuItem value="qwen-max">Qwen Max</MenuItem>
                        <MenuItem value="qwen-plus">Qwen Plus</MenuItem>
                        <MenuItem value="qwen-turbo">Qwen Turbo</MenuItem>
                        <ListSubheader>Claude Models</ListSubheader>
                        {CLAUDE_MODEL_OPTIONS.map((model) => (
                          <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                        ))}
                        <ListSubheader>Gemini Models</ListSubheader>
                        {GEMINI_MODEL_OPTIONS.map((model) => (
                          <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                        ))}
                        <ListSubheader>Gemma Models</ListSubheader>
                        <MenuItem value="gemma-2">Gemma 2</MenuItem>
                        <MenuItem value="gemma-3">Gemma 3</MenuItem>
                        <ListSubheader>Meta Llama Models</ListSubheader>
                        <MenuItem value="meta-llama/Llama-3-70b-chat-hf">Llama 3 70B Chat</MenuItem>
                        <MenuItem value="meta-llama/Llama-3-8b-chat-hf">Llama 3 8B Chat</MenuItem>
                        <MenuItem value="meta-llama/Meta-Llama-3-8B-Instruct-Lite">Llama 3 8B Instruct Lite</MenuItem>
                        <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Llama 3.1 8B Instruct Turbo</MenuItem>
                        <MenuItem value="meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Llama 3.1 70B Instruct Turbo</MenuItem>
                        <MenuItem value="meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo">Llama 3.1 405B Instruct Turbo</MenuItem>
                        <MenuItem value="meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo">Llama 3.2 11B Vision Instruct</MenuItem>
                        <MenuItem value="meta-llama/Llama-3.3-70B-Instruct-Turbo">Llama 3.3 70B Instruct Turbo</MenuItem>
                        <MenuItem value="meta-llama/llama-4-scout">Llama 4 Scout</MenuItem>
                        <MenuItem value="meta-llama/llama-4-maverick">Llama 4 Maverick</MenuItem>
                        <ListSubheader>NVIDIA Models</ListSubheader>
                        <MenuItem value="nvidia/Llama-3.1-Nemotron-70B-Instruct-HF">Nemotron 70B Instruct</MenuItem>
                        <MenuItem value="nvidia/llama-3.1-nemotron-70b-instruct">Nemotron 70B Instruct (Alt)</MenuItem>
                        <ListSubheader>Other Models</ListSubheader>
                        <MenuItem value="MythoMax-L2-13b-Lite">MythoMax L2 13B Lite</MenuItem>
                      </Select>
                      <FormHelperText>Choose the LLM model to use for extraction. If not specified, the project's primary LLM will be used.</FormHelperText>
                    </FormControl>
                    
                    {inputVar.extractionPrompt && (
                      <Box sx={{ 
                        mt: 1, 
                        p: 2, 
                        backgroundColor: '#e8f5e8', 
                        borderRadius: 1,
                        border: '1px solid #4caf50'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                          🤖 AI Extraction Preview:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#2e7d32', mb: 1 }}>
                          <strong>User says:</strong> "I need to book an appointment with Dr. Johnson at City Hospital for next Friday"
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#2e7d32', mb: 1 }}>
                          <strong>Your prompt:</strong> "{inputVar.extractionPrompt}"
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#2e7d32' }}>
                          <strong>AI would extract:</strong> {inputVar.extractionPrompt.toLowerCase().includes('doctor') ? '"Dr. Johnson"' : inputVar.extractionPrompt.toLowerCase().includes('hospital') ? '"City Hospital"' : inputVar.extractionPrompt.toLowerCase().includes('date') ? '"next Friday"' : '(depends on your prompt)'}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  value={inputVar.description || ''}
                  onChange={(e) => {
                    const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                    updatedInputVars[index] = { ...updatedInputVars[index], description: e.target.value };
                    handleVariableMappingChange('saveFromInput', updatedInputVars);
                  }}
                  placeholder="Describe what this variable contains"
                  sx={{ mb: 2 }}
                />

                {/* Required Toggle - Show for all extraction types */}
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1,
                  p: 2,
                  backgroundColor: inputVar.required !== false ? '#e8f5e8' : '#fff3cd',
                  borderRadius: 1,
                  border: inputVar.required !== false ? '1px solid #4caf50' : '1px solid #ffc107'
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inputVar.required !== false} // Default to true if not specified
                        onChange={(e) => {
                          const updatedInputVars = [...(step.variableMapping?.saveFromInput || [])];
                          updatedInputVars[index] = { 
                            ...updatedInputVars[index], 
                            required: e.target.checked 
                          };
                          handleVariableMappingChange('saveFromInput', updatedInputVars);
                        }}
                        color="primary"
                      />
                    }
                    label="Required Variable"
                    sx={{ flexGrow: 1 }}
                  />
                  <Typography variant="caption" sx={{ 
                    color: inputVar.required !== false ? '#2e7d32' : '#f57c00',
                    fontWeight: 500 
                  }}>
                    {inputVar.required !== false 
                      ? 'Flow will wait for this value' 
                      : 'Flow will continue even if extraction fails'
                    }
                  </Typography>
                </Box>
              </Box>
            ))}

            {/* Add Variable Button */}
            <Button 
              variant="outlined" 
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                const newInputVar = {
                  variableName: '',
                  inputType: 'direct_input',
                  extractionPattern: '',
                  extractionPrompt: '',
                  extractionModel: '',
                  description: '',
                  required: true // Default new variables to required
                };
                const updatedInputVars = [...(step.variableMapping?.saveFromInput || []), newInputVar];
                handleVariableMappingChange('saveFromInput', updatedInputVars);
              }}
              sx={{ borderColor: '#10b981', color: '#10b981' }}
            >
              Add Input Variable
            </Button>

            {/* Usage Examples */}
            <Box sx={{ 
              mt: 3,
              p: 2, 
              backgroundColor: '#f0f9ff', 
              borderRadius: 1,
              border: '1px solid #0ea5e9'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0369a1', mb: 1 }}>
                💡 Examples:
              </Typography>
              
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1, fontWeight: 600 }}>
                Regex Patterns:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1 }}>
                • <strong>Doctor Name:</strong> <code>(?:Dr\\.?\\s+|Doctor\\s+)(\\w+)</code>
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 2 }}>
                • <strong>Phone Number:</strong> <code>(\\d{3}-\\d{3}-\\d{4})</code>
              </Typography>
              
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1, fontWeight: 600 }}>
                🤖 AI Extraction Prompts:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1 }}>
                • <strong>"Extract the doctor's name"</strong> - Finds any doctor name mentioned
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1 }}>
                • <strong>"Extract the hospital or clinic name"</strong> - Finds medical facility names
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1 }}>
                • <strong>"Extract the appointment date and time"</strong> - Finds date/time mentions
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1', mb: 1 }}>
                • <strong>"Extract the patient's symptoms or medical issue"</strong> - Finds health concerns
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#0369a1' }}>
                • <strong>"Extract the urgency level (urgent, emergency, routine)"</strong> - Finds priority indicators
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {step.type === 'end' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            End Node Configuration
          </Typography>
          
          {/* Variable Persistence Configuration */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#f9fafb' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
              💾 Variable Persistence (Optional)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2, fontSize: '0.875rem' }}>
              Save flow variables to session scope for reuse in other flows. This allows data to persist across different conversation flows.
            </Typography>
            
            {/* Enable Variable Persistence Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={step.config?.end?.persistVariables || false}
                  onChange={(e) => {
                    const updatedStep = {
                      ...step,
                      config: {
                        ...step.config,
                        end: {
                          ...step.config?.end,
                          persistVariables: e.target.checked,
                          variablesToPersist: e.target.checked ? (step.config?.end?.variablesToPersist || []) : []
                        }
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                />
              }
              label="Enable Variable Persistence"
              sx={{ mb: 2 }}
            />
            
            {/* Variable Selection */}
            {step.config?.end?.persistVariables && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151', mb: 1 }}>
                  Add Variable Names to Persist:
                </Typography>
                
                {/* Manual Variable Input - Enhanced with Add/Remove functionality */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 2, fontSize: '0.875rem' }}>
                    Add the names of variables you want to persist. You can reference the available variables list below.
                  </Typography>
                  
                  {/* List of manually added variables */}
                  {(step.config?.end?.manualVariableNames || []).map((variableName, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1, 
                      mb: 1,
                      p: 1,
                      border: '1px solid #e5e7eb',
                      borderRadius: 1,
                      backgroundColor: '#fff'
                    }}>
                      <TextField
                        size="small"
                        value={variableName}
                        onChange={(e) => {
                          const updatedManualNames = [...(step.config?.end?.manualVariableNames || [])];
                          updatedManualNames[index] = e.target.value;
                          
                          const updatedStep = {
                            ...step,
                            config: {
                              ...step.config,
                              end: {
                                ...step.config?.end,
                                manualVariableNames: updatedManualNames
                              }
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        placeholder="Variable name"
                        sx={{ flex: 1 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const updatedManualNames = [...(step.config?.end?.manualVariableNames || [])];
                          updatedManualNames.splice(index, 1);
                          
                          const updatedStep = {
                            ...step,
                            config: {
                              ...step.config,
                              end: {
                                ...step.config?.end,
                                manualVariableNames: updatedManualNames
                              }
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        sx={{ color: '#dc2626' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  
                  {/* Add new variable button */}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const currentManualNames = step.config?.end?.manualVariableNames || [];
                      const updatedManualNames = [...currentManualNames, ''];
                      
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          end: {
                            ...step.config?.end,
                            manualVariableNames: updatedManualNames
                          }
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    variant="outlined"
                    sx={{ 
                      borderStyle: 'dashed',
                      color: '#6b7280',
                      borderColor: '#d1d5db',
                      '&:hover': {
                        borderColor: '#9ca3af',
                        backgroundColor: '#f9fafb'
                      }
                    }}
                  >
                    Add Variable
                  </Button>
                  
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 1 }}>
                    Variables that exist at runtime but aren't shown in the list above
                  </Typography>
                </Box>
                
                {/* Persistence Summary */}
                {(step.config?.end?.manualVariableNames || []).filter(name => name.trim()).length > 0 && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: '#ecfdf5', border: '1px solid #d1fae5', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#065f46', mb: 1 }}>
                      ✅ Variables to persist to session scope:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#047857' }}>
                      {(step.config?.end?.manualVariableNames || []).filter(name => name.trim()).join(', ')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#047857', mt: 1, display: 'block' }}>
                      These variables will be available for use in subsequent flows within the same chat session.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
          
          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
            The end node marks the completion of the flow and cleans up temporary variables.
          </Typography>
          
          {/* Show available variables at end */}
          <Box sx={{ 
            mt: 2,
            p: 2, 
            backgroundColor: '#f3f4f6', 
            borderRadius: 1,
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: '0.75rem' }}>
              📊 Variables Available at Flow End:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {availableVariables.length > 0 
                ? availableVariables.map(v => `${v.name} (${v.source})`).join(', ')
                : 'No variables collected in this flow'
              }
            </Typography>
          </Box>
        </Box>
      )}

      {(step.type === 'message' || step.type === 'intermediate_message') && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            Message Configuration
          </Typography>
          
          {/* Message Type Toggle */}
          <Box sx={{ mb: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', color: '#374151', fontWeight: 600 }}>
                Message Type
              </FormLabel>
              <RadioGroup
                row
                value={step.type}
                onChange={(e) => {
                  console.log('🔥 RADIO BUTTON CHANGE: From', step.type, 'to', e.target.value);
                  const updatedStep = {
                    ...step,
                    type: e.target.value
                  };
                  console.log('🔥 UPDATED STEP:', updatedStep);
                  onStepChange(updatedStep);
                }}
                sx={{ mt: 1 }}
              >
                <FormControlLabel 
                  value="message" 
                  control={<Radio size="small" />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">Regular Message</Typography>
                      <Chip 
                        label="Final" 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#10b981', 
                          color: 'white', 
                          fontSize: '0.65rem', 
                          height: 18 
                        }} 
                      />
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="intermediate_message" 
                  control={<Radio size="small" />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">Intermediate Message</Typography>
                      <Chip 
                        label="Ongoing" 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#06b6d4', 
                          color: 'white', 
                          fontSize: '0.65rem', 
                          height: 18 
                        }} 
                      />
                    </Box>
                  }
                />
              </RadioGroup>
              <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
                Regular messages end the conversation. Intermediate messages allow the conversation to continue.
              </Typography>
            </FormControl>
          </Box>
          
          {/* Message Content with Multi-Language Support */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
              Message Content
            </Typography>
            
            <Box>
              {/* Language Selection */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={currentMessageLang}
                    onChange={(e) => setCurrentMessageLang(e.target.value)}
                    label="Language"
                  >
                    {messageLanguages.map(lang => (
                      <MenuItem key={lang} value={lang}>
                        {lang === 'default' ? 'Default' : 
                         lang === 'id' ? 'Indonesian' : 
                         lang === 'en' ? 'English' : 
                         lang.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Add Language Button */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const newLang = prompt('Enter language code (e.g., en, id, es):');
                    if (newLang && !messageLanguages.includes(newLang)) {
                      const currentContent = step.config?.message?.content || '';
                      const newContent = isMessageMultiLanguage ? 
                        { ...messageContent, [newLang]: '' } : 
                        { default: currentContent, [newLang]: '' };
                      
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          message: {
                            ...step.config?.message,
                            content: newContent
                          }
                        }
                      };
                      onStepChange(updatedStep);
                      setCurrentMessageLang(newLang);
                    }
                  }}
                >
                  + Add Language
                </Button>
                
                {/* Remove Language Button */}
                {messageLanguages.length > 1 && currentMessageLang !== 'default' && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      const newContent = { ...messageContent };
                      delete newContent[currentMessageLang];
                      
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          message: {
                            ...step.config?.message,
                            content: newContent
                          }
                        }
                      };
                      onStepChange(updatedStep);
                      setCurrentMessageLang('default');
                    }}
                  >
                    Remove {currentMessageLang}
                  </Button>
                )}
              </Box>
              
              {/* Message Input for Current Language */}
              <EnhancedMessageInput
                label={`Message Content (${currentMessageLang === 'default' ? 'Default' : currentMessageLang.toUpperCase()})`}
                value={isMessageMultiLanguage ? (messageContent[currentMessageLang] || '') : (messageContent || '')}
                onChange={(newValue) => {
                  console.log('📝 [ENHANCED MESSAGE FIELD] onChange triggered with value:', newValue);
                  
                  let content;
                  if (isMessageMultiLanguage || messageLanguages.length > 1) {
                    // Multi-language format
                    content = {
                      ...messageContent,
                      [currentMessageLang]: newValue
                    };
                  } else {
                    // Simple string format for single language
                    content = newValue;
                  }
                  
                  const updatedStep = {
                    ...step,
                    config: {
                      ...step.config,
                      message: {
                        ...step.config?.message,
                        content
                      }
                    }
                  };
                  
                  console.log('📝 [ENHANCED MESSAGE FIELD] Immediately updating step via onStepChange');
                  onStepChange(updatedStep);
                }}
                availableVariables={availableVariables}
                availableApiEndpoints={availableApiEndpoints}
                placeholder="Enter the message to send to the user..."
                multiline={true}
                rows={8}
                helperText="Use the 📝 button to format lists from array variables, or the <> button to insert variables."
                sx={{
                  '& .MuiTextField-root': {
                    minHeight: '350px'
                  },
                  '& .MuiInputBase-root': {
                    minHeight: '350px',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    minHeight: '320px !important',
                    resize: 'vertical'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      )}

      {step.type === 'generate_message' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            Generate Message Configuration
          </Typography>
          
          {/* AI Model Selection */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>AI Model</InputLabel>
            <Select
              value={step.config?.generateMessage?.model || 'gpt-4o-mini'}
              onChange={(e) => {
                const updatedStep = {
                  ...step,
                  config: {
                    ...step.config,
                    generateMessage: {
                      ...step.config?.generateMessage,
                      model: e.target.value
                    }
                  }
                };
                onStepChange(updatedStep);
              }}
              label="AI Model"
            >
              <ListSubheader>OpenAI Models</ListSubheader>
              {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
              ))}
              <ListSubheader>DeepSeek Models</ListSubheader>
              <MenuItem value="deepseek-chat">DeepSeek Chat</MenuItem>
              <MenuItem value="deepseek-reasoner">DeepSeek Reasoner</MenuItem>
              <ListSubheader>Claude Models</ListSubheader>
              {CLAUDE_MODEL_OPTIONS.map((model) => (
                <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Generation Prompt */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Generation Prompt"
            value={step.config?.generateMessage?.prompt || ''}
            onChange={(e) => {
              const updatedStep = {
                ...step,
                config: {
                  ...step.config,
                  generateMessage: {
                    ...step.config?.generateMessage,
                    prompt: e.target.value
                  }
                }
              };
              onStepChange(updatedStep);
            }}
            placeholder="Create a personalized response for {{patientName}} about their appointment with Dr. {{doctorName}} on {{appointmentDate}}."
            helperText="Describe what kind of message you want the AI to generate. Use {{variable_name}} to include context from previous steps."
            sx={{ mb: 2 }}
          />
          
          {/* Generation Parameters */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Temperature: {step.config?.generateMessage?.temperature || 0.7}
                </Typography>
                <Box sx={{ px: 1 }}>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={step.config?.generateMessage?.temperature || 0.7}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          generateMessage: {
                            ...step.config?.generateMessage,
                            temperature: parseFloat(e.target.value)
                          }
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    style={{ width: '100%' }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  Higher values = more creative, lower = more focused
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Max Tokens"
                value={step.config?.generateMessage?.maxTokens || 150}
                onChange={(e) => {
                  const updatedStep = {
                    ...step,
                    config: {
                      ...step.config,
                      generateMessage: {
                        ...step.config?.generateMessage,
                        maxTokens: parseInt(e.target.value) || 150
                      }
                    }
                  };
                  onStepChange(updatedStep);
                }}
                inputProps={{ min: 1, max: 4000 }}
                helperText="Maximum length of generated response"
              />
            </Grid>
          </Grid>
          
          {/* Save to Variable */}
          <TextField
            fullWidth
            size="small"
            label="Save Generated Message to Variable (Optional)"
            value={step.config?.generateMessage?.saveToVariable || ''}
            onChange={(e) => {
              const updatedStep = {
                ...step,
                config: {
                  ...step.config,
                  generateMessage: {
                    ...step.config?.generateMessage,
                    saveToVariable: e.target.value
                  }
                }
              };
              onStepChange(updatedStep);
            }}
            placeholder="generatedResponse"
            helperText="If specified, the generated message will be saved to this variable for later use."
            sx={{ mb: 2 }}
          />
          
          {/* Fallback Message */}
          <TextField
            fullWidth
            size="small"
            label="Fallback Message"
            value={step.config?.generateMessage?.fallbackMessage || 'Thank you for your message.'}
            onChange={(e) => {
              const updatedStep = {
                ...step,
                config: {
                  ...step.config,
                  generateMessage: {
                    ...step.config?.generateMessage,
                    fallbackMessage: e.target.value
                  }
                }
              };
              onStepChange(updatedStep);
            }}
            placeholder="Thank you for your message."
            helperText="Message to send if AI generation fails."
            sx={{ mb: 2 }}
          />
          
          {/* Send Immediately Option */}
          <FormControlLabel
            control={
              <Switch
                checked={step.config?.generateMessage?.sendImmediately !== false}
                onChange={(e) => {
                  const updatedStep = {
                    ...step,
                    config: {
                      ...step.config,
                      generateMessage: {
                        ...step.config?.generateMessage,
                        sendImmediately: e.target.checked
                      }
                    }
                  };
                  onStepChange(updatedStep);
                }}
                size="small"
              />
            }
            label="Send immediately after generation"
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: -1 }}>
            When enabled, the generated message will be sent immediately. When disabled, it will be saved for later use.
          </Typography>
        </Box>
      )}

      {step.type === 'knowledge_lookup' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            Knowledge Lookup Configuration
          </Typography>
          
          {/* Search Query */}
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Search Query"
            placeholder="Enter search query (you can use variables like {{specialty_name}})"
            value={step.config?.knowledgeLookup?.query || ''}
            onChange={(e) => {
              const updatedStep = {
                ...step,
                config: {
                  ...step.config,
                  knowledgeLookup: {
                    ...step.config?.knowledgeLookup,
                    query: e.target.value
                  }
                }
              };
              onStepChange(updatedStep);
            }}
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: -1, mb: 2 }}>
            Use variables in double curly braces: {'{'}{'}'}{'{'}variable_name{'}'}{'}'} 
          </Typography>

          {/* Result Handling */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Result Handling</InputLabel>
            <Select
              value={step.config?.knowledgeLookup?.resultHandling?.selectionType || 'first'}
              onChange={(e) => {
                const updatedStep = {
                  ...step,
                  config: {
                    ...step.config,
                    knowledgeLookup: {
                      ...step.config?.knowledgeLookup,
                      resultHandling: {
                        ...step.config?.knowledgeLookup?.resultHandling,
                        selectionType: e.target.value
                      }
                    }
                  }
                };
                onStepChange(updatedStep);
              }}
              label="Result Handling"
            >
              <MenuItem value="first">First Result Only</MenuItem>
              <MenuItem value="top_n">Top N Results</MenuItem>
              <MenuItem value="all">All Results</MenuItem>
            </Select>
          </FormControl>

          {/* Top N input when needed */}
          {step.config?.knowledgeLookup?.resultHandling?.selectionType === 'top_n' && (
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Number of Results"
              placeholder="5"
              value={step.config?.knowledgeLookup?.resultHandling?.topN || 5}
              onChange={(e) => {
                const updatedStep = {
                  ...step,
                  config: {
                    ...step.config,
                    knowledgeLookup: {
                      ...step.config?.knowledgeLookup,
                      resultHandling: {
                        ...step.config?.knowledgeLookup?.resultHandling,
                        topN: parseInt(e.target.value) || 5
                      }
                    }
                  }
                };
                onStepChange(updatedStep);
              }}
              inputProps={{ min: 1, max: 20 }}
              sx={{ mb: 2 }}
            />
          )}

          {/* Variable Extractions */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
            Variable Extractions
          </Typography>
          {(step.config?.knowledgeLookup?.extractVariables || []).map((extraction, index) => (
            <Box key={index} sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 2, mb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label="Variable Name"
                  placeholder="e.g., doctor_id"
                  value={extraction.variableName || ''}
                  onChange={(e) => {
                    const updatedExtractions = [...(step.config?.knowledgeLookup?.extractVariables || [])];
                    updatedExtractions[index] = { ...updatedExtractions[index], variableName: e.target.value };
                    const updatedStep = {
                      ...step,
                      config: {
                        ...step.config,
                        knowledgeLookup: {
                          ...step.config?.knowledgeLookup,
                          extractVariables: updatedExtractions
                        }
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                />
                <TextField
                  size="small"
                  label="Source Path"
                  placeholder="e.g., metadata.id"
                  value={extraction.sourcePath || ''}
                  onChange={(e) => {
                    const updatedExtractions = [...(step.config?.knowledgeLookup?.extractVariables || [])];
                    updatedExtractions[index] = { ...updatedExtractions[index], sourcePath: e.target.value };
                    const updatedStep = {
                      ...step,
                      config: {
                        ...step.config,
                        knowledgeLookup: {
                          ...step.config?.knowledgeLookup,
                          extractVariables: updatedExtractions
                        }
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 1 }}>
                <FormControl size="small">
                  <InputLabel>Combine Method</InputLabel>
                  <Select
                    value={extraction.combineMethod || 'first_only'}
                    onChange={(e) => {
                      const updatedExtractions = [...(step.config?.knowledgeLookup?.extractVariables || [])];
                      updatedExtractions[index] = { ...updatedExtractions[index], combineMethod: e.target.value };
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          knowledgeLookup: {
                            ...step.config?.knowledgeLookup,
                            extractVariables: updatedExtractions
                          }
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Combine Method"
                  >
                    <MenuItem value="first_only">First Only</MenuItem>
                    <MenuItem value="array">Array</MenuItem>
                    <MenuItem value="comma_separated">Comma Separated</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Data Type</InputLabel>
                  <Select
                    value={extraction.dataType || 'string'}
                    onChange={(e) => {
                      const updatedExtractions = [...(step.config?.knowledgeLookup?.extractVariables || [])];
                      updatedExtractions[index] = { ...updatedExtractions[index], dataType: e.target.value };
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          knowledgeLookup: {
                            ...step.config?.knowledgeLookup,
                            extractVariables: updatedExtractions
                          }
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Data Type"
                  >
                    <MenuItem value="string">String</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="boolean">Boolean</MenuItem>
                    <MenuItem value="array">Array</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  color="error"
                  onClick={() => {
                    const updatedExtractions = (step.config?.knowledgeLookup?.extractVariables || []).filter((_, i) => i !== index);
                    const updatedStep = {
                      ...step,
                      config: {
                        ...step.config,
                        knowledgeLookup: {
                          ...step.config?.knowledgeLookup,
                          extractVariables: updatedExtractions
                        }
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                  sx={{ height: 'fit-content' }}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
          
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              // Ensure we have proper knowledge lookup config structure
              const currentKnowledgeLookup = step.config?.knowledgeLookup || {
                query: '',
                resultHandling: {
                  selectionType: 'first',
                  topN: 5,
                  minimumScore: 0.0
                },
                extractVariables: [],
                fallbackBehavior: 'continue',
                fallbackValues: []
              };
              
              const newExtraction = {
                variableName: '',
                sourcePath: 'metadata.id',
                combineMethod: 'first_only',
                dataType: 'string'
              };
              
              const currentExtractions = currentKnowledgeLookup.extractVariables || [];
              const updatedExtractions = [...currentExtractions, newExtraction];
              
              const updatedStep = {
                ...step,
                config: {
                  ...step.config,
                  knowledgeLookup: {
                    ...currentKnowledgeLookup,
                    extractVariables: updatedExtractions
                  }
                }
              };
              
              onStepChange(updatedStep);
            }}
            sx={{ mb: 2 }}
          >
            Add Variable Extraction
          </Button>

          {/* Fallback Behavior */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Fallback Behavior</InputLabel>
            <Select
              value={step.config?.knowledgeLookup?.fallbackBehavior || 'continue'}
              onChange={(e) => {
                const updatedStep = {
                  ...step,
                  config: {
                    ...step.config,
                    knowledgeLookup: {
                      ...step.config?.knowledgeLookup,
                      fallbackBehavior: e.target.value
                    }
                  }
                };
                onStepChange(updatedStep);
              }}
              label="Fallback Behavior"
            >
              <MenuItem value="continue">Continue Flow</MenuItem>
              <MenuItem value="set_default">Set Default Values</MenuItem>
              <MenuItem value="show_error">Show Error Message</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {step.type === 'api_call' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            API Call Configuration
          </Typography>
          
          {/* API Endpoint Selection */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>API Endpoint *</InputLabel>
            <Select
              value={(() => {
                const endpointValue = step.config?.apiCall?.apiEndpoint || '';
                console.log('🎯 DROPDOWN VALUE DEBUG:');
                console.log('  - step.config?.apiCall?.apiEndpoint:', step.config?.apiCall?.apiEndpoint);
                console.log('  - Final dropdown value:', endpointValue);
                console.log('  - Available endpoints:', availableApiEndpoints.map(ep => ({ id: ep._id, name: ep.name })));
                return endpointValue;
              })()}
              onChange={(e) => {
                // Reset mappings when endpoint changes (like in List View)
                const updatedStep = {
                  ...step,
                  config: {
                    ...step.config,
                    apiCall: {
                      ...step.config?.apiCall,
                      apiEndpoint: e.target.value,
                      parameterMapping: {},
                      parameters: {}
                    }
                  },
                  variableMapping: {
                    ...step.variableMapping,
                    saveFromResponse: {}
                  }
                };
                onStepChange(updatedStep);
              }}
              label="API Endpoint *"
            >
              <MenuItem value="">
                <em>Select API Endpoint</em>
              </MenuItem>
              {availableApiEndpoints.map((endpoint) => (
                <MenuItem key={endpoint._id} value={endpoint._id}>
                  {endpoint.name} ({endpoint.method} {endpoint.url})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {availableApiEndpoints.length === 0 && (
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem', mb: 2 }}>
              No API endpoints found. Create API endpoints first in the API Manager.
            </Typography>
          )}

          {/* Show endpoint details and configuration if selected */}
          {step.config?.apiCall?.apiEndpoint && (() => {
            const selectedEndpoint = getSelectedApiEndpoint(step.config.apiCall.apiEndpoint);
            if (!selectedEndpoint) return null;

            return (
              <Box sx={{ space: 2 }}>
                {/* Endpoint Details */}
                <Box sx={{ 
                  p: 2, 
                  mb: 2,
                  backgroundColor: '#eff6ff', 
                  border: '1px solid #bfdbfe',
                  borderRadius: 1 
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                    Endpoint Details
                  </Typography>
                  <Box sx={{ fontSize: '0.75rem', color: '#1e40af', space: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      <strong>Method:</strong> {selectedEndpoint.method}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      <strong>URL:</strong> {selectedEndpoint.url}
                    </Typography>
                    {selectedEndpoint.description && (
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        <strong>Description:</strong> {selectedEndpoint.description}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Parameter Mapping */}
                {selectedEndpoint.parameters && Array.isArray(selectedEndpoint.parameters) && selectedEndpoint.parameters.length > 0 && (
                  <Box sx={{ 
                    border: '1px solid #e5e7eb', 
                    borderRadius: 1, 
                    p: 2, 
                    mb: 2 
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                      🔧 API Parameters
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 2 }}>
                      Configure the parameters to send to the API. You can use static values or placeholders.
                    </Typography>
                    
                    <Box sx={{ space: 2 }}>
                      {(() => {
                        console.log('🔍 PARAMETER DEBUG:');
                        console.log('  - selectedEndpoint.parameters:', selectedEndpoint.parameters);
                        console.log('  - typeof selectedEndpoint.parameters:', typeof selectedEndpoint.parameters);
                        console.log('  - Array.isArray(selectedEndpoint.parameters):', Array.isArray(selectedEndpoint.parameters));
                        console.log('  - step.config?.apiCall?.parameters:', step.config?.apiCall?.parameters);
                        return null;
                      })()}
                      {(selectedEndpoint.parameters || []).map((paramConfig, index) => {
                        const paramKey = paramConfig.name;
                        return (
                        <Box key={paramKey} sx={{ 
                          backgroundColor: '#f9fafb', 
                          borderRadius: 1, 
                          p: 2, 
                          mb: 2 
                        }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                                {paramKey}
                                {paramConfig.required && (
                                  <span style={{ marginLeft: 4, color: '#dc2626', fontSize: '0.75rem' }}>*</span>
                                )}
                              </Typography>
                            </Box>
                            <Chip 
                              label={paramConfig.type || 'string'}
                              size="small"
                              sx={{ backgroundColor: '#e5e7eb', color: '#6b7280', fontSize: '0.65rem', height: 20 }}
                            />
                          </Box>
                          
                          {paramConfig.description && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 1 }}>
                              {paramConfig.description}
                            </Typography>
                          )}

                          {/* Parameter Source Selection */}
                          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel sx={{ fontSize: '0.75rem' }}>Parameter Source:</InputLabel>
                            <Select
                              value={step.config?.apiCall?.parameters?.[paramKey]?.type || 'static'}
                              onChange={(e) => {
                                const sourceType = e.target.value;
                                const updatedStep = {
                                  ...step,
                                  config: {
                                    ...step.config,
                                    apiCall: {
                                      ...step.config?.apiCall,
                                      parameters: {
                                        ...step.config?.apiCall?.parameters,
                                        [paramKey]: {
                                          type: sourceType,
                                          value: '',
                                          extractPrompt: sourceType === 'ai_extract' ? `Extract the ${paramKey} from the user message` : '',
                                          extractionModel: sourceType === 'ai_extract' ? '' : undefined,
                                          variableName: sourceType === 'previous_variable' ? '' : undefined
                                        }
                                      }
                                    }
                                  }
                                };
                                onStepChange(updatedStep);
                              }}
                              label="Parameter Source:"
                              sx={{ fontSize: '0.75rem' }}
                            >
                              <MenuItem value="static">Static Value</MenuItem>
                              <MenuItem value="previous_variable">Variable from Previous Step</MenuItem>
                              <MenuItem value="user_message">Full User Message</MenuItem>
                              <MenuItem value="ai_extract">AI Extract from Message</MenuItem>
                              <MenuItem value="user_data">User Data (name, email, etc.)</MenuItem>
                            </Select>
                          </FormControl>

                          {/* Parameter Value Input */}
                          {(() => {
                            const paramType = step.config?.apiCall?.parameters?.[paramKey]?.type || 'static';
                            const paramValue = step.config?.apiCall?.parameters?.[paramKey]?.value || '';
                            const paramVariableName = step.config?.apiCall?.parameters?.[paramKey]?.variableName || '';
                            
                            switch (paramType) {
                              case 'static':
                                return (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    value={paramValue}
                                    onChange={(e) => {
                                      const updatedStep = {
                                        ...step,
                                        config: {
                                          ...step.config,
                                          apiCall: {
                                            ...step.config?.apiCall,
                                            parameters: {
                                              ...step.config?.apiCall?.parameters,
                                              [paramKey]: {
                                                ...step.config?.apiCall?.parameters?.[paramKey],
                                                value: e.target.value
                                              }
                                            }
                                          }
                                        }
                                      };
                                      onStepChange(updatedStep);
                                    }}
                                    placeholder={paramConfig.example || `Enter ${paramKey} value`}
                                  />
                                );
                              
                              case 'previous_variable':
                                return (
                                  <Box sx={{ space: 1 }}>
                                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                      <InputLabel>Select Variable</InputLabel>
                                      <Select
                                        value={paramVariableName}
                                        onChange={(e) => {
                                          const updatedStep = {
                                            ...step,
                                            config: {
                                              ...step.config,
                                              apiCall: {
                                                ...step.config?.apiCall,
                                                parameters: {
                                                  ...step.config?.apiCall?.parameters,
                                                  [paramKey]: {
                                                    ...step.config?.apiCall?.parameters?.[paramKey],
                                                    variableName: e.target.value,
                                                    value: `{{${e.target.value}}}`
                                                  }
                                                }
                                              }
                                            }
                                          };
                                          onStepChange(updatedStep);
                                        }}
                                        label="Select Variable"
                                        displayEmpty
                                      >
                                        <MenuItem value="">Select a variable...</MenuItem>
                                        {availableVariables.map((variable) => (
                                          <MenuItem key={variable.name} value={variable.name}>
                                            {variable.name} ({variable.type}) - from {variable.source}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    
                                    {availableVariables.length === 0 && (
                                      <Box sx={{ 
                                        backgroundColor: '#fef3c7', 
                                        p: 2, 
                                        borderRadius: 1, 
                                        border: '1px solid #fbbf24',
                                        mb: 1
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#92400e' }}>
                                          <strong>No variables available:</strong> Add steps before this one that save variables to use them here.
                                        </Typography>
                                      </Box>
                                    )}
                                    
                                    {paramVariableName && (
                                      <Box sx={{ 
                                        backgroundColor: '#f0fdf4', 
                                        p: 2, 
                                        borderRadius: 1, 
                                        border: '1px solid #bbf7d0' 
                                      }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#166534' }}>
                                          <strong>Will send:</strong> The value of variable "{paramVariableName}" from a previous step
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#16a34a', mt: 0.5 }}>
                                          Preview: {`{{${paramVariableName}}}`}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              
                              case 'user_message':
                                return (
                                  <Box sx={{ 
                                    backgroundColor: '#eff6ff', 
                                    p: 2, 
                                    borderRadius: 1, 
                                    border: '1px solid #bfdbfe' 
                                  }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1e40af', mb: 1 }}>
                                      <strong>Will send:</strong> The complete user message as-is
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#3b82f6' }}>
                                      Example: If user says "Saya lagi cari dokter penyakit dalam", 
                                      the entire message will be sent to the API.
                                    </Typography>
                                  </Box>
                                );
                              
                              case 'ai_extract':
                                return (
                                  <Box sx={{ space: 1 }}>
                                    <TextField
                                      fullWidth
                                      multiline
                                      rows={2}
                                      size="small"
                                      value={step.config?.apiCall?.parameters?.[paramKey]?.extractPrompt || ''}
                                      onChange={(e) => {
                                        const updatedStep = {
                                          ...step,
                                          config: {
                                            ...step.config,
                                            apiCall: {
                                              ...step.config?.apiCall,
                                              parameters: {
                                                ...step.config?.apiCall?.parameters,
                                                [paramKey]: {
                                                  ...step.config?.apiCall?.parameters?.[paramKey],
                                                  extractPrompt: e.target.value
                                                }
                                              }
                                            }
                                          }
                                        };
                                        onStepChange(updatedStep);
                                      }}
                                      placeholder={`Extract the ${paramKey} from the user message`}
                                      sx={{ mb: 1 }}
                                    />
                                    
                                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                      <InputLabel>LLM Model for Extraction</InputLabel>
                                      <Select
                                        value={step.config?.apiCall?.parameters?.[paramKey]?.extractionModel || ''}
                                        label="LLM Model for Extraction"
                                        onChange={(e) => {
                                          const updatedStep = {
                                            ...step,
                                            config: {
                                              ...step.config,
                                              apiCall: {
                                                ...step.config?.apiCall,
                                                parameters: {
                                                  ...step.config?.apiCall?.parameters,
                                                  [paramKey]: {
                                                    ...step.config?.apiCall?.parameters?.[paramKey],
                                                    extractionModel: e.target.value
                                                  }
                                                }
                                              }
                                            }
                                          };
                                          onStepChange(updatedStep);
                                        }}
                                      >
                                        <MenuItem value="">Use Project Default</MenuItem>
                                        <ListSubheader>OpenAI Models</ListSubheader>
                                        {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                                          <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                                        ))}
                                        <ListSubheader>DeepSeek Models</ListSubheader>
                                        <MenuItem value="deepseek-chat">DeepSeek Chat</MenuItem>
                                        <MenuItem value="deepseek-reasoner">DeepSeek Reasoner</MenuItem>
                                        <ListSubheader>Qwen Models</ListSubheader>
                                        <MenuItem value="qwen-max">Qwen Max</MenuItem>
                                        <MenuItem value="qwen-plus">Qwen Plus</MenuItem>
                                        <MenuItem value="qwen-turbo">Qwen Turbo</MenuItem>
                                        <ListSubheader>Claude Models</ListSubheader>
                                        {CLAUDE_MODEL_OPTIONS.map((model) => (
                                          <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                                        ))}
                                        <ListSubheader>Gemini Models</ListSubheader>
                                        {GEMINI_MODEL_OPTIONS.map((model) => (
                                          <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                                        ))}
                                        <ListSubheader>Gemma Models</ListSubheader>
                                        <MenuItem value="gemma-2">Gemma 2</MenuItem>
                                        <MenuItem value="gemma-3">Gemma 3</MenuItem>
                                        <ListSubheader>Meta Llama Models</ListSubheader>
                                        <MenuItem value="meta-llama/Llama-3-70b-chat-hf">Llama 3 70B Chat</MenuItem>
                                        <MenuItem value="meta-llama/Llama-3-8b-chat-hf">Llama 3 8B Chat</MenuItem>
                                        <MenuItem value="meta-llama/Meta-Llama-3-8B-Instruct-Lite">Llama 3 8B Instruct Lite</MenuItem>
                                        <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Llama 3.1 8B Instruct Turbo</MenuItem>
                                        <MenuItem value="meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Llama 3.1 70B Instruct Turbo</MenuItem>
                                        <MenuItem value="meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo">Llama 3.1 405B Instruct Turbo</MenuItem>
                                        <MenuItem value="meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo">Llama 3.2 11B Vision Instruct</MenuItem>
                                        <MenuItem value="meta-llama/Llama-3.3-70B-Instruct-Turbo">Llama 3.3 70B Instruct Turbo</MenuItem>
                                        <MenuItem value="meta-llama/llama-4-scout">Llama 4 Scout</MenuItem>
                                        <MenuItem value="meta-llama/llama-4-maverick">Llama 4 Maverick</MenuItem>
                                        <ListSubheader>NVIDIA Models</ListSubheader>
                                        <MenuItem value="nvidia/Llama-3.1-Nemotron-70B-Instruct-HF">Nemotron 70B Instruct</MenuItem>
                                        <MenuItem value="nvidia/llama-3.1-nemotron-70b-instruct">Nemotron 70B Instruct (Alt)</MenuItem>
                                        <ListSubheader>Other Models</ListSubheader>
                                        <MenuItem value="MythoMax-L2-13b-Lite">MythoMax L2 13B Lite</MenuItem>
                                      </Select>
                                      <FormHelperText>Choose the LLM model to use for extraction. If not specified, the project's primary LLM will be used.</FormHelperText>
                                    </FormControl>
                                    <Box sx={{ 
                                      backgroundColor: '#f0fdf4', 
                                      p: 2, 
                                      borderRadius: 1, 
                                      border: '1px solid #bbf7d0' 
                                    }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#166534', mb: 1 }}>
                                        <strong>AI Extraction:</strong> Uses AI to extract specific information
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#16a34a', mb: 1 }}>
                                        <strong>Example prompt:</strong> "Extract the medical specialty name from the user message"
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#16a34a' }}>
                                        Input: "Saya lagi cari dokter penyakit dalam" → Output: "penyakit dalam"
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              
                              case 'user_data':
                                return (
                                  <Box sx={{ space: 1 }}>
                                    <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                      <Select
                                        value={paramValue}
                                        onChange={(e) => {
                                          const updatedStep = {
                                            ...step,
                                            config: {
                                              ...step.config,
                                              apiCall: {
                                                ...step.config?.apiCall,
                                                parameters: {
                                                  ...step.config?.apiCall?.parameters,
                                                  [paramKey]: {
                                                    ...step.config?.apiCall?.parameters?.[paramKey],
                                                    value: e.target.value
                                                  }
                                                }
                                              }
                                            }
                                          };
                                          onStepChange(updatedStep);
                                        }}
                                        displayEmpty
                                      >
                                        <MenuItem value="">Select user data field</MenuItem>
                                        <MenuItem value="user_name">User Name</MenuItem>
                                        <MenuItem value="user_email">User Email</MenuItem>
                                        <MenuItem value="user_phone">User Phone</MenuItem>
                                        <MenuItem value="user_id">User ID</MenuItem>
                                        <MenuItem value="session_id">Session ID</MenuItem>
                                        <MenuItem value="user_location">User Location</MenuItem>
                                        <MenuItem value="user_language">User Language</MenuItem>
                                        <MenuItem value="user_timezone">User Timezone</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <Box sx={{ 
                                      backgroundColor: '#faf5ff', 
                                      p: 2, 
                                      borderRadius: 1, 
                                      border: '1px solid #e9d5ff' 
                                    }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#7c3aed' }}>
                                        <strong>User Data:</strong> Pulls information about the current user
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              
                              default:
                                return null;
                            }
                          })()}
                        </Box>
                      )})}
                    </Box>
                  </Box>
                )}

                {/* POST Body Configuration */}
                {selectedEndpoint.method && ['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.method.toUpperCase()) && (
                  <Box sx={{ 
                    border: '2px solid #3b82f6', 
                    borderRadius: 1, 
                    p: 2, 
                    mb: 2,
                    backgroundColor: '#eff6ff'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8', mb: 2, fontSize: '16px' }}>
                      🚀 POST REQUEST BODY CONFIGURATION
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 2 }}>
                      Configure the request body for this {selectedEndpoint.method} request.
                    </Typography>

                    {/* Body Type Selection */}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Body Type</InputLabel>
                      <Select
                        value={step.config?.apiCall?.bodyType || 'none'}
                        onChange={(e) => {
                          const bodyType = e.target.value;
                          const updatedStep = {
                            ...step,
                            config: {
                              ...step.config,
                              apiCall: {
                                ...step.config?.apiCall,
                                bodyType: bodyType,
                                // Reset body-specific fields when type changes
                                bodyFields: bodyType === 'form-data' || bodyType === 'x-www-form-urlencoded' ? [] : undefined,
                                rawBodyTemplate: bodyType === 'raw' ? '' : undefined,
                                rawBodyContentType: bodyType === 'raw' ? 'application/json' : undefined,
                                graphqlQuery: bodyType === 'graphql' ? '' : undefined,
                                graphqlVariables: bodyType === 'graphql' ? '' : undefined
                              }
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        label="Body Type"
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="form-data">Form Data</MenuItem>
                        <MenuItem value="x-www-form-urlencoded">URL Encoded Form</MenuItem>
                        <MenuItem value="raw">Raw</MenuItem>
                        <MenuItem value="binary">Binary</MenuItem>
                        <MenuItem value="graphql">GraphQL</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Form Data / URL Encoded Form Fields */}
                    {(step.config?.apiCall?.bodyType === 'form-data' || step.config?.apiCall?.bodyType === 'x-www-form-urlencoded') && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                          Body Fields
                        </Typography>
                        
                        {/* List of current body fields */}
                        {(step.config?.apiCall?.bodyFields || []).map((field, index) => (
                          <Box key={index} sx={{ 
                            backgroundColor: '#f9fafb', 
                            borderRadius: 1, 
                            p: 2, 
                            mb: 2,
                            border: '1px solid #e5e7eb'
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                                Field {index + 1}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                  updatedFields.splice(index, 1);
                                  const updatedStep = {
                                    ...step,
                                    config: {
                                      ...step.config,
                                      apiCall: {
                                        ...step.config?.apiCall,
                                        bodyFields: updatedFields
                                      }
                                    }
                                  };
                                  onStepChange(updatedStep);
                                }}
                                sx={{ color: '#ef4444' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            {/* Field Key */}
                            <TextField
                              fullWidth
                              size="small"
                              label="Field Key"
                              value={field.key || ''}
                              onChange={(e) => {
                                const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                updatedFields[index] = { ...field, key: e.target.value };
                                const updatedStep = {
                                  ...step,
                                  config: {
                                    ...step.config,
                                    apiCall: {
                                      ...step.config?.apiCall,
                                      bodyFields: updatedFields
                                    }
                                  }
                                };
                                onStepChange(updatedStep);
                              }}
                              placeholder="e.g., patient_name"
                              sx={{ mb: 1 }}
                            />
                            
                            {/* Field Type */}
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <InputLabel>Field Type</InputLabel>
                              <Select
                                value={field.type || 'string'}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                  updatedFields[index] = { ...field, type: e.target.value };
                                  const updatedStep = {
                                    ...step,
                                    config: {
                                      ...step.config,
                                      apiCall: {
                                        ...step.config?.apiCall,
                                        bodyFields: updatedFields
                                      }
                                    }
                                  };
                                  onStepChange(updatedStep);
                                }}
                                label="Field Type"
                              >
                                <MenuItem value="string">String</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="boolean">Boolean</MenuItem>
                                <MenuItem value="object">Object</MenuItem>
                                <MenuItem value="array">Array</MenuItem>
                                <MenuItem value="file">File</MenuItem>
                              </Select>
                            </FormControl>
                            
                            {/* Field Value Source */}
                            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                              <InputLabel>Value Source</InputLabel>
                              <Select
                                value={field.valueSource || 'static'}
                                onChange={(e) => {
                                  const valueSource = e.target.value;
                                  const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                  updatedFields[index] = { 
                                    ...field, 
                                    valueSource: valueSource,
                                    value: '',
                                    extractPrompt: valueSource === 'ai_extract' ? `Extract the ${field.key} from the user message` : '',
                                    extractionModel: valueSource === 'ai_extract' ? '' : undefined,
                                    variableName: valueSource === 'previous_variable' ? '' : undefined
                                  };
                                  const updatedStep = {
                                    ...step,
                                    config: {
                                      ...step.config,
                                      apiCall: {
                                        ...step.config?.apiCall,
                                        bodyFields: updatedFields
                                      }
                                    }
                                  };
                                  onStepChange(updatedStep);
                                }}
                                label="Value Source"
                              >
                                <MenuItem value="static">Static Value</MenuItem>
                                <MenuItem value="previous_variable">Variable from Previous Step</MenuItem>
                                <MenuItem value="user_message">Full User Message</MenuItem>
                                <MenuItem value="ai_extract">AI Extract from Message</MenuItem>
                                <MenuItem value="user_data">User Data (name, email, etc.)</MenuItem>
                              </Select>
                            </FormControl>
                            
                            {/* Value Input based on source type */}
                            {field.valueSource === 'static' && (
                              <TextField
                                fullWidth
                                size="small"
                                label="Static Value"
                                value={field.value || ''}
                                onChange={(e) => {
                                  const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                  updatedFields[index] = { ...field, value: e.target.value };
                                  const updatedStep = {
                                    ...step,
                                    config: {
                                      ...step.config,
                                      apiCall: {
                                        ...step.config?.apiCall,
                                        bodyFields: updatedFields
                                      }
                                    }
                                  };
                                  onStepChange(updatedStep);
                                }}
                                placeholder="Enter static value"
                              />
                            )}
                            
                            {field.valueSource === 'previous_variable' && (
                              <FormControl fullWidth size="small">
                                <InputLabel>Select Variable</InputLabel>
                                <Select
                                  value={field.variableName || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                    updatedFields[index] = { 
                                      ...field, 
                                      variableName: e.target.value,
                                      value: `{{${e.target.value}}}`
                                    };
                                    const updatedStep = {
                                      ...step,
                                      config: {
                                        ...step.config,
                                        apiCall: {
                                          ...step.config?.apiCall,
                                          bodyFields: updatedFields
                                        }
                                      }
                                    };
                                    onStepChange(updatedStep);
                                  }}
                                  label="Select Variable"
                                  displayEmpty
                                >
                                  <MenuItem value="">Select a variable...</MenuItem>
                                  {availableVariables.map((variable) => (
                                    <MenuItem key={variable.name} value={variable.name}>
                                      {variable.name} ({variable.type}) - from {variable.source}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            
                            {field.valueSource === 'ai_extract' && (
                              <Box>
                                <TextField
                                  fullWidth
                                  multiline
                                  rows={2}
                                  size="small"
                                  label="Extraction Prompt"
                                  value={field.extractPrompt || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                    updatedFields[index] = { ...field, extractPrompt: e.target.value };
                                    const updatedStep = {
                                      ...step,
                                      config: {
                                        ...step.config,
                                        apiCall: {
                                          ...step.config?.apiCall,
                                          bodyFields: updatedFields
                                        }
                                      }
                                    };
                                    onStepChange(updatedStep);
                                  }}
                                  placeholder={`Extract the ${field.key} from the user message`}
                                  sx={{ mb: 1 }}
                                />
                                
                                <FormControl fullWidth size="small">
                                  <InputLabel>LLM Model for Extraction</InputLabel>
                                  <Select
                                    value={field.extractionModel || ''}
                                    label="LLM Model for Extraction"
                                    onChange={(e) => {
                                      const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                      updatedFields[index] = { ...field, extractionModel: e.target.value };
                                      const updatedStep = {
                                        ...step,
                                        config: {
                                          ...step.config,
                                          apiCall: {
                                            ...step.config?.apiCall,
                                            bodyFields: updatedFields
                                          }
                                        }
                                      };
                                      onStepChange(updatedStep);
                                    }}
                                  >
                                    <MenuItem value="">Use Project Default</MenuItem>
                                    <ListSubheader>OpenAI Models</ListSubheader>
                                    {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                                      <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                                    ))}
                                    <ListSubheader>DeepSeek Models</ListSubheader>
                                    <MenuItem value="deepseek-chat">DeepSeek Chat</MenuItem>
                                    <MenuItem value="deepseek-reasoner">DeepSeek Reasoner</MenuItem>
                                    <ListSubheader>Qwen Models</ListSubheader>
                                    <MenuItem value="qwen-max">Qwen Max</MenuItem>
                                    <MenuItem value="qwen-plus">Qwen Plus</MenuItem>
                                    <MenuItem value="qwen-turbo">Qwen Turbo</MenuItem>
                                    <ListSubheader>Claude Models</ListSubheader>
                                    {CLAUDE_MODEL_OPTIONS.map((model) => (
                                      <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                                    ))}
                                    <ListSubheader>Gemini Models</ListSubheader>
                                    {GEMINI_MODEL_OPTIONS.map((model) => (
                                      <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                                    ))}
                                    <ListSubheader>Gemma Models</ListSubheader>
                                    <MenuItem value="gemma-2">Gemma 2</MenuItem>
                                    <MenuItem value="gemma-3">Gemma 3</MenuItem>
                                    <ListSubheader>Meta Llama Models</ListSubheader>
                                    <MenuItem value="meta-llama/Llama-3-70b-chat-hf">Llama 3 70B Chat</MenuItem>
                                    <MenuItem value="meta-llama/Llama-3-8b-chat-hf">Llama 3 8B Chat</MenuItem>
                                    <MenuItem value="meta-llama/Meta-Llama-3-8B-Instruct-Lite">Llama 3 8B Instruct Lite</MenuItem>
                                    <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Llama 3.1 8B Instruct Turbo</MenuItem>
                                    <MenuItem value="meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Llama 3.1 70B Instruct Turbo</MenuItem>
                                    <MenuItem value="meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo">Llama 3.1 405B Instruct Turbo</MenuItem>
                                    <MenuItem value="meta-llama/Llama-3.2-11B-Vision-Instruct-Turbo">Llama 3.2 11B Vision Instruct</MenuItem>
                                    <MenuItem value="meta-llama/Llama-3.3-70B-Instruct-Turbo">Llama 3.3 70B Instruct Turbo</MenuItem>
                                    <MenuItem value="meta-llama/llama-4-scout">Llama 4 Scout</MenuItem>
                                    <MenuItem value="meta-llama/llama-4-maverick">Llama 4 Maverick</MenuItem>
                                    <ListSubheader>NVIDIA Models</ListSubheader>
                                    <MenuItem value="nvidia/Llama-3.1-Nemotron-70B-Instruct-HF">Nemotron 70B Instruct</MenuItem>
                                    <MenuItem value="nvidia/llama-3.1-nemotron-70b-instruct">Nemotron 70B Instruct (Alt)</MenuItem>
                                    <ListSubheader>Other Models</ListSubheader>
                                    <MenuItem value="MythoMax-L2-13b-Lite">MythoMax L2 13B Lite</MenuItem>
                                  </Select>
                                  <FormHelperText>Choose the LLM model to use for extraction. If not specified, the project's primary LLM will be used.</FormHelperText>
                                </FormControl>
                              </Box>
                            )}
                            
                            {field.valueSource === 'user_data' && (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={field.value || ''}
                                  onChange={(e) => {
                                    const updatedFields = [...(step.config?.apiCall?.bodyFields || [])];
                                    updatedFields[index] = { ...field, value: e.target.value };
                                    const updatedStep = {
                                      ...step,
                                      config: {
                                        ...step.config,
                                        apiCall: {
                                          ...step.config?.apiCall,
                                          bodyFields: updatedFields
                                        }
                                      }
                                    };
                                    onStepChange(updatedStep);
                                  }}
                                  displayEmpty
                                >
                                  <MenuItem value="">Select user data field</MenuItem>
                                  <MenuItem value="user_name">User Name</MenuItem>
                                  <MenuItem value="user_email">User Email</MenuItem>
                                  <MenuItem value="user_phone">User Phone</MenuItem>
                                  <MenuItem value="user_id">User ID</MenuItem>
                                  <MenuItem value="session_id">Session ID</MenuItem>
                                  <MenuItem value="user_location">User Location</MenuItem>
                                  <MenuItem value="user_language">User Language</MenuItem>
                                  <MenuItem value="user_timezone">User Timezone</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          </Box>
                        ))}
                        
                        {/* Add Body Field Button */}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const newField = {
                              key: '',
                              type: 'string',
                              valueSource: 'static',
                              value: ''
                            };
                            const updatedFields = [...(step.config?.apiCall?.bodyFields || []), newField];
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  bodyFields: updatedFields
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          sx={{ mt: 1 }}
                        >
                          + Add Body Field
                        </Button>
                      </Box>
                    )}

                    {/* Raw Body Configuration */}
                    {step.config?.apiCall?.bodyType === 'raw' && (
                      <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Content Type</InputLabel>
                          <Select
                            value={step.config?.apiCall?.rawBodyContentType || 'application/json'}
                            onChange={(e) => {
                              const updatedStep = {
                                ...step,
                                config: {
                                  ...step.config,
                                  apiCall: {
                                    ...step.config?.apiCall,
                                    rawBodyContentType: e.target.value
                                  }
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            label="Content Type"
                          >
                            <MenuItem value="application/json">JSON</MenuItem>
                            <MenuItem value="text/plain">Plain Text</MenuItem>
                            <MenuItem value="application/xml">XML</MenuItem>
                            <MenuItem value="text/html">HTML</MenuItem>
                            <MenuItem value="application/javascript">JavaScript</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <TextField
                          fullWidth
                          multiline
                          rows={8}
                          label="Raw Body Template"
                          value={step.config?.apiCall?.rawBodyTemplate || ''}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  rawBodyTemplate: e.target.value
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder="Enter your raw body template here..."
                          helperText="You can use variables like {{variableName}} in the template"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </Box>
                    )}

                    {/* GraphQL Configuration */}
                    {step.config?.apiCall?.bodyType === 'graphql' && (
                      <Box sx={{ mt: 2 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={8}
                          label="GraphQL Query"
                          value={step.config?.apiCall?.graphqlQuery || ''}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  graphqlQuery: e.target.value
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder="Enter your GraphQL query here..."
                          sx={{ mb: 2, fontFamily: 'monospace' }}
                        />
                        
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          label="Variables (JSON)"
                          value={step.config?.apiCall?.graphqlVariables || ''}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  graphqlVariables: e.target.value
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder="Enter your variables as JSON..."
                          helperText="You can use variables like {{variableName}} in the JSON"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </Box>
                    )}

                    {/* Binary Body Note */}
                    {step.config?.apiCall?.bodyType === 'binary' && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        backgroundColor: '#fef3c7', 
                        border: '1px solid #fbbf24',
                        borderRadius: 1
                      }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#92400e' }}>
                          <strong>Binary Body:</strong> Binary data will be handled automatically based on the file upload or data provided during execution.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Response Mapping */}
                <Box sx={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 1, 
                  p: 2, 
                  mb: 2 
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                    💾 Save Response Data
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 2 }}>
                    Choose which data from the API response to save for use in later steps.
                  </Typography>

                  

                  {/* Multiple Response Field Mappings */}
                  <Box sx={{ 
                    backgroundColor: '#eff6ff', 
                    borderRadius: 1, 
                    p: 2 
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1 }}>
                      Extract specific fields:
                    </Typography>
                    
                    {/* List of current mappings */}
                    <Box sx={{ space: 1, mb: 2 }}>
                      {(() => {
                        // Handle both array format (new) and object format (legacy)
                        const saveFromResponse = step.variableMapping?.saveFromResponse;
                        if (!saveFromResponse) return [];
                        
                        if (Array.isArray(saveFromResponse)) {
                          // New format - array of mapping objects
                          return saveFromResponse;
                        } else if (typeof saveFromResponse === 'object') {
                          // Legacy format - object with key-value pairs
                          return Object.entries(saveFromResponse).map(([variableName, sourcePath]) => ({
                            variableName,
                            sourcePath,
                            field: sourcePath, // for backward compatibility
                            dataType: 'string',
                            description: `Legacy mapping: ${variableName} from ${sourcePath}`
                          }));
                        }
                        
                        return [];
                      })().map((mapping, index) => (
                        <Box key={index} sx={{ 
                          backgroundColor: 'white', 
                          borderRadius: 1, 
                          border: '1px solid #e5e7eb',
                          mb: 2,
                          p: 2
                        }}>
                          {/* Header with edit and delete buttons */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                              Response Field {index + 1}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  // Load the mapping into _tempMapping for editing
                                  const updatedStep = {
                                    ...step,
                                    _tempMapping: {
                                      ...mapping,
                                      field: mapping.sourcePath || mapping.field,
                                      editingIndex: index // Track which mapping we're editing
                                    }
                                  };
                                  onStepChange(updatedStep);
                                }}
                                sx={{ color: '#3b82f6' }}
                                title="Edit this response field"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  const saveFromResponse = step.variableMapping?.saveFromResponse;
                                  
                                  if (Array.isArray(saveFromResponse)) {
                                    // New format - remove from array
                                    const updatedFields = [...saveFromResponse];
                                    updatedFields.splice(index, 1);
                                    handleVariableMappingChange('saveFromResponse', updatedFields);
                                  } else if (typeof saveFromResponse === 'object') {
                                    // Legacy format - remove from object
                                    const updatedObject = { ...saveFromResponse };
                                    const variableToRemove = Object.keys(updatedObject)[index];
                                    delete updatedObject[variableToRemove];
                                    handleVariableMappingChange('saveFromResponse', updatedObject);
                                  }
                                }}
                                sx={{ color: '#ef4444' }}
                                title="Delete this response field"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          
                          {/* Field Path and Variable Name */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#6b7280', mb: 0.5 }}>
                                Response Field Path:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '0.75rem',
                                backgroundColor: '#f3f4f6',
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}>
                                {mapping.sourcePath || mapping.field}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#6b7280', mb: 0.5 }}>
                                Variable Name:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 600, 
                                fontSize: '0.75rem',
                                backgroundColor: '#f3f4f6',
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}>
                                {mapping.variableName}
                              </Typography>
                            </Box>
                          </Box>
                          
                          {/* Data Type and Array Handling */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#6b7280', mb: 0.5 }}>
                                Data Type:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.75rem',
                                backgroundColor: mapping.dataType === 'array' ? '#dcfce7' : '#f3f4f6',
                                color: mapping.dataType === 'array' ? '#166534' : '#374151',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontWeight: mapping.dataType === 'array' ? 600 : 400
                              }}>
                                {mapping.dataType || 'string'}
                              </Typography>
                            </Box>
                            {mapping.dataType === 'array' && (
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#6b7280', mb: 0.5 }}>
                                  Array Selection:
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                  fontSize: '0.75rem',
                                  backgroundColor: mapping.arrayHandling?.selectionType === 'all' ? '#dcfce7' : '#fef3c7',
                                  color: mapping.arrayHandling?.selectionType === 'all' ? '#166534' : '#92400e',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontWeight: 600
                                }}>
                                  {mapping.arrayHandling?.selectionType === 'all' ? 'All Items (Array)' : 
                                   mapping.arrayHandling?.selectionType === 'first' ? 'First Item Only' :
                                   mapping.arrayHandling?.selectionType === 'last' ? 'Last Item Only' :
                                   mapping.arrayHandling?.selectionType === 'user_selection' ? 'User Selection' :
                                   'All Items (Default)'}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          
                          {/* Array formatting note */}
                          {mapping.dataType === 'array' && mapping.arrayHandling?.selectionType === 'all' && (
                            <Box sx={{ 
                              mt: 2, 
                              p: 1.5, 
                              backgroundColor: '#ecfdf5', 
                              border: '1px solid #10b981',
                              borderRadius: 1
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.7rem', 
                                color: '#065f46',
                                fontWeight: 600
                              }}>
                                ✅ Array Formatting Enabled
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem', 
                                color: '#065f46',
                                fontFamily: 'monospace',
                                mt: 0.5
                              }}>
                                Use: {`{{${mapping.variableName}[*]|template}}`}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Required Variable Toggle */}
                          <Box sx={{ 
                            mt: 2,
                            p: 1.5,
                            backgroundColor: mapping.required !== false ? '#e8f5e8' : '#fff3cd',
                            borderRadius: 1,
                            border: mapping.required !== false ? '1px solid #4caf50' : '1px solid #ffc107'
                          }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={mapping.required !== false} // Default to true if not specified
                                  onChange={(e) => {
                                    const saveFromResponse = step.variableMapping?.saveFromResponse;
                                    
                                    if (Array.isArray(saveFromResponse)) {
                                      const updatedFields = [...saveFromResponse];
                                      updatedFields[index] = { 
                                        ...updatedFields[index], 
                                        required: e.target.checked 
                                      };
                                      handleVariableMappingChange('saveFromResponse', updatedFields);
                                    }
                                  }}
                                  color="primary"
                                />
                              }
                              label="Required Variable"
                              sx={{ flexGrow: 1 }}
                            />
                            <Typography variant="caption" sx={{ 
                              color: mapping.required !== false ? '#2e7d32' : '#f57c00',
                              fontWeight: 500,
                              display: 'block',
                              fontSize: '0.7rem'
                            }}>
                              {mapping.required !== false 
                                ? 'Flow will wait for this value to be extracted' 
                                : 'Flow will continue even if extraction fails (value will be null)'
                              }
                            </Typography>
                          </Box>
                          
                          {/* LLM Model for Array Selection */}
                          {mapping.dataType === 'array' && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#6b7280', mb: 0.5 }}>
                                LLM Model for Smart Array Selection:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.7rem', 
                                color: '#374151',
                                backgroundColor: '#f8fafc',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontFamily: 'monospace'
                              }}>
                                {mapping.arraySelectionModel || 'gpt-4o-mini (default)'}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.6rem', 
                                color: '#6b7280',
                                mt: 0.5,
                                fontStyle: 'italic'
                              }}>
                                Used when multiple array items match the search criteria
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Description if present */}
                          {mapping.description && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#6b7280', mb: 0.5 }}>
                                Description:
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#374151' }}>
                                {mapping.description}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>

                    {/* Add/Edit response field mapping */}
                    <Box sx={{ 
                      border: step._tempMapping?.editingIndex !== undefined ? '2px solid #3b82f6' : '1px solid #e5e7eb', 
                      borderRadius: 1, 
                      p: 2, 
                      backgroundColor: step._tempMapping?.editingIndex !== undefined ? '#eff6ff' : 'white' 
                    }}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600, 
                        color: step._tempMapping?.editingIndex !== undefined ? '#1d4ed8' : '#374151',
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        {step._tempMapping?.editingIndex !== undefined ? (
                          <>
                            <EditIcon fontSize="small" />
                            Editing Response Field {step._tempMapping.editingIndex + 1}
                          </>
                        ) : (
                          <>
                            <AddIcon fontSize="small" />
                            Add New Response Field
                          </>
                        )}
                      </Typography>
                      <Box sx={{ space: 2 }}>
                        {/* Response Mapping Dropdown */}
                        {(() => {
                          const responseFields = getResponseFieldsForEndpoint(selectedEndpoint);
                          console.log('🔍 RESPONSE FIELD DEBUG:');
                          console.log('  - selectedEndpoint:', selectedEndpoint);
                          console.log('  - selectedEndpoint?.responseMapping:', selectedEndpoint?.responseMapping);
                          console.log('  - responseFields from getResponseFieldsForEndpoint:', responseFields);
                          console.log('  - responseFields.length:', responseFields.length);
                          
                          return responseFields.length > 0 ? (
                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                              <InputLabel>Select Response Field</InputLabel>
                              <Select
                                value={step._tempMapping?.selectedMapping || ''}
                                onChange={(e) => {
                                  console.log('🔍 RESPONSE FIELD SELECTION:', e.target.value);
                                  const selectedMapping = responseFields.find(field => field.key === e.target.value);
                                  console.log('🔍 FOUND MAPPING:', selectedMapping);
                                  console.log('🔍 ALL RESPONSE FIELDS:', responseFields);
                                  
                                  const updatedStep = {
                                    ...step,
                                    _tempMapping: {
                                      ...step._tempMapping,
                                      selectedMapping: e.target.value,
                                      field: selectedMapping?.path || '',
                                      variableName: selectedMapping?.key || ''
                                    }
                                  };
                                  console.log('🔍 UPDATED STEP WITH TEMP MAPPING:', updatedStep._tempMapping);
                                  onStepChange(updatedStep);
                                }}
                                label="Select Response Field"
                              >
                                <MenuItem value="">
                                  <em>Choose a pre-configured field</em>
                                </MenuItem>
                                {responseFields.map((field) => (
                                  <MenuItem key={field.key} value={field.key}>
                                    <Box sx={{ width: '100%' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                        {field.key}
                                      </Typography>
                                      <Typography variant="body2" sx={{ 
                                        fontSize: '0.65rem', 
                                        color: '#6b7280',
                                        fontFamily: 'monospace'
                                      }}>
                                        Path: {field.path}
                                      </Typography>
                                      <Typography variant="body2" sx={{ 
                                        fontSize: '0.65rem', 
                                        color: '#6b7280' 
                                      }}>
                                        Type: {field.type} • {field.description}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                                <MenuItem value="custom">
                                  <Box sx={{ width: '100%' }}>
                                    <Typography variant="body2" sx={{ 
                                      fontWeight: 600, 
                                      fontSize: '0.75rem',
                                      color: '#7c3aed'
                                    }}>
                                      Custom Field Path
                                    </Typography>
                                    <Typography variant="body2" sx={{ 
                                      fontSize: '0.65rem', 
                                      color: '#6b7280' 
                                    }}>
                                      Manually enter a custom field path
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Box sx={{ 
                              p: 2, 
                              backgroundColor: '#fef3c7', 
                              border: '1px solid #f59e0b',
                              borderRadius: 1,
                              mb: 2
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.75rem', 
                                color: '#92400e',
                                fontWeight: 600,
                                mb: 1
                              }}>
                                ⚠️ No Response Mapping Available
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.7rem', 
                                color: '#92400e' 
                              }}>
                                This API endpoint doesn't have pre-configured response mappings. 
                                Configure them in the API Manager for easier field extraction.
                              </Typography>
                            </Box>
                          );
                        })()}

                        {/* Custom field input when "custom" is selected or no mappings available */}
                        {(step._tempMapping?.selectedMapping === 'custom' || 
                          getResponseFieldsForEndpoint(selectedEndpoint).length === 0) && (
                          <TextField
                            fullWidth
                            size="small"
                            label="Custom Response Field Path"
                            value={step._tempMapping?.field || ''}
                            onChange={(e) => {
                              const updatedStep = {
                                ...step,
                                _tempMapping: {
                                  ...step._tempMapping,
                                  field: e.target.value
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            placeholder="data.results or data.user.name"
                            sx={{ mb: 2 }}
                            helperText="Enter the JSON path to extract data from the API response"
                          />
                        )}

                        {/* Variable name (auto-filled when selecting from dropdown) */}
                        <TextField
                          fullWidth
                          size="small"
                          label="Save as Variable"
                          value={step._tempMapping?.variableName || ''}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              _tempMapping: {
                                ...step._tempMapping,
                                variableName: e.target.value
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder="variable_name"
                          sx={{ mb: 2 }}
                          helperText="Variable name to use in later steps (auto-filled from selection)"
                        />
                        
                        {/* NEW: Data Type Selection */}
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Data Type *</InputLabel>
                          <Select
                            value={step._tempMapping?.dataType || 'string'}
                            onChange={(e) => {
                              const updatedStep = {
                                ...step,
                                _tempMapping: {
                                  ...step._tempMapping,
                                  dataType: e.target.value
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            label="Data Type *"
                          >
                            <MenuItem value="string">
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>String</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  Single text value (e.g., "John Doe")
                                </Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="number">
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Number</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  Numeric value (e.g., 123, 45.67)
                                </Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="boolean">
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Boolean</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  True/false value
                                </Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="object">
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Object</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                  Single object with properties
                                </Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem value="array">
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>Array ⭐</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#065f46' }}>
                                  List of items (enables array formatting)
                                </Typography>
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                        
                        {/* NEW: Array Handling (only shown for array type) */}
                        {step._tempMapping?.dataType === 'array' && (
                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Array Selection *</InputLabel>
                            <Select
                              value={step._tempMapping?.arrayHandling?.selectionType || 'all'}
                              onChange={(e) => {
                                const updatedStep = {
                                  ...step,
                                  _tempMapping: {
                                    ...step._tempMapping,
                                    arrayHandling: {
                                      selectionType: e.target.value
                                    }
                                  }
                                };
                                onStepChange(updatedStep);
                              }}
                              label="Array Selection *"
                            >
                              <MenuItem value="all">
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>All Items (Keep as Array) ⭐</Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#065f46' }}>
                                    Perfect for lists like doctorList, enables array formatting
                                  </Typography>
                                </Box>
                              </MenuItem>
                              <MenuItem value="first">
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>First Item Only</Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                    Takes only the first item from the array
                                  </Typography>
                                </Box>
                              </MenuItem>
                              <MenuItem value="last">
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Last Item Only</Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                    Takes only the last item from the array
                                  </Typography>
                                </Box>
                              </MenuItem>
                              <MenuItem value="user_selection">
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>User Selection</Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                    Let user choose which item to use
                                  </Typography>
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        
                        {/* LLM Model Selection for Array Processing */}
                        {step._tempMapping?.dataType === 'array' && (
                          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>LLM Model for Smart Array Selection</InputLabel>
                            <Select
                              value={step._tempMapping?.arraySelectionModel || 'gpt-4o-mini'}
                              onChange={(e) => {
                                const updatedStep = {
                                  ...step,
                                  _tempMapping: {
                                    ...step._tempMapping,
                                    arraySelectionModel: e.target.value
                                  }
                                };
                                onStepChange(updatedStep);
                              }}
                              label="LLM Model for Smart Array Selection"
                            >
                              <ListSubheader>Recommended (Fast & Cost-Effective)</ListSubheader>
                              <MenuItem value="gpt-4o-mini">GPT-4o Mini (Default)</MenuItem>
                              <MenuItem value="gpt-4o">GPT-4o</MenuItem>
                              <MenuItem value="claude-haiku-5-5">Claude Haiku 5.5</MenuItem>
                              <MenuItem value="qwen-turbo">Qwen Turbo</MenuItem>
                              <ListSubheader>OpenAI Models</ListSubheader>
                              {OPENAI_CHAT_MODEL_OPTIONS.filter((model) => model.value !== 'gpt-4o' && model.value !== 'gpt-4o-mini').map((model) => (
                                <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                              ))}
                              <ListSubheader>Claude Models</ListSubheader>
                              {CLAUDE_MODEL_OPTIONS.filter((model) => model.value !== 'claude-haiku-5-5').map((model) => (
                                <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                              ))}
                              <ListSubheader>DeepSeek Models</ListSubheader>
                              <MenuItem value="deepseek-chat">DeepSeek Chat</MenuItem>
                              <MenuItem value="deepseek-reasoner">DeepSeek Reasoner</MenuItem>
                              <ListSubheader>Qwen Models</ListSubheader>
                              <MenuItem value="qwen-max">Qwen Max</MenuItem>
                              <MenuItem value="qwen-plus">Qwen Plus</MenuItem>
                              <ListSubheader>Gemini Models</ListSubheader>
                              {GEMINI_MODEL_OPTIONS.map((model) => (
                                <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                              ))}
                              <ListSubheader>Meta Llama Models</ListSubheader>
                              <MenuItem value="meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo">Llama 3.1 8B Instruct Turbo</MenuItem>
                              <MenuItem value="meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo">Llama 3.1 70B Instruct Turbo</MenuItem>
                              <MenuItem value="meta-llama/Llama-3.3-70B-Instruct-Turbo">Llama 3.3 70B Instruct Turbo</MenuItem>
                            </Select>
                            <FormHelperText>
                              This model is used when multiple array items match the search criteria and intelligent selection is needed
                            </FormHelperText>
                          </FormControl>
                        )}
                        
                        {/* Custom LLM Prompt for Array Selection */}
                        {step._tempMapping?.dataType === 'array' && (
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={4}
                            label="Custom LLM Selection Prompt (Optional)"
                            value={step._tempMapping?.arraySelectionPrompt || ''}
                            onChange={(e) => {
                              const updatedStep = {
                                ...step,
                                _tempMapping: {
                                  ...step._tempMapping,
                                  arraySelectionPrompt: e.target.value
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            placeholder="Enter custom instructions for the LLM to help it select the best item from the array. If left empty, a default prompt will be used.

Example: Select the clinic that matches {{selectedClinicId}}. Look for the clinic where the 'id' field matches {{selectedClinicId}}. Extract the 'externalId' from that matching clinic record."
                            sx={{ mb: 2 }}
                            helperText="💡 NEW FEATURE: You can now use Flow Builder variables in your prompt! Use {{variableName}} to reference any previously set variables. The LLM will receive the actual values to help with array selection. Also supports {{searchTerm}} for search criteria."
                          />
                        )}
                        
                        {/* Array formatting help */}
                        {step._tempMapping?.dataType === 'array' && step._tempMapping?.arrayHandling?.selectionType === 'all' && (
                          <Box sx={{ 
                            p: 2, 
                            backgroundColor: '#ecfdf5', 
                            border: '1px solid #10b981',
                            borderRadius: 1,
                            mb: 2
                          }}>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.75rem', 
                              color: '#065f46',
                              fontWeight: 600,
                              mb: 1
                            }}>
                              ✅ Array Formatting Enabled!
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.7rem', 
                              color: '#065f46',
                              mb: 1
                            }}>
                              With this configuration, you can use array formatting in collect_input/message steps:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              fontSize: '0.65rem', 
                              color: '#065f46',
                              fontFamily: 'monospace',
                              backgroundColor: '#f0fdf4',
                              padding: '4px 8px',
                              borderRadius: '4px'
                            }}>
                              {`{{${step._tempMapping?.variableName || 'variableName'}[*]|**Name:** {attributes.name}**Location:** {attributes.location}---}}`}
                            </Typography>
                          </Box>
                        )}
                        
                        {/* Required Variable Toggle */}
                        <Box sx={{ 
                          mb: 2,
                          p: 1.5,
                          backgroundColor: step._tempMapping?.required !== false ? '#e8f5e8' : '#fff3cd',
                          borderRadius: 1,
                          border: step._tempMapping?.required !== false ? '1px solid #4caf50' : '1px solid #ffc107'
                        }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={step._tempMapping?.required !== false} // Default to true if not specified
                                onChange={(e) => {
                                  const updatedStep = {
                                    ...step,
                                    _tempMapping: {
                                      ...step._tempMapping,
                                      required: e.target.checked
                                    }
                                  };
                                  onStepChange(updatedStep);
                                }}
                                color="primary"
                              />
                            }
                            label="Required Variable"
                            sx={{ flexGrow: 1 }}
                          />
                          <Typography variant="caption" sx={{ 
                            color: step._tempMapping?.required !== false ? '#2e7d32' : '#f57c00',
                            fontWeight: 500,
                            display: 'block',
                            fontSize: '0.7rem'
                          }}>
                            {step._tempMapping?.required !== false 
                              ? 'Flow will wait for this value to be extracted from the response' 
                              : 'Flow will continue even if extraction fails (value will be null)'
                            }
                          </Typography>
                        </Box>
                        
                        {/* Description field */}
                        <TextField
                          fullWidth
                          size="small"
                          label="Description (Optional)"
                          value={step._tempMapping?.description || ''}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              _tempMapping: {
                                ...step._tempMapping,
                                description: e.target.value
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder="Describe what this variable contains"
                          sx={{ mb: 2 }}
                          helperText="Optional description for documentation"
                        />
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => {
                              console.log('=== SAVE RESPONSE FIELD MAPPING DEBUG ===');
                              console.log('step._tempMapping:', step._tempMapping);
                              
                              const sourcePath = step._tempMapping?.field;
                              const variableName = step._tempMapping?.variableName;
                              const dataType = step._tempMapping?.dataType || 'string';
                              const description = step._tempMapping?.description || '';
                              const arrayHandling = step._tempMapping?.arrayHandling || { selectionType: 'all' };
                              const arraySelectionModel = step._tempMapping?.arraySelectionModel || 'gpt-4o-mini';
                              const arraySelectionPrompt = step._tempMapping?.arraySelectionPrompt || '';
                              const editingIndex = step._tempMapping?.editingIndex;
                              
                              console.log('Extracted values:', { sourcePath, variableName, dataType, description, arrayHandling, arraySelectionModel, arraySelectionPrompt, editingIndex });
                              
                              if (sourcePath && variableName) {
                                const currentSaveFromResponse = step.variableMapping?.saveFromResponse || [];
                                console.log('Current saveFromResponse before modification:', currentSaveFromResponse);
                                
                                // Convert to array format if it's not already
                                let currentFields = [];
                                if (Array.isArray(currentSaveFromResponse)) {
                                  currentFields = [...currentSaveFromResponse];
                                } else if (typeof currentSaveFromResponse === 'object') {
                                  // Convert legacy object format to array
                                  currentFields = Object.entries(currentSaveFromResponse).map(([varName, path]) => ({
                                    variableName: varName,
                                    sourcePath: path,
                                    field: path,
                                    dataType: 'string',
                                    description: `Legacy mapping converted to new format`
                                  }));
                                }
                                
                                const newMapping = { 
                                  sourcePath, 
                                  variableName, 
                                  dataType, 
                                  description,
                                  required: step._tempMapping?.required !== false, // Include required field
                                  ...(dataType === 'array' && { 
                                    arrayHandling,
                                    arraySelectionModel,
                                    arraySelectionPrompt 
                                  })
                                };
                                
                                let updatedSaveFromResponse;
                                
                                if (editingIndex !== undefined && editingIndex >= 0) {
                                  // Edit mode - replace existing mapping
                                  console.log('Editing existing mapping at index:', editingIndex);
                                  updatedSaveFromResponse = [...currentFields];
                                  updatedSaveFromResponse[editingIndex] = newMapping;
                                } else {
                                  // Add mode - check for duplicates and add new mapping
                                  const duplicateExists = currentFields.some(existing => 
                                    (existing.sourcePath || existing.field) === sourcePath && existing.variableName === variableName
                                  );
                                  
                                  if (duplicateExists) {
                                    console.log('⚠️ Duplicate mapping detected - NOT adding:', { sourcePath, variableName });
                                    return;
                                  }
                                  
                                  console.log('Adding new mapping:', newMapping);
                                  updatedSaveFromResponse = [...currentFields, newMapping];
                                }
                                
                                console.log('Updated saveFromResponse after modification:', updatedSaveFromResponse);
                                
                                const updatedStep = {
                                  ...step,
                                  variableMapping: {
                                    ...step.variableMapping,
                                    saveFromResponse: updatedSaveFromResponse
                                  },
                                  _tempMapping: {} // Clear temp mapping
                                };
                                
                                console.log('Final updatedStep being sent to onStepChange:', updatedStep);
                                console.log('=== SAVE RESPONSE FIELD MAPPING DEBUG END ===');
                                onStepChange(updatedStep);
                              } else {
                                console.log('❌ Missing sourcePath or variableName, not saving mapping');
                              }
                            }}
                            disabled={!step._tempMapping?.variableName || !step._tempMapping?.field}
                          >
                            {step._tempMapping?.editingIndex !== undefined ? 'Update Response Field' : 'Add Response Field Mapping'}
                          </Button>
                          
                          {/* Cancel button for edit mode */}
                          {step._tempMapping?.editingIndex !== undefined && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const updatedStep = {
                                  ...step,
                                  _tempMapping: {} // Clear temp mapping to cancel edit
                                };
                                onStepChange(updatedStep);
                              }}
                              sx={{ minWidth: 100 }}
                            >
                              Cancel
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    
                    {/* Updated help text */}
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#f8fafc', 
                      border: '1px solid #e2e8f0',
                      borderRadius: 1,
                      mt: 2
                    }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#475569', fontWeight: 600, mb: 1 }}>
                        💡 Response Field Configuration Guide:
                      </Typography>
                      <Box component="ul" sx={{ fontSize: '0.7rem', color: '#64748b', m: 0, pl: 2 }}>
                        <li>
                          <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                            <strong>Field Path:</strong> Use dot notation like "data.items" or "response.results"
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                            <strong>Data Type "Array":</strong> Perfect for lists like doctorList, patientList
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                            <strong>Array Selection "All Items":</strong> Enables array formatting like <code>{'{{doctorList[*]|...}}'}</code>
                          </Typography>
                        </li>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Error Response Mapping Configuration */}
                <Box sx={{ 
                  border: '2px solid #dc2626', 
                  borderRadius: 1, 
                  p: 2, 
                  mb: 2,
                  backgroundColor: '#fef2f2'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626', mb: 2, fontSize: '16px' }}>
                    ⚠️ ERROR RESPONSE MAPPING
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 2 }}>
                    Configure how to extract error information when this API call fails. These variables can be used in branch steps to handle errors gracefully.
                  </Typography>

                  {/* List of current error response mappings */}
                  {(step.config?.apiCall?.errorResponseMapping || []).map((mapping, index) => (
                    <Box key={index} sx={{ 
                      backgroundColor: '#ffffff', 
                      borderRadius: 1, 
                      p: 2, 
                      mb: 2,
                      border: '1px solid #fca5a5'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626' }}>
                          Error Mapping {index + 1}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => {
                            const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || [])];
                            updatedMappings.splice(index, 1);
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  errorResponseMapping: updatedMappings
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                        <TextField
                          label="Variable Name"
                          size="small"
                          value={mapping.key || ''}
                          onChange={(e) => {
                            const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || [])];
                            updatedMappings[index] = { ...mapping, key: e.target.value };
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  errorResponseMapping: updatedMappings
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder="error_message"
                        />
                        
                        <TextField
                          label="JSON Path"
                          size="small"
                          value={mapping.path || ''}
                          onChange={(e) => {
                            const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || [])];
                            updatedMappings[index] = { ...mapping, path: e.target.value };
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  errorResponseMapping: updatedMappings
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder="error.message"
                        />
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                        <FormControl size="small">
                          <InputLabel>Data Type</InputLabel>
                          <Select
                            value={mapping.type || 'string'}
                            onChange={(e) => {
                              const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || [])];
                              updatedMappings[index] = { ...mapping, type: e.target.value };
                              const updatedStep = {
                                ...step,
                                config: {
                                  ...step.config,
                                  apiCall: {
                                    ...step.config?.apiCall,
                                    errorResponseMapping: updatedMappings
                                  }
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            label="Data Type"
                          >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="boolean">Boolean</MenuItem>
                            <MenuItem value="object">Object</MenuItem>
                            <MenuItem value="array">Array</MenuItem>
                          </Select>
                        </FormControl>

                        <FormControl size="small">
                          <InputLabel>Error Condition</InputLabel>
                          <Select
                            value={mapping.errorCondition || 'always'}
                            onChange={(e) => {
                              const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || [])];
                              updatedMappings[index] = { ...mapping, errorCondition: e.target.value };
                              const updatedStep = {
                                ...step,
                                config: {
                                  ...step.config,
                                  apiCall: {
                                    ...step.config?.apiCall,
                                    errorResponseMapping: updatedMappings
                                  }
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            label="Error Condition"
                          >
                            <MenuItem value="always">Always extract on error</MenuItem>
                            <MenuItem value="status_code">HTTP Status Code</MenuItem>
                            <MenuItem value="response_field">Response Field Value</MenuItem>
                          </Select>
                        </FormControl>

                        <TextField
                          label="Condition Value"
                          size="small"
                          value={mapping.errorValue || ''}
                          onChange={(e) => {
                            const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || [])];
                            updatedMappings[index] = { ...mapping, errorValue: e.target.value };
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                apiCall: {
                                  ...step.config?.apiCall,
                                  errorResponseMapping: updatedMappings
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder={mapping.errorCondition === 'status_code' ? '400-499 or 500' : 'error_value'}
                          disabled={mapping.errorCondition === 'always'}
                        />
                      </Box>

                      <TextField
                        label="Description"
                        size="small"
                        fullWidth
                        value={mapping.description || ''}
                        onChange={(e) => {
                          const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || [])];
                          updatedMappings[index] = { ...mapping, description: e.target.value };
                          const updatedStep = {
                            ...step,
                            config: {
                              ...step.config,
                              apiCall: {
                                ...step.config?.apiCall,
                                errorResponseMapping: updatedMappings
                              }
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        placeholder="Description of this error mapping"
                      />
                    </Box>
                  ))}

                  {/* Add Error Response Mapping Button */}
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      const newMapping = {
                        key: '',
                        path: '',
                        type: 'string',
                        description: '',
                        errorCondition: 'always',
                        errorValue: ''
                      };
                      const updatedMappings = [...(step.config?.apiCall?.errorResponseMapping || []), newMapping];
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          apiCall: {
                            ...step.config?.apiCall,
                            errorResponseMapping: updatedMappings
                          }
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    sx={{ width: '100%' }}
                  >
                    + Add Error Response Mapping
                  </Button>

                  {/* Help text for error mapping */}
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#fef2f2', 
                    border: '1px solid #fca5a5',
                    borderRadius: 1,
                    mt: 2
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                      🔧 API Parameters
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Query Parameters */}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                          Query Parameters
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {step.config?.apiCall?.queryParams?.map((param, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                size="small"
                                value={param.key}
                                onChange={(e) => {
                                  const updatedParams = [...step.config?.apiCall?.queryParams];
                                  updatedParams[index].key = e.target.value;
                                  handleConfigChange('apiCall', {
                                    ...step.config?.apiCall,
                                    queryParams: updatedParams
                                  });
                                }}
                                placeholder="Key"
                                sx={{ flex: 1 }}
                              />
                              <TextField
                                size="small"
                                value={param.value}
                                onChange={(e) => {
                                  const updatedParams = [...step.config?.apiCall?.queryParams];
                                  updatedParams[index].value = e.target.value;
                                  handleConfigChange('apiCall', {
                                    ...step.config?.apiCall,
                                    queryParams: updatedParams
                                  });
                                }}
                                placeholder="Value"
                                sx={{ flex: 1 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedParams = [...step.config?.apiCall?.queryParams];
                                  updatedParams.splice(index, 1);
                                  handleConfigChange('apiCall', {
                                    ...step.config?.apiCall,
                                    queryParams: updatedParams
                                  });
                                }}
                                sx={{ color: '#dc2626' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const updatedParams = [...(step.config?.apiCall?.queryParams || [])];
                              updatedParams.push({ key: '', value: '' });
                              handleConfigChange('apiCall', {
                                ...step.config?.apiCall,
                                queryParams: updatedParams
                              });
                            }}
                            variant="outlined"
                            sx={{ 
                              borderStyle: 'dashed',
                              color: '#6b7280',
                              borderColor: '#d1d5db',
                              '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                              }
                            }}
                          >
                            Add Parameter
                          </Button>
                        </Box>
                      </Box>

                      {/* Headers */}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                          Headers
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {step.config?.apiCall?.headers?.map((header, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                size="small"
                                value={header.key}
                                onChange={(e) => {
                                  const updatedHeaders = [...step.config?.apiCall?.headers];
                                  updatedHeaders[index].key = e.target.value;
                                  handleConfigChange('apiCall', {
                                    ...step.config?.apiCall,
                                    headers: updatedHeaders
                                  });
                                }}
                                placeholder="Key"
                                sx={{ flex: 1 }}
                              />
                              <TextField
                                size="small"
                                value={header.value}
                                onChange={(e) => {
                                  const updatedHeaders = [...step.config?.apiCall?.headers];
                                  updatedHeaders[index].value = e.target.value;
                                  handleConfigChange('apiCall', {
                                    ...step.config?.apiCall,
                                    headers: updatedHeaders
                                  });
                                }}
                                placeholder="Value"
                                sx={{ flex: 1 }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const updatedHeaders = [...step.config?.apiCall?.headers];
                                  updatedHeaders.splice(index, 1);
                                  handleConfigChange('apiCall', {
                                    ...step.config?.apiCall,
                                    headers: updatedHeaders
                                  });
                                }}
                                sx={{ color: '#dc2626' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          ))}
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const updatedHeaders = [...(step.config?.apiCall?.headers || [])];
                              updatedHeaders.push({ key: '', value: '' });
                              handleConfigChange('apiCall', {
                                ...step.config?.apiCall,
                                headers: updatedHeaders
                              });
                            }}
                            variant="outlined"
                            sx={{ 
                              borderStyle: 'dashed',
                              color: '#6b7280',
                              borderColor: '#d1d5db',
                              '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                              }
                            }}
                          >
                            Add Header
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Delay Configuration */}
                <Box sx={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: 1, 
                  p: 2 
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
                    ⏱️ Execution Delay
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={step.config?.apiCall?.delay || 0}
                      onChange={(e) => handleConfigChange('apiCall', { 
                        ...step.config?.apiCall,
                        delay: parseInt(e.target.value) || 0 
                      })}
                      inputProps={{ min: 0, max: 30 }}
                      sx={{ width: 100 }}
                    />
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      seconds (0-30)
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Add a delay before executing this step (useful for rate limiting or user experience).
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </Box>
      )}

      {step.type === 'collect_input' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            Input Collection Configuration
          </Typography>
          
          {/* Input Message Content with Multi-Language Support */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
              Message to User
            </Typography>
            
            <Box>
              {/* Language Selection */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    value={currentCollectInputLang}
                    onChange={(e) => setCurrentCollectInputLang(e.target.value)}
                    label="Language"
                  >
                    {collectInputLanguages.map(lang => (
                      <MenuItem key={lang} value={lang}>
                        {lang === 'default' ? 'Default' : 
                         lang === 'id' ? 'Indonesian' : 
                         lang === 'en' ? 'English' : 
                         lang.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Add Language Button */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const newLang = prompt('Enter language code (e.g., en, id, es):');
                    if (newLang && !collectInputLanguages.includes(newLang)) {
                      const currentContent = step.config?.collectInput?.message?.content || step.config?.collectInput?.prompt || '';
                      const newContent = isCollectInputMultiLanguage ? 
                        { ...collectInputContent, [newLang]: '' } : 
                        { default: currentContent, [newLang]: '' };
                      
                      handleConfigChange('collectInput', { 
                        ...step.config?.collectInput,
                        message: {
                          ...step.config?.collectInput?.message,
                          content: newContent
                        },
                        prompt: typeof newContent === 'string' ? newContent : newContent.default || ''
                      });
                      setCurrentCollectInputLang(newLang);
                    }
                  }}
                >
                  + Add Language
                </Button>
                
                {/* Remove Language Button */}
                {collectInputLanguages.length > 1 && currentCollectInputLang !== 'default' && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      const newContent = { ...collectInputContent };
                      delete newContent[currentCollectInputLang];
                      
                      handleConfigChange('collectInput', { 
                        ...step.config?.collectInput,
                        message: {
                          ...step.config?.collectInput?.message,
                          content: newContent
                        },
                        prompt: typeof newContent === 'string' ? newContent : newContent.default || ''
                      });
                      setCurrentCollectInputLang('default');
                    }}
                  >
                    Remove {currentCollectInputLang}
                  </Button>
                )}
              </Box>
              
              {/* Message Input for Current Language */}
              <EnhancedMessageInput
                label={`Message to User (${currentCollectInputLang === 'default' ? 'Default' : currentCollectInputLang.toUpperCase()})`}
                value={isCollectInputMultiLanguage ? (collectInputContent[currentCollectInputLang] || '') : (collectInputContent || '')}
                onChange={(newValue) => {
                  console.log('📝 [COLLECT INPUT MESSAGE] onChange triggered with value:', newValue);
                  
                  let content;
                  if (isCollectInputMultiLanguage || collectInputLanguages.length > 1) {
                    // Multi-language format
                    content = {
                      ...collectInputContent,
                      [currentCollectInputLang]: newValue
                    };
                  } else {
                    // Simple string format for single language
                    content = newValue;
                  }
                  
                  handleConfigChange('collectInput', { 
                    ...step.config?.collectInput,
                    message: {
                      ...step.config?.collectInput?.message,
                      content
                    },
                    // Keep legacy prompt for backward compatibility
                    prompt: typeof content === 'string' ? content : content.default || newValue
                  });
                }}
                availableVariables={availableVariables}
                availableApiEndpoints={availableApiEndpoints}
                placeholder="Enter the message to show to the user when asking for input..."
                multiline={true}
                rows={8}
                helperText="Use the 📝 button to format lists (for multiple choice inputs), or the <> button to insert variables."
                sx={{
                  '& .MuiTextField-root': {
                    minHeight: '350px'
                  },
                  '& .MuiInputBase-root': {
                    minHeight: '350px',
                    alignItems: 'flex-start'
                  },
                  '& .MuiInputBase-input': {
                    minHeight: '320px !important',
                    resize: 'vertical'
                  }
                }}
              />
            </Box>
          </Box>

          {/* Negative Message Configuration */}
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
              📛 Negative Message (Error/Retry Message)
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2, fontSize: '0.875rem' }}>
              This message is shown when the user's input is unclear or doesn't match expected choices, followed by the original message.
            </Typography>
            
            <Box sx={{ 
              backgroundColor: '#fef3c7', 
              border: '1px solid #fbbf24', 
              borderRadius: 1, 
              p: 2, 
              mb: 2 
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e', mb: 1, fontSize: '0.8rem' }}>
                💡 Example Flow:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#92400e', mb: 1 }}>
                1. User sees your main message: "Choose 1 or 2..."
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#92400e', mb: 1 }}>
                2. User responds: "3" or "Doctor John" (invalid choice)
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#92400e' }}>
                3. System shows: "[Negative Message] + [Original Message]"
              </Typography>
            </Box>

            {/* Language Selection for Negative Message */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={currentCollectInputLang}
                  onChange={(e) => setCurrentCollectInputLang(e.target.value)}
                  displayEmpty
                >
                  {collectInputLanguages.map(lang => (
                    <MenuItem key={lang} value={lang}>
                      {lang === 'default' ? 'Default' : 
                       lang === 'en' ? 'English' : 
                       lang.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                (Uses same languages as main message)
              </Typography>
            </Box>

            {/* Negative Message Input */}
            <EnhancedMessageInput
              label={`Negative Message (${currentCollectInputLang === 'default' ? 'Default' : currentCollectInputLang.toUpperCase()})`}
              value={(() => {
                const negativeContent = step.config?.collectInput?.negativeMessage?.content || {};
                if (typeof negativeContent === 'string') {
                  return negativeContent;
                }
                return negativeContent[currentCollectInputLang] || '';
              })()}
              onChange={(newValue) => {
                console.log('📝 [NEGATIVE MESSAGE] onChange triggered with value:', newValue);
                
                const currentNegativeContent = step.config?.collectInput?.negativeMessage?.content || {};
                let content;
                
                if (isCollectInputMultiLanguage || collectInputLanguages.length > 1) {
                  // Multi-language format
                  content = typeof currentNegativeContent === 'string' 
                    ? { default: currentNegativeContent, [currentCollectInputLang]: newValue }
                    : { ...currentNegativeContent, [currentCollectInputLang]: newValue };
                } else {
                  // Simple string format for single language
                  content = newValue;
                }
                
                handleConfigChange('collectInput', { 
                  ...step.config?.collectInput,
                  negativeMessage: {
                    ...step.config?.collectInput?.negativeMessage,
                    content
                  }
                });
              }}
              availableVariables={availableVariables}
              availableApiEndpoints={availableApiEndpoints}
              placeholder="e.g., Sorry, I didn't understand your choice. Please try again."
              multiline={true}
              rows={8}
              helperText="Enter the message to show when user input is unclear. This will be followed by the original message."
              sx={{
                '& .MuiTextField-root': {
                  minHeight: '120px'
                },
                '& .MuiInputBase-root': {
                  minHeight: '120px',
                  alignItems: 'flex-start'
                },
                '& .MuiInputBase-input': {
                  minHeight: '90px !important',
                  resize: 'vertical'
                }
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={step.config?.collectInput?.validation?.required !== false}
                onChange={(e) => {
                  console.log('🔧 [VisualFlowEditor] REQUIRED FIELD TOGGLE CLICKED!');
                  console.log('🔧 [VisualFlowEditor] Current required value:', step.config?.collectInput?.validation?.required);
                  console.log('🔧 [VisualFlowEditor] New required value:', e.target.checked);
                  
                  handleConfigChange('collectInput', {
                    ...step.config?.collectInput,
                    validation: {
                      ...step.config?.collectInput?.validation,
                      required: e.target.checked
                    }
                  });
                }}
                size="small"
              />
            }
            label="Required field"
            sx={{ mb: 2 }}
          />

          {/* Conditional Requirements for Step-Level Validation */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                Conditional Step Requirements
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const currentConditions = step.config?.collectInput?.validation?.conditionalRequirements || [];
                  handleConfigChange('collectInput', {
                    ...step.config?.collectInput,
                    validation: {
                      ...step.config?.collectInput?.validation,
                      conditionalRequirements: [
                        ...currentConditions,
                        {
                          variable: '',
                          operator: 'exists',
                          value: '',
                          makeRequired: true
                        }
                      ]
                    }
                  });
                }}
                sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
              >
                + Add Condition
              </Button>
            </Box>
            
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 2 }}>
              Make this entire step required or optional based on values of other variables.
            </Typography>
            
            {/* Logic Operator Selection (only show if multiple conditions exist or could exist) */}
            {(step.config?.collectInput?.validation?.conditionalRequirements || []).length > 1 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#374151', mb: 1 }}>
                  Logic for Multiple Conditions
                </Typography>
                <Select
                  value={step.config?.collectInput?.validation?.conditionalRequirementsLogic || 'OR'}
                  onChange={(e) => {
                    handleConfigChange('collectInput', {
                      ...step.config?.collectInput,
                      validation: {
                        ...step.config?.collectInput?.validation,
                        conditionalRequirementsLogic: e.target.value
                      }
                    });
                  }}
                  size="small"
                  sx={{ fontSize: '0.75rem', minWidth: 120 }}
                >
                  <MenuItem value="OR" sx={{ fontSize: '0.75rem' }}>
                    ANY condition (OR)
                  </MenuItem>
                  <MenuItem value="AND" sx={{ fontSize: '0.75rem' }}>
                    ALL conditions (AND)
                  </MenuItem>
                </Select>
                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280', mt: 0.5 }}>
                  {step.config?.collectInput?.validation?.conditionalRequirementsLogic === 'AND' 
                    ? 'All conditions must be true to trigger the requirement change'
                    : 'Any single condition being true will trigger the requirement change'
                  }
                </Typography>
              </Box>
            )}
            
            {(step.config?.collectInput?.validation?.conditionalRequirements || []).length > 0 ? (
              <Box sx={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 1, p: 2, space: 2 }}>
                {(step.config?.collectInput?.validation?.conditionalRequirements || []).map((condition, condIndex) => (
                  <Box key={condIndex} sx={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #d1d5db', 
                    borderRadius: 1, 
                    p: 2,
                    mb: condIndex < (step.config?.collectInput?.validation?.conditionalRequirements || []).length - 1 ? 2 : 0
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                        Condition {condIndex + 1}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        onClick={() => {
                          const updatedConditions = [...(step.config?.collectInput?.validation?.conditionalRequirements || [])];
                          updatedConditions.splice(condIndex, 1);
                          handleConfigChange('collectInput', {
                            ...step.config?.collectInput,
                            validation: {
                              ...step.config?.collectInput?.validation,
                              conditionalRequirements: updatedConditions
                            }
                          });
                        }}
                        sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                      >
                        🗑️ Remove
                      </Button>
                    </Box>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                      {/* Variable Selection */}
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#374151', mb: 1 }}>
                          Variable
                        </Typography>
                        <Select
                          value={condition.variable || ''}
                          onChange={(e) => {
                            const updatedConditions = [...(step.config?.collectInput?.validation?.conditionalRequirements || [])];
                            updatedConditions[condIndex] = {
                              ...updatedConditions[condIndex],
                              variable: e.target.value
                            };
                            handleConfigChange('collectInput', {
                              ...step.config?.collectInput,
                              validation: {
                                ...step.config?.collectInput?.validation,
                                conditionalRequirements: updatedConditions
                              }
                            });
                          }}
                          size="small"
                          fullWidth
                          displayEmpty
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.75rem' }}>
                            <em>Select variable...</em>
                          </MenuItem>
                          {availableVariables.map((availableVar) => (
                            <MenuItem key={availableVar.name} value={availableVar.name} sx={{ fontSize: '0.75rem' }}>
                              {availableVar.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                      
                      {/* Operator Selection */}
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#374151', mb: 1 }}>
                          Condition
                        </Typography>
                        <Select
                          value={condition.operator || 'exists'}
                          onChange={(e) => {
                            const updatedConditions = [...(step.config?.collectInput?.validation?.conditionalRequirements || [])];
                            updatedConditions[condIndex] = {
                              ...updatedConditions[condIndex],
                              operator: e.target.value
                            };
                            handleConfigChange('collectInput', {
                              ...step.config?.collectInput,
                              validation: {
                                ...step.config?.collectInput?.validation,
                                conditionalRequirements: updatedConditions
                              }
                            });
                          }}
                          size="small"
                          fullWidth
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <MenuItem value="exists" sx={{ fontSize: '0.75rem' }}>exists</MenuItem>
                          <MenuItem value="not_exists" sx={{ fontSize: '0.75rem' }}>does not exist</MenuItem>
                          <MenuItem value="equals" sx={{ fontSize: '0.75rem' }}>equals</MenuItem>
                          <MenuItem value="not_equals" sx={{ fontSize: '0.75rem' }}>does not equal</MenuItem>
                          <MenuItem value="true" sx={{ fontSize: '0.75rem' }}>is true</MenuItem>
                          <MenuItem value="false" sx={{ fontSize: '0.75rem' }}>is false</MenuItem>
                          <MenuItem value="empty" sx={{ fontSize: '0.75rem' }}>is empty</MenuItem>
                          <MenuItem value="not_empty" sx={{ fontSize: '0.75rem' }}>is not empty</MenuItem>
                          <MenuItem value="contains" sx={{ fontSize: '0.75rem' }}>contains</MenuItem>
                          <MenuItem value="greater_than" sx={{ fontSize: '0.75rem' }}>is greater than</MenuItem>
                          <MenuItem value="less_than" sx={{ fontSize: '0.75rem' }}>is less than</MenuItem>
                        </Select>
                      </Box>
                      
                      {/* Value Input (only show for operators that need a value) */}
                      {['equals', 'not_equals', 'contains', 'greater_than', 'less_than'].includes(condition.operator) ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#374151', mb: 1 }}>
                            Value
                          </Typography>
                          <TextField
                            value={condition.value || ''}
                            onChange={(e) => {
                              const updatedConditions = [...(step.config?.collectInput?.validation?.conditionalRequirements || [])];
                              updatedConditions[condIndex] = {
                                ...updatedConditions[condIndex],
                                value: e.target.value
                              };
                              handleConfigChange('collectInput', {
                                ...step.config?.collectInput,
                                validation: {
                                  ...step.config?.collectInput?.validation,
                                  conditionalRequirements: updatedConditions
                                }
                              });
                            }}
                            size="small"
                            fullWidth
                            placeholder="Enter value..."
                            sx={{ fontSize: '0.75rem' }}
                          />
                        </Box>
                      ) : (
                        <Box></Box>
                      )}
                    </Box>
                    
                    {/* Requirement Action */}
                    <Box sx={{ mb: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={condition.makeRequired !== false}
                            onChange={(e) => {
                              const updatedConditions = [...(step.config?.collectInput?.validation?.conditionalRequirements || [])];
                              updatedConditions[condIndex] = {
                                ...updatedConditions[condIndex],
                                makeRequired: e.target.checked
                              };
                              handleConfigChange('collectInput', {
                                ...step.config?.collectInput,
                                validation: {
                                  ...step.config?.collectInput?.validation,
                                  conditionalRequirements: updatedConditions
                                }
                              });
                            }}
                            size="small"
                          />
                        }
                        label={`Make step ${condition.makeRequired !== false ? 'required' : 'optional'} when condition is met`}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Box>
                    
                    {/* Condition Preview */}
                    {condition.variable && (
                      <Box sx={{ 
                        backgroundColor: '#eff6ff', 
                        border: '1px solid #bfdbfe', 
                        borderRadius: 1, 
                        p: 1.5
                      }}>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1e40af' }}>
                          <strong>Preview:</strong> If {condition.variable} {condition.operator}
                          {['equals', 'not_equals', 'contains', 'greater_than', 'less_than'].includes(condition.operator) && condition.value && (
                            <span> "{condition.value}"</span>
                          )}
                          {', '}then this input step will be <strong>{condition.makeRequired !== false ? 'required' : 'optional'}</strong>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ 
                backgroundColor: '#f9fafb', 
                border: '1px solid #e5e7eb', 
                borderRadius: 1, 
                p: 3,
                textAlign: 'center'
              }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                  No conditional requirements set. This step's required status is fixed.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#9ca3af', mt: 1 }}>
                  Add conditions to dynamically change whether this step is required based on other variables.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Variable Extraction Configuration */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151', mb: 2 }}>
              Variable Extraction from User Input
            </Typography>
            <VariableManager
              step={step}
              onUpdateStep={onStepChange}
              availableVariables={availableVariables}
              projectId={step.project || flowSteps[0]?.project}
              apiEndpoints={availableApiEndpoints}
            />
          </Box>


        </Box>
      )}

      {step.type === 'branch' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            Branch Logic Configuration
          </Typography>
          
          {/* Branch Logic Overview */}
          <Box sx={{ 
            backgroundColor: '#fff7ed', 
            border: '1px solid #fed7aa', 
            borderRadius: 1, 
            p: 2, 
            mb: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#ea580c', mb: 1 }}>
              🔀 Branch Logic Overview
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#c2410c', mb: 1 }}>
              Configure conditions to control the flow path. Each condition can redirect to a specific step when met.
            </Typography>
            
            {/* Flow Diagram */}
            <Box sx={{ 
              backgroundColor: 'white', 
              borderRadius: 1, 
              p: 2, 
              border: '1px solid #fed7aa',
              mt: 2
            }}>
              <Box sx={{ fontSize: '0.7rem', color: '#c2410c', space: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, backgroundColor: '#ea580c', borderRadius: '50%', mr: 1 }}></Box>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                    <strong>IF</strong> conditions are met → Go to specified step
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 8, height: 8, backgroundColor: '#6b7280', borderRadius: '50%', mr: 1 }}></Box>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                    <strong>ELSE</strong> (no conditions met) → Go to default step
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Conditions Summary */}
          <Box sx={{ 
            backgroundColor: 'white', 
            border: '1px solid #e5e7eb', 
            borderRadius: 1, 
            p: 2,
            mb: 2
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                Conditional Paths
              </Typography>
              <Chip 
                label={`${step.config?.branch?.conditions?.length || 0} condition(s)`}
                size="small"
                sx={{ backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: '0.65rem', height: 20 }}
              />
            </Box>
            
            {step.config?.branch?.conditions?.length > 0 ? (
              <Box sx={{ space: 1 }}>
                {(step.config.branch.conditions || []).map((condition, index) => (
                  <Box key={index} sx={{ 
                    p: 2, 
                    backgroundColor: '#f9fafb', 
                    borderRadius: 1, 
                    border: '1px solid #e5e7eb',
                    mb: 1
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      Condition {index + 1}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      {condition.variable} {condition.operator} {condition.value}
                    </Typography>
                    {condition.nextStepId && (
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#059669' }}>
                        → Goes to: {flowSteps.find(s => s.id === condition.nextStepId)?.name || 'Unknown step'}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#ef4444', fontSize: '0.75rem', textAlign: 'center' }}>
                No conditions defined yet. Configure conditions in List View for detailed setup.
              </Typography>
            )}
          </Box>

          

          {/* Branch Summary */}
          <Box sx={{ 
            backgroundColor: '#eff6ff', 
            border: '1px solid #bfdbfe', 
            borderRadius: 1, 
            p: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, fontSize: '0.75rem' }}>
              Branch Summary
            </Typography>
            <Box sx={{ fontSize: '0.75rem', color: '#1e40af', space: 0.5 }}>
              {(step.config?.branch?.conditions || []).length === 0 ? (
                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                  No conditions defined - flow will continue to default step
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>Conditions:</strong> {(step.config?.branch?.conditions || []).length}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>Logic:</strong> {step.config?.branch?.globalLogicOperator || 'AND'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>Default step:</strong> {
                      step.config?.branch?.defaultNextStepId ? 
                        flowSteps.find(s => s.id === step.config.branch.defaultNextStepId)?.name || 'Unknown step' :
                        'Next step in order'
                    }
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Error Detection Helper */}
          <Box sx={{ 
            backgroundColor: '#fef2f2', 
            border: '2px solid #fca5a5', 
            borderRadius: 1, 
            p: 2,
            mb: 2
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626', mb: 2, fontSize: '0.75rem' }}>
              ⚠️ API Error Detection Quick Setup
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#b91c1c', mb: 2 }}>
              Quickly configure branch conditions to detect and handle API errors from previous steps.
            </Typography>
            
            {/* Quick Error Condition Buttons */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                                     const newCondition = {
                     variable: 'api_error',
                     operator: 'equals',
                     value: 'true',
                     conditionType: 'simple',
                     nextStepId: null
                   };
                  const updatedStep = {
                    ...step,
                    config: {
                      ...step.config,
                      branch: {
                        ...step.config?.branch,
                        conditions: [...(step.config?.branch?.conditions || []), newCondition],
                        conditionName: step.config?.branch?.conditionName || 'API Error Check'
                      }
                    }
                  };
                  onStepChange(updatedStep);
                }}
                sx={{ fontSize: '0.65rem', height: 28 }}
              >
                + API Error (api_error = true)
              </Button>

              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                                     const newCondition = {
                     variable: 'error_message',
                     operator: 'exists',
                     value: '',
                     conditionType: 'simple',
                     nextStepId: null
                   };
                  const updatedStep = {
                    ...step,
                    config: {
                      ...step.config,
                      branch: {
                        ...step.config?.branch,
                        conditions: [...(step.config?.branch?.conditions || []), newCondition],
                        conditionName: step.config?.branch?.conditionName || 'Error Message Check'
                      }
                    }
                  };
                  onStepChange(updatedStep);
                }}
                sx={{ fontSize: '0.65rem', height: 28 }}
              >
                + Error Message (error_message exists)
              </Button>

              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                                     const newCondition = {
                     variable: 'error_status_code',
                     operator: 'greater_than',
                     value: '399',
                     conditionType: 'simple',
                     nextStepId: null
                   };
                  const updatedStep = {
                    ...step,
                    config: {
                      ...step.config,
                      branch: {
                        ...step.config?.branch,
                        conditions: [...(step.config?.branch?.conditions || []), newCondition],
                        conditionName: step.config?.branch?.conditionName || 'HTTP Error Check'
                      }
                    }
                  };
                  onStepChange(updatedStep);
                }}
                sx={{ fontSize: '0.65rem', height: 28 }}
              >
                + HTTP Error (status &gt; 399)
              </Button>
            </Box>

            {/* Available Error Variables from Previous API Steps */}
            {(() => {
              const errorVariables = [];
              
              // Check all previous steps for API calls with error mappings
              flowSteps.forEach(stepInFlow => {
                if (stepInFlow.type === 'api_call' && stepInFlow.config?.apiCall?.errorResponseMapping) {
                  stepInFlow.config.apiCall.errorResponseMapping.forEach(mapping => {
                    if (mapping.key) {
                      errorVariables.push({
                        name: mapping.key,
                        description: mapping.description || 'Error variable from API call',
                        stepName: stepInFlow.name
                      });
                    }
                  });
                }
              });

              if (errorVariables.length > 0) {
                return (
                  <Box sx={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #fca5a5',
                    borderRadius: 1, 
                    p: 2,
                    mt: 2
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626', mb: 1 }}>
                      🎯 Error Variables Available from Previous Steps:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {errorVariables.map((variable, index) => (
                        <Chip
                          key={index}
                          label={`{{${variable.name}}}`}
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                                                         const newCondition = {
                               variable: variable.name,
                               operator: 'exists',
                               value: '',
                               conditionType: 'simple',
                               nextStepId: null
                             };
                            const updatedStep = {
                              ...step,
                              config: {
                                ...step.config,
                                branch: {
                                  ...step.config?.branch,
                                  conditions: [...(step.config?.branch?.conditions || []), newCondition],
                                  conditionName: step.config?.branch?.conditionName || `${variable.name} Check`
                                }
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          sx={{ 
                            fontSize: '0.65rem', 
                            height: 24,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#fef2f2'
                            }
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ color: '#b91c1c', fontSize: '0.65rem', display: 'block', mt: 1 }}>
                      Click any variable to create a condition that checks if it exists
                    </Typography>
                  </Box>
                );
              }
              return null;
            })()}

            {/* Standard Error Variables Reference */}
            <Box sx={{ 
              backgroundColor: 'white', 
              border: '1px solid #fca5a5',
              borderRadius: 1, 
              p: 2,
              mt: 2
            }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626', mb: 1 }}>
                📋 Standard Error Variables (always available):
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1, fontSize: '0.7rem', color: '#b91c1c' }}>
                <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                  • <code>api_error</code> - Boolean (true if any API failed)
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                  • <code>error_status_code</code> - HTTP status code
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                  • <code>error_message</code> - General error message
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Single Condition Configuration */}
          <Box sx={{ 
            backgroundColor: '#f0fdf4', 
            border: '1px solid #bbf7d0', 
            borderRadius: 1, 
            p: 2,
            mb: 2
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534', mb: 2, fontSize: '0.75rem' }}>
              ⚡ Branch Condition Setup
            </Typography>
            
            {/* Condition Name */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Condition Name"
                value={step.config?.branch?.conditionName || ''}
                onChange={(e) => {
                  const updatedStep = {
                    ...step,
                    config: {
                      ...step.config,
                      branch: {
                        ...step.config?.branch,
                        conditionName: e.target.value
                      }
                    }
                  };
                  if (step.onConfigChange) {
                    step.onConfigChange(updatedStep);
                  } else {
                    onStepChange(updatedStep);
                  }
                }}
                placeholder="e.g., VIP Customer Check, Age Verification, Location Check"
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                  '& .MuiInputBase-input': { fontSize: '0.75rem' }
                }}
              />
              <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                This name will appear on the canvas edges
              </Typography>
            </Box>

            {/* Existing Conditions Display */}
            {step.config?.branch?.conditions && step.config.branch.conditions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#166534', mb: 1 }}>
                  Current Conditions
                </Typography>
                {step.config.branch.conditions.map((condition, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 1, 
                    p: 2, 
                    backgroundColor: 'white', 
                    borderRadius: 1,
                    border: '1px solid #bbf7d0'
                  }}>
                    {index > 0 && (
                      <Typography variant="body2" sx={{ 
                        fontSize: '0.7rem', 
                        fontWeight: 600, 
                        color: '#166534',
                        minWidth: 30
                      }}>
                        AND
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', flex: 1 }}>
                      <span style={{ color: '#9333ea', fontWeight: 600 }}>
                        [{condition.type === 'time_date' ? 'Time/Date' : 
                          condition.type === 'numeric_comparison' ? 'Numeric' :
                          condition.type === 'boolean_logic' ? 'Boolean' :
                          condition.type === 'text_pattern' ? 'Text Pattern' :
                          'Variable'}]
                      </span>
                      {' '}
                      {condition.variable} {condition.operator} 
                      {!['exists', 'not_exists', 'is_true', 'is_false'].includes(condition.operator) && 
                        ` "${condition.value}"`
                      }
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updatedConditions = [...(step.config.branch.conditions || [])];
                        updatedConditions.splice(index, 1);
                        const updatedStep = {
                          ...step,
                          config: {
                            ...step.config,
                            branch: {
                              ...step.config.branch,
                              conditions: updatedConditions
                            }
                          }
                        };
                        if (step.onConfigChange) {
                          step.onConfigChange(updatedStep);
                        } else {
                          onStepChange(updatedStep);
                        }
                      }}
                      sx={{ color: '#dc2626' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            
            {/* Add New Condition */}
            <Box sx={{ 
              mb: 2,
              p: 2,
              backgroundColor: 'white',
              borderRadius: 1,
              border: '1px dashed #bbf7d0'
            }}>
              {console.log('🔀 BRANCH RENDER: Branch configuration rendering:', {
                stepId: step.id,
                stepName: step.name,
                stepType: step.type,
                availableVariablesCount: availableVariables?.length,
                currentTempCondition: step._tempCondition,
                branchConfig: step.config?.branch
              })}
              {/* AND Logic Indicator */}
              {(step.config?.branch?.conditions || []).length > 0 && (
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ 
                    fontSize: '0.7rem', 
                    fontWeight: 600, 
                    color: '#166534',
                    backgroundColor: '#f0fdf4',
                    px: 1,
                    py: 0.5,
                    borderRadius: 0.5,
                    display: 'inline-block'
                  }}>
                    AND
                  </Typography>
                </Box>
              )}
              
              {/* First Row: Condition Type */}
              <Box sx={{ mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Condition Type</InputLabel>
                  <Select
                    value={step._tempCondition?.type || 'variable_value'}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempCondition: {
                          ...step._tempCondition,
                          type: e.target.value,
                          operator: getOperatorsForConditionType(e.target.value)[0]?.value || 'equals' // Reset operator when type changes
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Condition Type"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="variable_value">Variable Value</MenuItem>
                    <MenuItem value="array_length">Array Length</MenuItem>
                    <MenuItem value="time_date">Time/Date Comparison</MenuItem>
                    <MenuItem value="numeric_comparison">Numeric Comparison</MenuItem>
                    <MenuItem value="boolean_logic">Boolean Logic</MenuItem>
                    <MenuItem value="text_pattern">Text Pattern</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Second Row: Variable and Operator */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                mb: 2
              }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Variable</InputLabel>
                  <Select
                    value={step._tempCondition?.variable || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempCondition: {
                          ...step._tempCondition,
                          variable: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Variable"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {(availableVariables || []).map((variable) => (
                      <MenuItem key={variable.name} value={variable.name}>
                        {variable.name}
                      </MenuItem>
                    ))}
                    {/* Add system variables */}
                    <MenuItem value="current_timestamp">Current Timestamp</MenuItem>
                    <MenuItem value="current_date">Current Date</MenuItem>
                    <MenuItem value="TODAY">Today</MenuItem>
                    <MenuItem value="NOW">Now</MenuItem>
                    <MenuItem value="current_time">Current Time</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Operator</InputLabel>
                  <Select
                    value={step._tempCondition?.operator || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempCondition: {
                          ...step._tempCondition,
                          operator: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Operator"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {getOperatorsForConditionType(step._tempCondition?.type || 'variable_value').map((operator) => (
                      <MenuItem key={operator.value} value={operator.value}>
                        {operator.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              {/* Third Row: Value and Add Button */}
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center'
              }}>
                {/* Only show value field if operator is not exists/not_exists */}
                {!['exists', 'not_exists', 'is_true', 'is_false'].includes(step._tempCondition?.operator) && (
                  <TextField
                    size="small"
                    label={
                      step._tempCondition?.type === 'time_date' ? 'Date/Time Value' :
                      step._tempCondition?.type === 'numeric_comparison' ? 'Numeric Value' :
                      step._tempCondition?.type === 'text_pattern' && step._tempCondition?.operator === 'matches' ? 'Regular Expression' :
                      'Comparison Value'
                    }
                    type={
                      step._tempCondition?.type === 'numeric_comparison' ? 'number' :
                      'text'
                    }
                    value={step._tempCondition?.value || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempCondition: {
                          ...step._tempCondition,
                          value: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder={
                      step._tempCondition?.type === 'time_date' ? '{{NOW}} or {{TODAY}} or {{current_timestamp}} or 2024-12-31T10:00' :
                      step._tempCondition?.type === 'text_pattern' && step._tempCondition?.operator === 'matches' ? 'e.g., /(cancel|hapus|batalkan)/i' :
                      step._tempCondition?.type === 'variable_value' ? 'Value or {{variable_name}}' :
                      'Enter comparison value'
                    }
                    sx={{ flex: 1 }}
                  />
                )}
                
                {/* Show help text for time/date comparison */}
                {step._tempCondition?.type === 'time_date' && !['exists', 'not_exists', 'is_true', 'is_false'].includes(step._tempCondition?.operator) && (
                  <Box sx={{ 
                    flex: 1, 
                    p: 1, 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: 1,
                    border: '1px solid #bfdbfe',
                    ml: 1
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#1e40af' }}>
                      💡 Use {`{{NOW}}`} for current time, {`{{TODAY}}`} for today's date, or enter a specific date/time
                    </Typography>
                  </Box>
                )}
                
                {/* Show helpful text for operators that don't need values */}
                {['exists', 'not_exists', 'is_true', 'is_false'].includes(step._tempCondition?.operator) && (
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: 1,
                    border: '1px solid #bfdbfe'
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#1e40af' }}>
                      ℹ️ No value needed for "{step._tempCondition?.operator}" operator
                    </Typography>
                  </Box>
                )}
                
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const tempCondition = step._tempCondition;
                    
                    if (tempCondition?.variable && tempCondition?.operator && 
                        (tempCondition?.value || ['exists', 'not_exists', 'is_true', 'is_false'].includes(tempCondition?.operator))) {
                      const newCondition = {
                        id: `condition_${Date.now()}`, // Required by backend schema
                        type: tempCondition.type || 'variable_value', // Include the condition type
                        variable: tempCondition.variable,
                        operator: tempCondition.operator,
                        value: tempCondition.value,
                        logicOperator: 'AND', // Required for multiple conditions
                        nextStepId: null // Will be handled by trueNextStepId/falseNextStepId instead
                      };
                      
                      const currentConditions = step.config?.branch?.conditions || [];
                      
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          branch: {
                            ...step.config?.branch,
                            conditions: [...currentConditions, newCondition]
                          }
                        },
                        _tempCondition: { type: 'variable_value' } // Clear temp condition but keep type
                      };
                      
                      if (step.onConfigChange) {
                        step.onConfigChange(updatedStep);
                      } else {
                        onStepChange(updatedStep);
                      }
                    }
                  }}
                  disabled={!step._tempCondition?.variable || !step._tempCondition?.operator || 
                    (!step._tempCondition?.value && !['exists', 'not_exists', 'is_true', 'is_false'].includes(step._tempCondition?.operator))}
                  sx={{ 
                    minWidth: 'auto', 
                    px: 2,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Add Condition
                </Button>
              </Box>
            </Box>
            
            {/* TRUE/FALSE Path Configuration */}
            <Box sx={{ space: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#166534', minWidth: 80 }}>
                  If TRUE, go to:
                </Typography>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Next Step</InputLabel>
                  <Select
                    value={step.config?.branch?.trueNextStepId || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          branch: {
                            ...step.config?.branch,
                            trueNextStepId: e.target.value
                          }
                        }
                      };
                      if (step.onConfigChange) {
                        step.onConfigChange(updatedStep);
                      } else {
                        onStepChange(updatedStep);
                      }
                    }}
                    label="Next Step"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {flowSteps
                      .filter(s => s.id !== step.id)
                      .map((nextStep) => (
                        <MenuItem key={nextStep.id} value={nextStep.id}>
                          {nextStep.name} ({nextStep.type})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#dc2626', minWidth: 80 }}>
                  If FALSE, go to:
                </Typography>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Next Step</InputLabel>
                  <Select
                    value={step.config?.branch?.falseNextStepId || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        config: {
                          ...step.config,
                          branch: {
                            ...step.config?.branch,
                            falseNextStepId: e.target.value
                          }
                        }
                      };
                      if (step.onConfigChange) {
                        step.onConfigChange(updatedStep);
                      } else {
                        onStepChange(updatedStep);
                      }
                    }}
                    label="Next Step"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {flowSteps
                      .filter(s => s.id !== step.id)
                      .map((nextStep) => (
                        <MenuItem key={nextStep.id} value={nextStep.id}>
                          {nextStep.name} ({nextStep.type})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          {/* Branch Summary - Single Condition */}
          {(step.config?.branch?.conditions && step.config.branch.conditions.length > 0) ||
           (step.config?.branch?.trueNextStepId || step.config?.branch?.falseNextStepId) ? (
            <Box sx={{ 
              backgroundColor: '#eff6ff', 
              border: '1px solid #bfdbfe', 
              borderRadius: 1, 
              p: 2,
              mb: 2
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', mb: 2, fontSize: '0.75rem' }}>
                📋 Branch Configuration
              </Typography>
              
              <Box sx={{ space: 1 }}>
                {step.config?.branch?.conditionName && (
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#1e40af', mb: 1 }}>
                    <strong>Condition Name:</strong> {step.config.branch.conditionName}
                  </Typography>
                )}
                
                {step.config?.branch?.conditions && step.config.branch.conditions.length > 0 && (
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#1e40af', mb: 1 }}>
                    <strong>Conditions:</strong> {step.config.branch.conditions.length} condition{step.config.branch.conditions.length > 1 ? 's with AND logic' : ''}
                  </Typography>
                )}
                
                <Box sx={{ display: 'flex', gap: 2, fontSize: '0.6rem', mt: 1 }}>
                  {step.config?.branch?.trueNextStepId && (
                    <Typography variant="body2" sx={{ fontSize: '0.6rem', color: '#059669' }}>
                      ✓ TRUE → {flowSteps.find(s => s.id === step.config.branch.trueNextStepId)?.name || 'Unknown'}
                    </Typography>
                  )}
                  {step.config?.branch?.falseNextStepId && (
                    <Typography variant="body2" sx={{ fontSize: '0.6rem', color: '#dc2626' }}>
                      ✗ FALSE → {flowSteps.find(s => s.id === step.config.branch.falseNextStepId)?.name || 'Unknown'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      )}

      {/* Variable Operation Step Configuration */}
      {step.type === 'variable_operation' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
            Variable Operation Configuration
          </Typography>
          
          {/* Variable Operation Overview */}
          <Box sx={{ 
            backgroundColor: '#fdf2f8', 
            border: '1px solid #fbb6ce', 
            borderRadius: 1, 
            p: 2, 
            mb: 2 
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#ec4899', mb: 1 }}>
              ⚙️ Variable Operation Overview
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#be185d', mb: 1 }}>
              This step allows you to set, modify, or clear variables without sending any message to the user.
              Perfect for post-branch logic and internal flow control.
            </Typography>
          </Box>

          {/* Variable Operations Configuration */}
          <Box sx={{ 
            backgroundColor: '#faf5ff', 
            border: '1px solid #d8b4fe', 
            borderRadius: 1, 
            p: 2 
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed', mb: 2 }}>
              Configure Variable Operations
            </Typography>
            
            {/* Existing Variable Operations */}
            {((step.variableOperations && step.variableOperations.length > 0) || (step.config?.variableOperation?.operations && step.config.variableOperation.operations.length > 0)) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed', mb: 1 }}>
                  Current Operations ({(step.variableOperations || step.config?.variableOperation?.operations || []).length})
                </Typography>
                {(step.variableOperations || step.config?.variableOperation?.operations || []).map((operation, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 1, 
                    p: 2, 
                    backgroundColor: 'white', 
                    borderRadius: 1,
                    border: '1px solid #d8b4fe'
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', flex: 1 }}>
                      {operation.operation === 'set_null' ? `Set ${operation.variableName} = null` : 
                       operation.operation === 'set_value' ? (
                         operation.value === '[]' ? `Create empty array: ${operation.variableName}` :
                         operation.value === '{}' ? `Create empty object: ${operation.variableName}` :
                         operation.value && operation.value.startsWith('[') ? `Set array ${operation.variableName} = ${operation.value}` :
                         operation.value && operation.value.startsWith('{') ? `Set object ${operation.variableName} = ${operation.value}` :
                         `Set ${operation.variableName} = "${operation.value}"`
                       ) :
                       operation.operation === 'clear' ? `Clear ${operation.variableName}` :
                       operation.operation === 'combine_datetime' ? `Combine ${operation.dateVariable} + ${operation.timeVariable} → ${operation.variableName}${operation.dateFormat ? ` (${operation.dateFormat})` : ''}` :
                       operation.operation === 'format_date' ? `Format ${operation.sourceVariable} → ${operation.variableName} (${operation.targetFormat})` :
                       operation.operation === 'calculate_date' ? `${operation.dateOperation === 'add' ? 'Add' : 'Subtract'} ${operation.amount} ${operation.unit} ${operation.dateOperation === 'add' ? 'to' : 'from'} ${operation.sourceVariable} → ${operation.variableName}` :
                       operation.operation === 'create_array' ? `Create empty array: ${operation.variableName}` :
                       operation.operation === 'create_object' ? `Create empty object: ${operation.variableName}` :
                       operation.operation === 'add_to_object' ? `Add properties to object: ${operation.variableName}` :
                       operation.operation === 'ai_extract' ? `🤖 AI Extract → ${operation.variableName}: ${operation.extractionPrompt?.substring(0, 40) || 'No prompt'}...` :
                       operation.operation === 'parse_json' ? `📋 Parse JSON → ${operation.variableName}` :
                       operation.operation === 'convert_to_object' ? `🔄 Convert to Object → ${operation.variableName}${operation.sourceVariable ? ` (from ${operation.sourceVariable})` : ''}` :
                       operation.operation === 'convert_to_array' ? `🔄 Convert to Array → ${operation.variableName}${operation.sourceVariable ? ` (from ${operation.sourceVariable})` : ''}${operation.conversionType && operation.conversionType !== 'values' ? ` [${operation.conversionType}]` : ''}` :
                       `${operation.operation} ${operation.variableName}`}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        console.log('🔧 Variable Operations - Edit Operation clicked, index:', index);
                        const operationToEdit = operation;
                        console.log('🔧 Variable Operations - Operation to edit:', operationToEdit);
                        
                        // Populate the temp operation with the existing operation for editing
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...operationToEdit,
                            _editingIndex: index // Track which operation we're editing
                          },
                          _isEditingOperation: true // Flag to show we're in edit mode
                        };
                        
                        console.log('🔧 Variable Operations - Updated step for edit:', updatedStep);
                        onStepChange(updatedStep);
                      }}
                      sx={{ minWidth: 'auto', p: 0.5, color: '#059669', mr: 0.5 }}
                    >
                      ✏️
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        const currentOperations = step.variableOperations || step.config?.variableOperation?.operations || [];
                        const updatedOperations = currentOperations.filter((_, i) => i !== index);
                        
                        const updatedStep = {
                          ...step,
                          variableOperations: updatedOperations,
                          config: {
                            ...step.config,
                            variableOperation: {
                              ...step.config?.variableOperation,
                              operations: updatedOperations
                            }
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      sx={{ minWidth: 'auto', p: 0.5, color: '#dc2626' }}
                    >
                      ×
                    </Button>
                  </Box>
                ))}
              </Box>
            )}

            {/* Add New Variable Operation */}
            <Box sx={{ 
              backgroundColor: 'white', 
              border: '1px solid #e5e7eb', 
              borderRadius: 1, 
              p: 2 
            }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                {step._isEditingOperation ? '✏️ Edit Variable Operation' : 'Add Variable Operation'}
              </Typography>
              
              {step._isEditingOperation && (
                <Box sx={{ mb: 2, p: 1, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem' }}>
                    ℹ️ Editing operation #{(step._tempVariableOperation?._editingIndex || 0) + 1}: {step._tempVariableOperation?.operation} on {step._tempVariableOperation?.variableName}
                  </Typography>
                  {/* DEBUG INFO */}
                  <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.6rem', display: 'block', mt: 0.5 }}>
                    🐛 DEBUG: Section=1, _isEditingOperation={String(step._isEditingOperation)}, stepType={step.type}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Variable</InputLabel>
                  <Select
                    value={step._tempVariableOperation?.variableName || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          variableName: e.target.value,
                          ...(e.target.value !== '__CREATE_NEW__' ? { customVariableName: '' } : {})
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Variable"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {(availableVariables || []).map((variable) => (
                      <MenuItem key={variable.name} value={variable.name}>
                        {variable.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="__CREATE_NEW__" sx={{ color: '#059669', fontWeight: 600 }}>
                      + Create New Variable
                    </MenuItem>
                  </Select>
                </FormControl>
                
                {step._tempVariableOperation?.variableName === '__CREATE_NEW__' && (
                  <TextField
                    size="small"
                    label="Variable Name"
                    value={step._tempVariableOperation?.customVariableName || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          customVariableName: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Enter variable name (e.g., my_variable)"
                    sx={{ flex: 1 }}
                  />
                )}
                
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Operation</InputLabel>
                  <Select
                    value={step._tempVariableOperation?.operation || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          operation: e.target.value,
                          value: e.target.value === 'set_value' ? step._tempVariableOperation?.value || '' : 
                                 e.target.value === 'create_array' ? '[]' :
                                 e.target.value === 'create_object' ? '{}' :
                                 undefined
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Operation"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    <MenuItem value="set_null">Set to Null</MenuItem>
                    <MenuItem value="set_value">Set to Value</MenuItem>
                    <MenuItem value="create_array">Create Empty Array</MenuItem>
                    <MenuItem value="append_to_array">Append to Array</MenuItem>
                    <MenuItem value="add_to_object">Add to Object</MenuItem>
                    <MenuItem value="clear">Clear Variable</MenuItem>
                    <MenuItem value="combine_datetime">Combine Date + Time</MenuItem>
                    <MenuItem value="format_date">Format Date</MenuItem>
                    <MenuItem value="calculate_date">Calculate Date</MenuItem>
                    <MenuItem value="create_object">Create Object</MenuItem>
                    <MenuItem value="ai_extract">🤖 AI Extract</MenuItem>
                    <MenuItem value="parse_json">📋 Parse JSON String</MenuItem>
                    <MenuItem value="convert_to_object">🔄 Convert to Object</MenuItem>
                    <MenuItem value="convert_to_array">🔄 Convert to Array</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              {step._tempVariableOperation?.operation === 'set_value' && (
                <TextField
                  size="small"
                  label="Value"
                  value={step._tempVariableOperation?.value || ''}
                  onChange={(e) => {
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {
                        ...step._tempVariableOperation,
                        value: e.target.value
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                  placeholder="Enter value (use {{variable}} for variable references)"
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
              
              {/* Object Merge Configuration */}
              {step._tempVariableOperation?.operation === 'add_to_object' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    Configure Object Properties
                  </Typography>
                  
                  {/* Existing object merge items */}
                  {(step._tempVariableOperation?.objectMerge || []).map((mergeItem, index) => (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      gap: 1, 
                      mb: 1, 
                      p: 2, 
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: 1 
                    }}>
                      <TextField
                        size="small"
                        label="Key"
                        value={mergeItem.key || ''}
                        onChange={(e) => {
                          const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                          updatedObjectMerge[index] = { ...mergeItem, key: e.target.value };
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              objectMerge: updatedObjectMerge
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        placeholder="Property name"
                        sx={{ flex: 1 }}
                      />
                      
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={{ fontSize: '0.75rem' }}>Type</InputLabel>
                        <Select
                          value={mergeItem.valueType || 'static'}
                          onChange={(e) => {
                            const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                            updatedObjectMerge[index] = { ...mergeItem, valueType: e.target.value };
                            const updatedStep = {
                              ...step,
                              _tempVariableOperation: {
                                ...step._tempVariableOperation,
                                objectMerge: updatedObjectMerge
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          label="Type"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <MenuItem value="static">Static Value</MenuItem>
                          <MenuItem value="variable">Variable Reference</MenuItem>
                          <MenuItem value="expression">Expression</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        size="small"
                        label={mergeItem.valueType === 'variable' ? 'Variable Name' : 'Value'}
                        value={mergeItem.value || ''}
                        onChange={(e) => {
                          const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                          updatedObjectMerge[index] = { ...mergeItem, value: e.target.value };
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              objectMerge: updatedObjectMerge
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        placeholder={
                          mergeItem.valueType === 'variable' ? 'variable_name' :
                          mergeItem.valueType === 'expression' ? '{{variable}} or complex expression' :
                          'Static value'
                        }
                        sx={{ flex: 2 }}
                      />
                      
                      <Button
                        size="small"
                        onClick={() => {
                          const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                          updatedObjectMerge.splice(index, 1);
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              objectMerge: updatedObjectMerge
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        sx={{ minWidth: 'auto', p: 0.5, color: '#dc2626' }}
                      >
                        ×
                      </Button>
                    </Box>
                  ))}
                  
                  {/* Add new property button */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const newMergeItem = { key: '', value: '', valueType: 'static' };
                      const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || []), newMergeItem];
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          objectMerge: updatedObjectMerge
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    sx={{ fontSize: '0.75rem', mt: 1 }}
                  >
                    + Add Property
                  </Button>
                  
                  {/* Help text */}
                  <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#0369a1', mb: 1, fontWeight: 600 }}>
                      💡 How to use:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#0369a1', mb: 0.5 }}>
                      • <strong>Static Value:</strong> Fixed text or number (e.g., "John Doe", 123)
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#0369a1', mb: 0.5 }}>
                      • <strong>Variable Reference:</strong> Name of another variable (e.g., patient_id, user_name)
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#0369a1' }}>
                      • <strong>Expression:</strong> Complex values with variables (e.g., {`"Hello {{user_name}}"`})
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Combine Date + Time Configuration */}
              {step._tempVariableOperation?.operation === 'combine_datetime' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    Combine Date + Time Configuration
                  </Typography>
                  
                  <TextField
                    size="small"
                    label="Date Variable"
                    value={step._tempVariableOperation?.dateVariable || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          dateVariable: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Variable containing date (e.g., lastScheduleDate)"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  
                  <TextField
                    size="small"
                    label="Time Variable"
                    value={step._tempVariableOperation?.timeVariable || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          timeVariable: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Variable containing time (e.g., lastScheduleEndTime)"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Output Format</InputLabel>
                    <Select
                      value={step._tempVariableOperation?.outputFormat || 'iso'}
                      onChange={(e) => {
                        console.log('🔥 OUTPUT FORMAT CHANGED:', e.target.value);
                        console.log('🔥 Current _tempVariableOperation before:', step._tempVariableOperation);
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            outputFormat: e.target.value
                          }
                        };
                        console.log('🔥 Updated _tempVariableOperation after:', updatedStep._tempVariableOperation);
                        onStepChange(updatedStep);
                      }}
                      label="Output Format"
                    >
                      <MenuItem value="iso">ISO Format (2025-06-28T14:00:00.000Z)</MenuItem>
                      <MenuItem value="timestamp">Timestamp (1751180400000)</MenuItem>
                      <MenuItem value="readable">Readable (June 28, 2025 at 2:00 PM)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#0369a1' }}>
                      💡 Example: Date variable "2025-06-28" + Time variable "14:00" → Combined datetime
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Format Date Configuration */}
              {step._tempVariableOperation?.operation === 'format_date' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    Format Date Configuration
                  </Typography>
                  
                  <TextField
                    size="small"
                    label="Source Variable"
                    value={step._tempVariableOperation?.sourceVariable || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          sourceVariable: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Variable containing date/datetime to format"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Target Format</InputLabel>
                    <Select
                      value={step._tempVariableOperation?.targetFormat || 'YYYY-MM-DD'}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            targetFormat: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      label="Target Format"
                    >
                      <MenuItem value="YYYY-MM-DD">YYYY-MM-DD (2025-06-28)</MenuItem>
                      <MenuItem value="DD/MM/YYYY">DD/MM/YYYY (28/06/2025)</MenuItem>
                      <MenuItem value="MM/DD/YYYY">MM/DD/YYYY (06/28/2025)</MenuItem>
                      <MenuItem value="readable">Readable (June 28, 2025)</MenuItem>
                      <MenuItem value="iso">ISO (2025-06-28T14:00:00.000Z)</MenuItem>
                      <MenuItem value="timestamp">Timestamp (1751180400000)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#0369a1' }}>
                      💡 Convert dates between different formats for display or comparison
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Calculate Date Configuration */}
              {step._tempVariableOperation?.operation === 'calculate_date' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    Calculate Date Configuration
                  </Typography>
                  
                  <TextField
                    size="small"
                    label="Source Variable"
                    value={step._tempVariableOperation?.sourceVariable || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          sourceVariable: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Variable containing source date"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Operation</InputLabel>
                    <Select
                      value={step._tempVariableOperation?.dateOperation || 'add'}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            dateOperation: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      label="Operation"
                    >
                      <MenuItem value="add">Add Time</MenuItem>
                      <MenuItem value="subtract">Subtract Time</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      label="Amount"
                      type="number"
                      value={step._tempVariableOperation?.amount || ''}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            amount: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      placeholder="Number of units"
                      sx={{ flex: 1 }}
                    />
                    
                    <FormControl size="small" sx={{ flex: 1 }}>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        value={step._tempVariableOperation?.unit || 'days'}
                        onChange={(e) => {
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              unit: e.target.value
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        label="Unit"
                      >
                        <MenuItem value="minutes">Minutes</MenuItem>
                        <MenuItem value="hours">Hours</MenuItem>
                        <MenuItem value="days">Days</MenuItem>
                        <MenuItem value="weeks">Weeks</MenuItem>
                        <MenuItem value="months">Months</MenuItem>
                        <MenuItem value="years">Years</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#0369a1' }}>
                      💡 Example: Add 7 days to appointment date to create follow-up reminder
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Special message for create_array operation */}
              {step._tempVariableOperation?.operation === 'create_array' && (
                <Box sx={{ mb: 2, p: 1, backgroundColor: '#e0f2fe', borderRadius: 1, border: '1px solid #29b6f6' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#0d47a1' }}>
                    ℹ️ This will create an empty array variable that you can use with filtering and append operations.
                  </Typography>
                </Box>
              )}

              {/* Special message for create_object operation */}
              {step._tempVariableOperation?.operation === 'create_object' && (
                <Box sx={{ mb: 2, p: 1, backgroundColor: '#e0f2fe', borderRadius: 1, border: '1px solid #29b6f6' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#0d47a1' }}>
                    ℹ️ This will create an empty object variable {} that you can populate using "Add to Object" operations.
                  </Typography>
                </Box>
              )}

              {/* AI Extract Configuration */}
              {step._tempVariableOperation?.operation === 'ai_extract' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    🤖 AI Extraction Configuration
                  </Typography>
                  
                  <TextField
                    size="small"
                    label="Extraction Prompt"
                    value={step._tempVariableOperation?.extractionPrompt || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          extractionPrompt: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Describe what you want to extract in natural language"
                    fullWidth
                    sx={{ mb: 2 }}
                    multiline
                    rows={8}
                    helperText="Example: Extract the doctor's name from the conversation history"
                  />
                  
                  <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Context Source</InputLabel>
                    <Select
                      value={step._tempVariableOperation?.extractionContext || 'conversation_history'}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            extractionContext: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      label="Context Source"
                    >
                      <MenuItem value="last_message">Last User Message</MenuItem>
                      <MenuItem value="conversation_history">Conversation History</MenuItem>
                      <MenuItem value="all_variables">All Variables</MenuItem>
                      <MenuItem value="custom">Custom Context</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {step._tempVariableOperation?.extractionContext === 'custom' && (
                    <TextField
                      size="small"
                      label="Custom Context Template"
                      value={step._tempVariableOperation?.customContext || ''}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            customContext: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      placeholder="Use {{variableName}} to include specific variables in context"
                      fullWidth
                      sx={{ mb: 2 }}
                      multiline
                      rows={2}
                      helperText="Example: Patient details: {{patientName}}, Appointment: {{appointmentTime}}"
                    />
                  )}
                  
                  <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Model</InputLabel>
                    <Select
                      value={step._tempVariableOperation?.extractionModel || 'gpt-4o-mini'}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            extractionModel: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      label="Model"
                    >
                      {OPENAI_CHAT_MODEL_OPTIONS.map((model) => (
                        <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                      ))}
                      <MenuItem value="deepseek-chat">DeepSeek Chat</MenuItem>
                      <MenuItem value="deepseek-reasoner">DeepSeek Reasoner</MenuItem>
                      {CLAUDE_MODEL_OPTIONS.map((model) => (
                        <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                      ))}
                      {GEMINI_MODEL_OPTIONS.map((model) => (
                        <MenuItem key={`gemini-${model.value}`} value={model.value}>{model.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={step._tempVariableOperation?.required !== false}
                        onChange={(e) => {
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              required: e.target.checked
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        size="small"
                      />
                    }
                    label="Required (flow fails if extraction fails)"
                    sx={{ mb: 1 }}
                  />
                  
                  <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem', display: 'block' }}>
                      💡 AI will intelligently extract information based on your prompt. Works great for extracting names, dates, locations, and other specific data from conversations.
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Parse JSON Configuration */}
              {step._tempVariableOperation?.operation === 'parse_json' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    📋 Parse JSON Configuration
                  </Typography>
                  
                  <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem', display: 'block' }}>
                      💡 This will convert a JSON string stored in the variable into a proper JavaScript object or array. Perfect for converting AI-extracted JSON strings into usable data structures.
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Convert to Object Configuration */}
              {step._tempVariableOperation?.operation === 'convert_to_object' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    🔄 Convert to Object Configuration
                  </Typography>
                  
                  <TextField
                    size="small"
                    label="Source Variable (optional)"
                    value={step._tempVariableOperation?.sourceVariable || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          sourceVariable: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Leave empty to convert the target variable itself"
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText="Specify a different variable to convert from, or leave empty to convert the target variable"
                  />
                  
                  <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem', display: 'block' }}>
                      💡 Converts arrays, strings, or primitive values into objects. Arrays become objects with indices as keys, strings are parsed as JSON if possible, primitives are wrapped in {'{value: data}'}.
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Convert to Array Configuration */}
              {step._tempVariableOperation?.operation === 'convert_to_array' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                    🔄 Convert to Array Configuration
                  </Typography>
                  
                  <TextField
                    size="small"
                    label="Source Variable (optional)"
                    value={step._tempVariableOperation?.sourceVariable || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          sourceVariable: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Leave empty to convert the target variable itself"
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText="Specify a different variable to convert from, or leave empty to convert the target variable"
                  />
                  
                  <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Object Conversion Type</InputLabel>
                    <Select
                      value={step._tempVariableOperation?.conversionType || 'values'}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            conversionType: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      label="Object Conversion Type"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      <MenuItem value="values">Object Values</MenuItem>
                      <MenuItem value="keys">Object Keys</MenuItem>
                      <MenuItem value="entries">Key-Value Entries</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    size="small"
                    label="String Delimiter (optional)"
                    value={step._tempVariableOperation?.delimiter || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          delimiter: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="e.g., ',' or '|' or '\\n'"
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText="If converting a string, specify delimiter to split by (leave empty to wrap as single item)"
                  />
                  
                  <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem', display: 'block' }}>
                      💡 Converts objects, strings, or primitive values into arrays. Objects can be converted to arrays of values, keys, or key-value pairs. Strings can be split by delimiter or wrapped as single items.
                    </Typography>
                  </Box>
                </Box>
              )}
              
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  const tempOperation = step._tempVariableOperation;
                  const isEditing = step._isEditingOperation;
                  const editingIndex = tempOperation?._editingIndex;
                  
                  if (tempOperation?.variableName && tempOperation?.operation) {
                    const variableName = tempOperation.variableName === '__CREATE_NEW__' ? 
                      tempOperation.customVariableName : tempOperation.variableName;
                    
                    if (!variableName) return;
                    
                    const newOperation = {
                      variableName,
                      operation: tempOperation.operation,
                      value: tempOperation.value || 
                             (tempOperation.operation === 'create_array' ? '[]' :
                              tempOperation.operation === 'create_object' ? '{}' : undefined)
                    };
                    
                    // Add object merge data for add_to_object operation
                    if (tempOperation.operation === 'add_to_object' && tempOperation.objectMerge) {
                      newOperation.objectMerge = tempOperation.objectMerge;
                    }
                    
                    // Add date/time operation specific fields
                    if (tempOperation.operation === 'combine_datetime') {
                      newOperation.dateVariable = tempOperation.dateVariable;
                      newOperation.timeVariable = tempOperation.timeVariable;
                      newOperation.outputFormat = tempOperation.outputFormat || 'iso';
                    } else if (tempOperation.operation === 'format_date') {
                      newOperation.sourceVariable = tempOperation.sourceVariable;
                      newOperation.targetFormat = tempOperation.targetFormat || 'YYYY-MM-DD';
                    } else if (tempOperation.operation === 'calculate_date') {
                      newOperation.sourceVariable = tempOperation.sourceVariable;
                      newOperation.dateOperation = tempOperation.dateOperation || 'add';
                      newOperation.amount = tempOperation.amount;
                      newOperation.unit = tempOperation.unit || 'days';
                    } else if (tempOperation.operation === 'ai_extract') {
                      // For ai_extract, include extraction configuration
                      newOperation.extractionPrompt = tempOperation.extractionPrompt;
                      newOperation.extractionModel = tempOperation.extractionModel || 'gpt-4o-mini';
                      newOperation.extractionContext = tempOperation.extractionContext || 'conversation_history';
                      newOperation.customContext = tempOperation.customContext;
                      newOperation.required = tempOperation.required !== false; // Default to true
                    } else if (tempOperation.operation === 'convert_to_object') {
                      // For convert_to_object, include source variable
                      newOperation.sourceVariable = tempOperation.sourceVariable;
                    } else if (tempOperation.operation === 'convert_to_array') {
                      // For convert_to_array, include source variable and conversion options
                      newOperation.sourceVariable = tempOperation.sourceVariable;
                      newOperation.conversionType = tempOperation.conversionType || 'values';
                      newOperation.delimiter = tempOperation.delimiter;
                    }
                    
                    const currentOperations = step.variableOperations || step.config?.variableOperation?.operations || [];
                    let updatedOperations;
                    
                    if (isEditing && typeof editingIndex === 'number') {
                      // Update existing operation
                      updatedOperations = [...currentOperations];
                      updatedOperations[editingIndex] = newOperation;
                      console.log(`🔧 Variable Operations - Updated operation at index ${editingIndex}`);
                    } else {
                      // Add new operation
                      updatedOperations = [...currentOperations, newOperation];
                      console.log('🔧 Variable Operations - Added new operation');
                    }
                    
                    const updatedStep = {
                      ...step,
                      variableOperations: updatedOperations,
                      config: {
                        ...step.config,
                        variableOperation: {
                          ...step.config?.variableOperation,
                          operations: updatedOperations
                        }
                      },
                      _tempVariableOperation: {}, // Clear temp operation
                      _isEditingOperation: false // Exit edit mode
                    };
                    onStepChange(updatedStep);
                  }
                }}
                              disabled={!step._tempVariableOperation?.variableName || !step._tempVariableOperation?.operation ||
                       (step._tempVariableOperation?.variableName === '__CREATE_NEW__' && !step._tempVariableOperation?.customVariableName) ||
                       (step._tempVariableOperation?.operation === 'combine_datetime' && (!step._tempVariableOperation?.dateVariable || !step._tempVariableOperation?.timeVariable)) ||
                       (step._tempVariableOperation?.operation === 'format_date' && !step._tempVariableOperation?.sourceVariable) ||
                       (step._tempVariableOperation?.operation === 'calculate_date' && (!step._tempVariableOperation?.sourceVariable || !step._tempVariableOperation?.amount)) ||
                       (step._tempVariableOperation?.operation === 'ai_extract' && !step._tempVariableOperation?.extractionPrompt)}
              sx={{ fontSize: '0.75rem' }}
              >
                {step._isEditingOperation ? 'Update Operation' : 'Add Operation'}
              </Button>
              
              {step._isEditingOperation && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    console.log('🔧 Variable Operations - Cancel Edit clicked');
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {},
                      _isEditingOperation: false
                    };
                    onStepChange(updatedStep);
                  }}
                  sx={{ 
                    ml: 1,
                    color: '#6b7280',
                    fontSize: '0.75rem'
                  }}
                >
                  Cancel Edit
                </Button>
              )}
              
              {/* DEBUG BUTTONS */}
              <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.6rem', display: 'block', mt: 1 }}>
                🐛 DEBUG: Section=1, isEditing={String(step._isEditingOperation)}, showCancel={String(!!step._isEditingOperation)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Variable Operations Section */}
      {(step.type === 'branch' || step.type === 'message' || step.type === 'intermediate_message' || step.type === 'api_call' || step.type === 'collect_input' || step.type === 'generate_message' || step.type === 'knowledge_lookup') && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#7c3aed', mb: 2, fontSize: '0.75rem' }}>
            🔧 Variable Operations
          </Typography>
          
          {/* Existing Variable Operations */}
          {((step.variableOperations && step.variableOperations.length > 0) || (step.config?.variableOperations && step.config.variableOperations.length > 0)) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed', mb: 1 }}>
                Current Operations ({(step.variableOperations || step.config?.variableOperations || []).length})
              </Typography>
              {(step.variableOperations || step.config?.variableOperations || []).map((operation, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: 1, 
                  p: 2, 
                  backgroundColor: '#faf5ff', 
                  borderRadius: 1,
                  border: '1px solid #d8b4fe'
                }}>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', flex: 1 }}>
                    {operation.operation === 'set_null' ? `Set ${operation.variableName} = null` : 
                     operation.operation === 'set_value' ? (
                       operation.value === '[]' ? `Create empty array: ${operation.variableName}` :
                       operation.value === '{}' ? `Create empty object: ${operation.variableName}` :
                       operation.value && operation.value.startsWith('[') ? `Set array ${operation.variableName} = ${operation.value}` :
                       operation.value && operation.value.startsWith('{') ? `Set object ${operation.variableName} = ${operation.value}` :
                       `Set ${operation.variableName} = "${operation.value}"`
                     ) :
                     operation.operation === 'add_to_object' ? (
                       operation.objectMerge && operation.objectMerge.length > 0 ? 
                       `Add to object ${operation.variableName}: ${operation.objectMerge.map(item => `${item.key}=${item.valueType === 'variable' ? '{{' + item.value + '}}' : '"' + item.value + '"'}`).join(', ')}` :
                       `Add to object ${operation.variableName}`
                     ) :
                     operation.operation === 'combine_datetime' ? `Combine DateTime: ${operation.variableName} (${operation.dateVariable} + ${operation.timeVariable})` :
                     operation.operation === 'format_date' ? `Format Date: ${operation.variableName} = ${operation.sourceVariable} → ${operation.targetFormat}` :
                     operation.operation === 'calculate_date' ? `Calculate Date: ${operation.variableName} = ${operation.sourceVariable} ${operation.dateOperation} ${operation.amount} ${operation.unit}` :
                     operation.operation === 'ai_extract' ? `🤖 AI Extract → ${operation.variableName}: ${operation.extractionPrompt?.substring(0, 40) || 'No prompt'}...` :
                     operation.operation === 'parse_json' ? `📋 Parse JSON → ${operation.variableName}` :
                     operation.operation === 'convert_to_object' ? `🔄 Convert to Object → ${operation.variableName}${operation.sourceVariable ? ` (from ${operation.sourceVariable})` : ''}` :
                     operation.operation === 'convert_to_array' ? `🔄 Convert to Array → ${operation.variableName}${operation.sourceVariable ? ` (from ${operation.sourceVariable})` : ''}${operation.conversionType && operation.conversionType !== 'values' ? ` [${operation.conversionType}]` : ''}` :
                     operation.operation === 'clear' ? `Clear ${operation.variableName}` :
                     `${operation.operation} ${operation.variableName}`}
                  </Typography>

                  <Button
                    size="small"
                    onClick={() => {
                      console.log('🔧 Variable Operations - Edit Operation clicked, index:', index);
                      const currentOperations = step.variableOperations || step.config?.variableOperations || [];
                      const operationToEdit = currentOperations[index];
                      console.log('🔧 Variable Operations - Operation to edit:', operationToEdit);
                      
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...operationToEdit,
                          _editingIndex: index
                        },
                        _isEditingOperation: true
                      };
                      console.log('🔧 Variable Operations - Updated step for edit:', updatedStep);
                      console.log('🔧 Variable Operations - _isEditingOperation flag:', updatedStep._isEditingOperation);
                      onStepChange(updatedStep);
                    }}
                    sx={{ minWidth: 'auto', p: 0.5, color: '#2563eb', mr: 0.5 }}
                  >
                    ✏️
                  </Button>

                  <Button
                    size="small"
                    onClick={() => {
                      console.log('🔧 Variable Operations - Delete Operation clicked, index:', index);
                      const currentOperations = step.variableOperations || step.config?.variableOperations || [];
                      console.log('🔧 Variable Operations - Current operations:', currentOperations);
                      
                      const updatedOperations = currentOperations.filter((_, i) => i !== index);
                      console.log('🔧 Variable Operations - Updated operations after delete:', updatedOperations);
                      
                      const updatedStep = {
                        ...step,
                        variableOperations: updatedOperations,
                        // Also update config for backwards compatibility during transition
                        config: {
                          ...step.config,
                          variableOperations: updatedOperations
                        }
                      };
                      console.log('🔧 Variable Operations - Updated step after delete:', updatedStep);
                      
                      if (step.onConfigChange) {
                        console.log('🔧 Variable Operations - Calling step.onConfigChange for delete');
                        step.onConfigChange(updatedStep);
                      } else {
                        console.log('🔧 Variable Operations - Calling onStepChange for delete');
                        onStepChange(updatedStep);
                      }
                    }}
                    sx={{ minWidth: 'auto', p: 0.5, color: '#dc2626' }}
                  >
                    ×
                  </Button>
                </Box>
              ))}
            </Box>
          )}

          {/* Add New Variable Operation */}
          <Box sx={{ 
            backgroundColor: '#faf5ff', 
            border: '1px solid #d8b4fe', 
            borderRadius: 1, 
            p: 2 
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed', mb: 2 }}>
              {step._isEditingOperation ? '✏️ Edit Variable Operation' : 'Add Variable Operation'}
            </Typography>
            
            {/* Edit Mode Information */}
            {step._isEditingOperation && (
              <Box sx={{ 
                p: 1.5, 
                backgroundColor: '#dbeafe', 
                border: '1px solid #3b82f6', 
                borderRadius: 1, 
                mb: 2 
              }}>
                <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.7rem', display: 'block' }}>
                  📝 Editing operation #{(step._tempVariableOperation?._editingIndex || 0) + 1}: {step._tempVariableOperation?.operation} on {step._tempVariableOperation?.variableName}
                </Typography>
                {/* DEBUG INFO */}
                <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.6rem', display: 'block', mt: 0.5 }}>
                  🐛 DEBUG: Section=2, _isEditingOperation={String(step._isEditingOperation)}, stepType={step.type}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Variable</InputLabel>
                <Select
                  value={step._tempVariableOperation?.variableName || ''}
                  onChange={(e) => {
                    console.log('🔧 Variable Operations - Variable selected:', e.target.value);
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {
                        ...step._tempVariableOperation,
                        variableName: e.target.value,
                        // Clear custom variable name if not creating new
                        ...(e.target.value !== '__CREATE_NEW__' ? { customVariableName: '' } : {})
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                  label="Variable"
                  sx={{ fontSize: '0.75rem' }}
                >
                  {(() => {
                    console.log('🔧 Variable Operations - Available variables:', availableVariables?.length, availableVariables);
                    const menuItems = (availableVariables || []).map((variable) => (
                      <MenuItem key={variable.name} value={variable.name}>
                        {variable.name}
                      </MenuItem>
                    ));
                    
                    // Add "Create New Variable" option at the end
                    menuItems.push(
                      <MenuItem key="__CREATE_NEW__" value="__CREATE_NEW__" sx={{ color: '#059669', fontWeight: 600 }}>
                        + Create New Variable
                      </MenuItem>
                    );
                    
                    return menuItems;
                  })()}
                </Select>
              </FormControl>
              
              {/* Custom Variable Name Input */}
              {step._tempVariableOperation?.variableName === '__CREATE_NEW__' && (
                <Box sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    label="Variable Name"
                    value={step._tempVariableOperation?.customVariableName || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          customVariableName: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                                         placeholder="Enter variable name (e.g., my_array)"
                     sx={{ width: '100%' }}
                     helperText="Use lowercase with underscores (snake_case)"
                     error={step._tempVariableOperation?.customVariableName && 
                            !/^[a-z][a-z0-9_]*$/.test(step._tempVariableOperation.customVariableName)}
                     {...(step._tempVariableOperation?.customVariableName && 
                         !/^[a-z][a-z0-9_]*$/.test(step._tempVariableOperation.customVariableName) && {
                       helperText: "Variable name must start with a letter and contain only lowercase letters, numbers, and underscores"
                     })}
                  />
                </Box>
              )}
              
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Operation</InputLabel>
                <Select
                  value={step._tempVariableOperation?.operation || ''}
                  onChange={(e) => {
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {
                        ...step._tempVariableOperation,
                        operation: e.target.value,
                        // Clear value when operation changes
                        value: e.target.value === 'set_value' ? step._tempVariableOperation?.value || '' : 
                               e.target.value === 'create_array' ? '[]' :
                               e.target.value === 'append_to_array' ? step._tempVariableOperation?.value || '' : 
                               undefined
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                  label="Operation"
                  sx={{ fontSize: '0.75rem' }}
                >
                  <MenuItem value="set_null">Set to Null</MenuItem>
                  <MenuItem value="set_value">Set to Value</MenuItem>
                  <MenuItem value="create_array">Create Empty Array</MenuItem>
                  <MenuItem value="append_to_array">Append to Array</MenuItem>
                  <MenuItem value="add_to_object">Add to Object</MenuItem>
                  <MenuItem value="clear">Clear Variable</MenuItem>
                  <MenuItem value="combine_datetime">Combine Date + Time</MenuItem>
                  <MenuItem value="format_date">Format Date</MenuItem>
                  <MenuItem value="calculate_date">Calculate Date</MenuItem>
                  <MenuItem value="create_object">Create Object</MenuItem>
                  <MenuItem value="ai_extract">🤖 AI Extract</MenuItem>
                  <MenuItem value="parse_json">📋 Parse JSON String</MenuItem>
                  <MenuItem value="convert_to_object">🔄 Convert to Object</MenuItem>
                  <MenuItem value="convert_to_array">🔄 Convert to Array</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Value field for operations that need values */}
            {(step._tempVariableOperation?.operation === 'set_value' || 
              step._tempVariableOperation?.operation === 'append_to_array') && (
              <Box sx={{ mb: 2 }}>
                {step._tempVariableOperation?.operation === 'append_to_array' ? (
                  <>
                <TextField
                  size="small"
                      label="Value to Append"
                  value={step._tempVariableOperation?.value || ''}
                  onChange={(e) => {
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {
                        ...step._tempVariableOperation,
                        value: e.target.value
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                      placeholder="Enter value to append, e.g., {{selected_schedule_id}}"
                  sx={{ width: '100%' }}
                />
                <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      This will add the value to the end of the array. Use {`{{variable_name}}`} for dynamic values.
                </Typography>
                  </>
                ) : (
                  <>
                    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Value Type</InputLabel>
                      <Select
                        value={step._tempVariableOperation?.valueType || 'string'}
                        onChange={(e) => {
                          const valueType = e.target.value;
                          let defaultValue = '';
                          
                          switch (valueType) {
                            case 'array':
                              defaultValue = '[]';
                              break;
                            case 'object':
                              defaultValue = '{}';
                              break;
                            case 'number':
                              defaultValue = '0';
                              break;
                            case 'boolean':
                              defaultValue = 'false';
                              break;
                            default:
                              defaultValue = '';
                          }
                          
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              valueType: valueType,
                              value: defaultValue
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                                                 label="Value Type"
                         sx={{ fontSize: '0.75rem' }}
                      >
                        <MenuItem value="string">String</MenuItem>
                        <MenuItem value="number">Number</MenuItem>
                        <MenuItem value="boolean">Boolean</MenuItem>
                        <MenuItem value="array">Array</MenuItem>
                        <MenuItem value="object">Object</MenuItem>
                        <MenuItem value="variable_reference">Variable Reference</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {step._tempVariableOperation?.valueType === 'array' ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
                          Array Builder
                        </Typography>
                        <TextField
                          size="small"
                          label="Array Items (JSON format)"
                          value={step._tempVariableOperation?.value || '[]'}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              _tempVariableOperation: {
                                ...step._tempVariableOperation,
                                value: e.target.value
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder='["item1", "item2"] or [] for empty array'
                          multiline
                          rows={2}
                          sx={{ width: '100%', mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const updatedStep = {
                                ...step,
                                _tempVariableOperation: {
                                  ...step._tempVariableOperation,
                                  value: '[]'
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Empty Array
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              const updatedStep = {
                                ...step,
                                _tempVariableOperation: {
                                  ...step._tempVariableOperation,
                                  value: '["item1", "item2", "item3"]'
                                }
                              };
                              onStepChange(updatedStep);
                            }}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            Sample Array
                          </Button>
                        </Box>
                        <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                                     {'Create an array variable. Use JSON format: ["item1", "item2"] or reference variables: ["{{var1}}", "{{var2}}"]'}
                        </Typography>
                      </Box>
                    ) : step._tempVariableOperation?.valueType === 'object' ? (
                      <Box>
                        <TextField
                          size="small"
                          label="Object (JSON format)"
                          value={step._tempVariableOperation?.value || '{}'}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              _tempVariableOperation: {
                                ...step._tempVariableOperation,
                                value: e.target.value
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          placeholder='{"key": "value"} or {} for empty object'
                          multiline
                          rows={2}
                          sx={{ width: '100%' }}
                        />
                        <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                          Create an object variable in JSON format
                        </Typography>
                      </Box>
                    ) : step._tempVariableOperation?.valueType === 'boolean' ? (
                      <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontSize: '0.75rem' }}>Boolean Value</InputLabel>
                        <Select
                          value={step._tempVariableOperation?.value || 'false'}
                          onChange={(e) => {
                            const updatedStep = {
                              ...step,
                              _tempVariableOperation: {
                                ...step._tempVariableOperation,
                                value: e.target.value
                              }
                            };
                            onStepChange(updatedStep);
                          }}
                          label="Boolean Value"
                          sx={{ fontSize: '0.75rem' }}
                        >
                          <MenuItem value="true">True</MenuItem>
                          <MenuItem value="false">False</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        size="small"
                        label={step._tempVariableOperation?.valueType === 'number' ? 'Numeric Value' : 'Value'}
                        type={step._tempVariableOperation?.valueType === 'number' ? 'number' : 'text'}
                        value={step._tempVariableOperation?.value || ''}
                        onChange={(e) => {
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              value: e.target.value
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        placeholder={step._tempVariableOperation?.valueType === 'variable_reference' 
                          ? "{{variable_name}}" 
                          : step._tempVariableOperation?.valueType === 'number' 
                            ? "Enter a number" 
                            : "Enter value or {{variable_name}}"}
                        sx={{ width: '100%' }}
                      />
                    )}
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                      {step._tempVariableOperation?.valueType === 'variable_reference' 
                        ? "Reference another variable using {{variable_name}} syntax"
                        : "You can use variable references like {{user_name}} or enter static values"}
                    </Typography>
                  </>
                )}
              </Box>
            )}
            
            {/* Object Merge Configuration for non-dedicated variable operation steps */}
            {step._tempVariableOperation?.operation === 'add_to_object' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                  Configure Object Properties
                </Typography>
                
                {/* Existing object merge items */}
                {(step._tempVariableOperation?.objectMerge || []).map((mergeItem, index) => (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    mb: 1, 
                    p: 2, 
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 1 
                  }}>
                    <TextField
                      size="small"
                      label="Key"
                      value={mergeItem.key || ''}
                      onChange={(e) => {
                        const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                        updatedObjectMerge[index] = { ...mergeItem, key: e.target.value };
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            objectMerge: updatedObjectMerge
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      placeholder="Property name"
                      sx={{ flex: 1 }}
                    />
                    
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Type</InputLabel>
                      <Select
                        value={mergeItem.valueType || 'static'}
                        onChange={(e) => {
                          const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                          updatedObjectMerge[index] = { ...mergeItem, valueType: e.target.value };
                          const updatedStep = {
                            ...step,
                            _tempVariableOperation: {
                              ...step._tempVariableOperation,
                              objectMerge: updatedObjectMerge
                            }
                          };
                          onStepChange(updatedStep);
                        }}
                        label="Type"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        <MenuItem value="static">Static Value</MenuItem>
                        <MenuItem value="variable">Variable Reference</MenuItem>
                        <MenuItem value="expression">Expression</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      size="small"
                      label={mergeItem.valueType === 'variable' ? 'Variable Name' : 'Value'}
                      value={mergeItem.value || ''}
                      onChange={(e) => {
                        const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                        updatedObjectMerge[index] = { ...mergeItem, value: e.target.value };
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            objectMerge: updatedObjectMerge
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      placeholder={
                        mergeItem.valueType === 'variable' ? 'variable_name' :
                        mergeItem.valueType === 'expression' ? '{{variable}} or complex expression' :
                        'Static value'
                      }
                      sx={{ flex: 2 }}
                    />
                    
                    <Button
                      size="small"
                      onClick={() => {
                        const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || [])];
                        updatedObjectMerge.splice(index, 1);
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            objectMerge: updatedObjectMerge
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      sx={{ minWidth: 'auto', p: 0.5, color: '#dc2626' }}
                    >
                      ×
                    </Button>
                  </Box>
                ))}
                
                {/* Add new property button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newMergeItem = { key: '', value: '', valueType: 'static' };
                    const updatedObjectMerge = [...(step._tempVariableOperation?.objectMerge || []), newMergeItem];
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {
                        ...step._tempVariableOperation,
                        objectMerge: updatedObjectMerge
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                  sx={{ fontSize: '0.75rem', mt: 1 }}
                >
                  + Add Property
                </Button>
                
                {/* Help text */}
                <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.7rem', color: '#0369a1', mb: 1, fontWeight: 600 }}>
                    💡 How to use:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#0369a1', mb: 0.5 }}>
                    • <strong>Static Value:</strong> Fixed text or number (e.g., "John Doe", 123)
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#0369a1', mb: 0.5 }}>
                    • <strong>Variable Reference:</strong> Name of another variable (e.g., patient_id, user_name)
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.65rem', color: '#0369a1' }}>
                    • <strong>Expression:</strong> Complex values with variables (e.g., {`"Hello {{user_name}}"`})
                  </Typography>
                </Box>
              </Box>
            )}
            
            {/* Special message for create_array operation */}
            {step._tempVariableOperation?.operation === 'create_array' && (
              <Box sx={{ mb: 2, p: 1, backgroundColor: '#e0f2fe', borderRadius: 1, border: '1px solid #29b6f6' }}>
                <Typography variant="caption" sx={{ color: '#0d47a1', fontSize: '0.7rem', display: 'block' }}>
                  ℹ️ This will create an empty array variable that you can use with filtering and append operations.
                </Typography>
              </Box>
            )}



            {/* Format Date Configuration */}
            {step._tempVariableOperation?.operation === 'format_date' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                  Format Date Configuration
                </Typography>
                
                <TextField
                  size="small"
                  label="Source Variable"
                  value={step._tempVariableOperation?.sourceVariable || ''}
                  onChange={(e) => {
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {
                        ...step._tempVariableOperation,
                        sourceVariable: e.target.value
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                  placeholder="Variable containing date/datetime to format"
                  fullWidth
                  sx={{ mb: 2 }}
                />
                
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Target Format</InputLabel>
                  <Select
                    value={step._tempVariableOperation?.targetFormat || 'YYYY-MM-DD'}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          targetFormat: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Target Format"
                  >
                    <MenuItem value="YYYY-MM-DD">YYYY-MM-DD (2025-06-28)</MenuItem>
                    <MenuItem value="DD/MM/YYYY">DD/MM/YYYY (28/06/2025)</MenuItem>
                    <MenuItem value="MM/DD/YYYY">MM/DD/YYYY (06/28/2025)</MenuItem>
                    <MenuItem value="readable">Readable (June 28, 2025)</MenuItem>
                    <MenuItem value="iso">ISO (2025-06-28T14:00:00.000Z)</MenuItem>
                    <MenuItem value="timestamp">Timestamp (1751180400000)</MenuItem>
                  </Select>
                </FormControl>
                
                <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem', display: 'block' }}>
                    💡 Convert dates between different formats for display or comparison
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Calculate Date Configuration */}
            {step._tempVariableOperation?.operation === 'calculate_date' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 2 }}>
                  Calculate Date Configuration
                </Typography>
                
                <TextField
                  size="small"
                  label="Source Variable"
                  value={step._tempVariableOperation?.sourceVariable || ''}
                  onChange={(e) => {
                    const updatedStep = {
                      ...step,
                      _tempVariableOperation: {
                        ...step._tempVariableOperation,
                        sourceVariable: e.target.value
                      }
                    };
                    onStepChange(updatedStep);
                  }}
                  placeholder="Variable containing source date"
                  fullWidth
                  sx={{ mb: 2 }}
                />
                
                <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Operation</InputLabel>
                  <Select
                    value={step._tempVariableOperation?.dateOperation || 'add'}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          dateOperation: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    label="Operation"
                  >
                    <MenuItem value="add">Add Time</MenuItem>
                    <MenuItem value="subtract">Subtract Time</MenuItem>
                  </Select>
                </FormControl>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="Amount"
                    type="number"
                    value={step._tempVariableOperation?.amount || ''}
                    onChange={(e) => {
                      const updatedStep = {
                        ...step,
                        _tempVariableOperation: {
                          ...step._tempVariableOperation,
                          amount: e.target.value
                        }
                      };
                      onStepChange(updatedStep);
                    }}
                    placeholder="Number of units"
                    sx={{ flex: 1 }}
                  />
                  
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Unit</InputLabel>
                    <Select
                      value={step._tempVariableOperation?.unit || 'days'}
                      onChange={(e) => {
                        const updatedStep = {
                          ...step,
                          _tempVariableOperation: {
                            ...step._tempVariableOperation,
                            unit: e.target.value
                          }
                        };
                        onStepChange(updatedStep);
                      }}
                      label="Unit"
                    >
                      <MenuItem value="minutes">Minutes</MenuItem>
                      <MenuItem value="hours">Hours</MenuItem>
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                      <MenuItem value="months">Months</MenuItem>
                      <MenuItem value="years">Years</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ p: 1.5, backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#0369a1', fontSize: '0.7rem', display: 'block' }}>
                    💡 Example: Add 7 days to appointment date to create follow-up reminder
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Create Object Configuration */}
            {step._tempVariableOperation?.operation === 'create_object' && (
              <Box sx={{ mb: 2, p: 1, backgroundColor: '#e0f2fe', borderRadius: 1, border: '1px solid #29b6f6' }}>
                <Typography variant="caption" sx={{ color: '#0d47a1', fontSize: '0.7rem', display: 'block' }}>
                  ℹ️ This will create an empty object variable {} that you can populate using "Add to Object" operations.
                </Typography>
              </Box>
            )}


            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                console.log('🔥 Variable Operations - Add Operation clicked');
                const tempOperation = step._tempVariableOperation;
                console.log('🔥 Variable Operations - tempOperation:', tempOperation);
                
                // Determine the actual variable name to use
                const actualVariableName = tempOperation?.variableName === '__CREATE_NEW__' 
                  ? tempOperation?.customVariableName 
                  : tempOperation?.variableName;
                
                // Validate custom variable name if creating new
                const isValidVariableName = tempOperation?.variableName !== '__CREATE_NEW__' || 
                  (tempOperation?.customVariableName && /^[a-z][a-z0-9_]*$/.test(tempOperation.customVariableName));
                
                if (actualVariableName && tempOperation?.operation && isValidVariableName) {
                  let newOperation = {
                    variableName: actualVariableName,
                    operation: tempOperation.operation
                  };
                  
                  // Handle different operation types
                  if (tempOperation.operation === 'set_value' && tempOperation.value) {
                    newOperation.value = tempOperation.value;
                  } else if (tempOperation.operation === 'create_array') {
                    // For create_array, we set the value to an empty array
                    newOperation.operation = 'set_value';
                    newOperation.value = '[]';
                  } else if (tempOperation.operation === 'append_to_array' && tempOperation.value) {
                    // For append_to_array, we create a special append operation
                    newOperation.operation = 'set_value';
                    newOperation.value = `{{${actualVariableName}}},${tempOperation.value}`;
                  } else if (tempOperation.operation === 'add_to_object' && tempOperation.objectMerge) {
                    // Add object merge data for add_to_object operation
                    newOperation.objectMerge = tempOperation.objectMerge;
                  } else if (tempOperation.operation === 'combine_datetime') {
                    // For combine_datetime, include date and time variables and output format
                    console.log('🔥 COMBINE_DATETIME MAPPING DEBUG:', {
                      tempOperationOutputFormat: tempOperation.outputFormat,
                      defaultDateFormat: 'iso',
                      finalDateFormat: tempOperation.outputFormat || 'iso'
                    });
                    newOperation.dateVariable = tempOperation.dateVariable;
                    newOperation.timeVariable = tempOperation.timeVariable;
                    newOperation.dateFormat = tempOperation.outputFormat || 'iso';
                  } else if (tempOperation.operation === 'format_date') {
                    // For format_date, include source variable and target format
                    newOperation.sourceVariable = tempOperation.sourceVariable;
                    newOperation.targetFormat = tempOperation.targetFormat || 'YYYY-MM-DD';
                  } else if (tempOperation.operation === 'calculate_date') {
                    // For calculate_date, include source variable, operation, amount, and unit
                    newOperation.sourceVariable = tempOperation.sourceVariable;
                    newOperation.dateOperation = tempOperation.dateOperation || 'add';
                    newOperation.amount = tempOperation.amount;
                    newOperation.unit = tempOperation.unit || 'days';
                  } else if (tempOperation.operation === 'create_object') {
                    // For create_object, we set the value to an empty object
                    newOperation.operation = 'set_value';
                    newOperation.value = '{}';
                  } else if (tempOperation.operation === 'ai_extract') {
                    // For ai_extract, include extraction configuration
                    newOperation.extractionPrompt = tempOperation.extractionPrompt;
                    newOperation.extractionModel = tempOperation.extractionModel || 'gpt-4o-mini';
                    newOperation.extractionContext = tempOperation.extractionContext || 'conversation_history';
                    newOperation.customContext = tempOperation.customContext;
                    newOperation.required = tempOperation.required !== false; // Default to true
                  }
                  
                  console.log('🔥 Variable Operations - newOperation created:', newOperation);
                  
                  const currentOperations = step.variableOperations || step.config?.variableOperations || [];
                  console.log('🔥 Variable Operations - currentOperations:', currentOperations);
                  
                  const isEditing = step._isEditingOperation;
                  const editingIndex = tempOperation?._editingIndex;
                  let updatedOperations;
                  
                  if (isEditing && typeof editingIndex === 'number') {
                    // Update existing operation
                    updatedOperations = [...currentOperations];
                    updatedOperations[editingIndex] = newOperation;
                    console.log(`🔥 Variable Operations - Updated operation at index ${editingIndex}`);
                  } else {
                    // Add new operation
                    updatedOperations = [...currentOperations, newOperation];
                    console.log('🔥 Variable Operations - Added new operation');
                  }
                  
                  const updatedStep = {
                    ...step,
                    variableOperations: updatedOperations,
                    config: {
                      ...step.config,
                      variableOperations: updatedOperations
                    },
                    _tempVariableOperation: {}, // Clear temp operation
                    _isEditingOperation: false // Exit edit mode
                  };
                  
                  console.log('🔥 Variable Operations - updatedStep created:', updatedStep);
                  
                  if (step.onConfigChange) {
                    console.log('🔥 Variable Operations - Calling step.onConfigChange');
                    step.onConfigChange(updatedStep);
                  } else {
                    console.log('🔥 Variable Operations - Calling onStepChange');
                    onStepChange(updatedStep);
                  }
                } else {
                  console.log('🔥 Variable Operations - Validation failed:', {
                    hasVariableName: !!actualVariableName,
                    hasOperation: !!tempOperation?.operation,
                    isValidVariableName: isValidVariableName,
                    actualVariableName: actualVariableName,
                    tempOperation: tempOperation
                  });
                }
              }}
              disabled={!step._tempVariableOperation?.variableName || !step._tempVariableOperation?.operation || 
                (step._tempVariableOperation?.operation === 'set_value' && !step._tempVariableOperation?.value) ||
                (step._tempVariableOperation?.operation === 'combine_datetime' && (!step._tempVariableOperation?.dateVariable || !step._tempVariableOperation?.timeVariable)) ||
                (step._tempVariableOperation?.operation === 'format_date' && !step._tempVariableOperation?.sourceVariable) ||
                (step._tempVariableOperation?.operation === 'calculate_date' && (!step._tempVariableOperation?.sourceVariable || !step._tempVariableOperation?.amount))}
              sx={{ 
                minWidth: 'auto', 
                px: 2,
                whiteSpace: 'nowrap'
              }}
            >
              {step._isEditingOperation ? 'Update Operation' : 'Add Operation'}
            </Button>
            
            {step._isEditingOperation && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  console.log('🔥 Variable Operations - Cancel Edit clicked');
                  const updatedStep = {
                    ...step,
                    _tempVariableOperation: {},
                    _isEditingOperation: false
                  };
                  onStepChange(updatedStep);
                }}
                sx={{ 
                  ml: 1,
                  color: '#6b7280',
                  fontSize: '0.75rem'
                }}
              >
                Cancel Edit
              </Button>
            )}
            
            {/* DEBUG BUTTONS */}
            <Typography variant="caption" sx={{ color: '#dc2626', fontSize: '0.6rem', display: 'block', mt: 1 }}>
              🐛 DEBUG: Section=2, isEditing={String(step._isEditingOperation)}, showCancel={String(!!step._isEditingOperation)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Configuration Status */}
      <Box sx={{ p: 2, backgroundColor: step.config && Object.keys(step.config).length > 0 ? '#f0fdf4' : '#fef2f2', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ 
          color: step.config && Object.keys(step.config).length > 0 ? '#166534' : '#dc2626',
          fontWeight: 600
        }}>
          {step.config && Object.keys(step.config).length > 0 ? '✓ Step Configured' : '⚠ Step Needs Configuration'}
        </Typography>
        <Typography variant="body2" sx={{ 
          color: step.config && Object.keys(step.config).length > 0 ? '#166534' : '#dc2626',
          fontSize: '0.75rem',
          mt: 0.5
        }}>
          {step.config && Object.keys(step.config).length > 0 
            ? 'This step has been configured and is ready to use.'
            : 'This step needs configuration before the flow can be executed.'
          }
        </Typography>
      </Box>
    </Box>
  );
};

const VisualFlowEditor = ({ 
  flowData, 
  onFlowDataChange, 
  availableVariables = [],
  availableApiEndpoints = [],
  availableIntents = [],
  projectId 
}) => {
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Track internal changes to prevent useEffect regeneration loops
  const internalChangeRef = useRef(false);
  const lastFlowDataRef = useRef(null);

  // Custom nodes change handler to sync positions back to flowData
  const onNodesChange = useCallback((changes) => {
    console.log('🎯 NODES CHANGE: Nodes changed:', changes);
    
    // Apply the visual changes first
    onNodesChangeInternal(changes);
    
    // Check for position changes and update flowData immediately
    const positionChanges = changes.filter(change => 
      change.type === 'position' && change.position && change.dragging === false
    );
    console.log("Changes: ", changes);
    
    console.log("Position Changes: ", positionChanges);

    if (positionChanges.length > 0) {
      console.log('🎯 POSITION SYNC: Position changes detected:', positionChanges);
      
      // Update flowData with new positions immediately
      onFlowDataChange(currentFlowData => {
        if (!currentFlowData || !currentFlowData.steps) {
          console.log('🎯 POSITION SYNC: No flowData or steps to update');
          return currentFlowData;
        }
        
        const updatedSteps = currentFlowData.steps.map(step => {
          const positionChange = positionChanges.find(change => change.id === step.id);
          console.log("Step ID: ", step.id);
          console.log("Position Changed? ", positionChange);
          if (positionChange) {
            console.log(`🎯 POSITION SYNC: Updating position for step ${step.id}:`, {
              old: step.position,
              new: positionChange.position
            });
            return {
              ...step,
              position: positionChange.position
            };
          }
          return step;
        });
        
        const newFlowData = {
          ...currentFlowData,
          steps: updatedSteps
        };
        
        console.log('🎯 POSITION SYNC: Updated flowData with new positions:', newFlowData);
        
        // Mark as internal change to prevent useEffect regeneration
        internalChangeRef.current = true;
        return newFlowData;
      });
    }
  }, [onNodesChangeInternal, onFlowDataChange]);

  // Add dedicated handler for node drag stop events
  const onNodeDragStop = useCallback((event, node) => {
    console.log('🎯 NODE DRAG STOP: Node dragged and dropped:', node);
    console.log('🎯 NODE DRAG STOP: New position:', node.position);
    
    // Update flowData with new position immediately
    onFlowDataChange(currentFlowData => {
      if (!currentFlowData || !currentFlowData.steps) {
        console.log('🎯 NODE DRAG STOP: No flowData or steps to update');
        return currentFlowData;
      }
      
      const updatedSteps = currentFlowData.steps.map(step => {
        if (step.id === node.id) {
          console.log(`🎯 NODE DRAG STOP: Updating position for step ${step.id}:`, {
            old: step.position,
            new: node.position
          });
          return {
            ...step,
            position: node.position
          };
        }
        return step;
      });
      
      const newFlowData = {
        ...currentFlowData,
        steps: updatedSteps
      };
      
      console.log('🎯 NODE DRAG STOP: Updated flowData with new position:', newFlowData);
      
      // Mark as internal change to prevent useEffect regeneration
      internalChangeRef.current = true;
      return newFlowData;
    });
  }, [onFlowDataChange]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showFlowInfo, setShowFlowInfo] = useState(true);
  const [editingStep, setEditingStep] = useState(null);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [selectedNodeForConfig, setSelectedNodeForConfig] = useState(null);
  
  // React Flow instance for viewport access
  const reactFlowInstance = useReactFlow();

  console.log('VisualFlowEditor rendered with flowData:', flowData);
  console.log('Current nodes state:', nodes);
  console.log('Current edges state:', edges);

  // Function to gather all variables from previous steps in the flow
  const gatherAvailableVariables = useCallback((currentStepIndex = -1) => {
    const variables = [];
    
    // Add global variables that are always available
    const globalVariables = [
      { name: 'user_name', type: 'string', source: 'System', description: 'Current user name' },
      { name: 'user_email', type: 'string', source: 'System', description: 'Current user email' },
      { name: 'user_id', type: 'string', source: 'System', description: 'Current user ID' },
      { name: 'session_id', type: 'string', source: 'System', description: 'Current session ID' },
      { name: 'current_timestamp', type: 'string', source: 'System', description: 'Current timestamp' },
      { name: 'current_date', type: 'string', source: 'System', description: 'Current date' },
      { name: 'last_user_message', type: 'string', source: 'System', description: 'Last user message' },
      { name: 'user_location', type: 'string', source: 'System', description: 'Extract location information' },
      { name: 'user_latitude', type: 'string', source: 'System', description: 'User latitude coordinate' },
      { name: 'user_longitude', type: 'string', source: 'System', description: 'User longitude coordinate' },
      { name: 'medical_specialty', type: 'string', source: 'System', description: 'Medical Speciality' },
      // Standard error variables that are always available after API errors
      { name: 'api_error', type: 'boolean', source: 'System', description: 'Boolean (true if any API failed)' },
      { name: 'error_status_code', type: 'number', source: 'System', description: 'HTTP status code from failed API calls' },
      { name: 'error_message', type: 'string', source: 'System', description: 'General error message from failed API calls' }
    ];
    
    variables.push(...globalVariables);
    
    // If no specific step index provided, gather from all steps
    const stepsToCheck = currentStepIndex >= 0 
      ? (flowData.steps || []).slice(0, currentStepIndex)
      : (flowData.steps || []);
    
    // Add variables from previous steps in the flow
    stepsToCheck.forEach((step, stepIndex) => {
      if (!step) return;
      
      const stepNumber = stepIndex + 1;
      const stepName = step.name || `Step ${stepNumber}`;
      
      // Helper function to check if step ID is a valid MongoDB ObjectId (24 character hex string)
      const isValidObjectId = (id) => {
        return id && typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id);
      };
      
      // Only include sourceStep if it's a valid ObjectId, otherwise exclude it
      const createVariableEntry = (baseEntry) => {
        const entry = { ...baseEntry, sourceStepIndex: stepIndex };
        if (isValidObjectId(step.id)) {
          entry.sourceStep = step.id;
        }
        return entry;
      };
      
      // Variables from API responses
      if (step.variableMapping?.saveFromResponse) {
        if (Array.isArray(step.variableMapping.saveFromResponse)) {
          // New format - array of variable mappings
          step.variableMapping.saveFromResponse.forEach(mapping => {
            if (mapping.variableName) {
              variables.push(createVariableEntry({
                name: mapping.variableName,
                type: mapping.dataType || 'string',
                source: `${stepName} (API Response)`,
                description: mapping.description || `From API response: ${mapping.sourcePath}`
              }));
            }
          });
        } else if (typeof step.variableMapping.saveFromResponse === 'object') {
          // Legacy format - object with variable mappings
          Object.entries(step.variableMapping.saveFromResponse).forEach(([varName, path]) => {
            variables.push(createVariableEntry({
              name: varName,
              type: 'string',
              source: `${stepName} (API Response)`,
              description: `Extracted from response path: ${path}`
            }));
          });
        }
      }

      // 🔧 NEW: Variables from error response mapping (API Call steps)
      if (step.type === 'api_call') {
        // Check both possible locations for error response mapping
        const errorMappings = step.apiCall?.errorResponseMapping || step.config?.apiCall?.errorResponseMapping;
        
        if (errorMappings && Array.isArray(errorMappings)) {
          errorMappings.forEach(mapping => {
            if (mapping.key) {
              variables.push(createVariableEntry({
                name: mapping.key,
                type: mapping.type || 'string',
                source: `${stepName} (API Error)`,
                description: mapping.description || `From API error response: ${mapping.path}`
              }));
            }
          });
        }
      }
      
      // Full response variable
      if (step.variableMapping?.saveFullResponse) {
        variables.push(createVariableEntry({
          name: step.variableMapping.saveFullResponse,
          type: 'object',
          source: `${stepName} (API Response)`,
          description: `Complete API response from ${stepName}`
        }));
      }
      
      // Variables from response fields (new enhanced format)
      if (step.variableMapping?.responseFields && Array.isArray(step.variableMapping.responseFields)) {
        step.variableMapping.responseFields.forEach(field => {
          if (field.variableName) {
            variables.push(createVariableEntry({
              name: field.variableName,
              type: 'string',
              source: `${stepName} (API Response)`,
              description: `Extracted from response field: ${field.field}`
            }));
          }
        });
      }
      
      // Variables from user input
      if (step.variableMapping?.saveFromInput) {
        if (Array.isArray(step.variableMapping.saveFromInput)) {
          step.variableMapping.saveFromInput.forEach(mapping => {
            if (mapping.variableName) {
              variables.push(createVariableEntry({
                name: mapping.variableName,
                type: 'string',
                source: `${stepName} (User Input)`,
                description: mapping.description || `From user input`
              }));
            }
          });
        }
      }
      
      // Variables from collect_input steps
      if (step.type === 'collect_input' && step.config?.collectInput?.slotName) {
        variables.push(createVariableEntry({
          name: step.config.collectInput.slotName,
          type: 'string',
          source: `${stepName} (Collected Input)`,
          description: `User input from: ${step.config.collectInput.prompt || 'input collection'}`
        }));
      }
      
      // 🔧 NEW: Variables from Variable Operations
      if (step.variableOperations && Array.isArray(step.variableOperations)) {
        step.variableOperations.forEach(operation => {
          if (operation.variableName && operation.operation !== 'clear') {
            let variableType = 'string';
            let description = `Created by variable operation: ${operation.operation}`;
            
            // Determine variable type based on the operation value
            if (operation.operation === 'set_value' && operation.value) {
              if (operation.value === '[]') {
                variableType = 'array';
                description = 'Empty array created by Variable Operations';
              } else if (operation.value === '{}') {
                variableType = 'object';
                description = 'Empty object created by Variable Operations';
              } else if (operation.value.startsWith('[') && operation.value.endsWith(']')) {
                variableType = 'array';
                description = 'Array variable created by Variable Operations';
              } else if (operation.value.startsWith('{') && operation.value.endsWith('}')) {
                variableType = 'object';
                description = 'Object variable created by Variable Operations';
              } else if (operation.value === 'true' || operation.value === 'false') {
                variableType = 'boolean';
                description = 'Boolean variable created by Variable Operations';
              } else if (!isNaN(operation.value)) {
                variableType = 'number';
                description = 'Number variable created by Variable Operations';
              }
            } else if (operation.operation === 'set_null') {
              variableType = 'null';
              description = 'Variable set to null by Variable Operations';
            } else if (operation.operation === 'ai_extract') {
              variableType = 'string';
              description = `AI extracted: ${operation.extractionPrompt?.substring(0, 50) || 'No prompt'}...`;
            }
            
            variables.push(createVariableEntry({
              name: operation.variableName,
              type: variableType,
              source: `${stepName} (Variable Operation)`,
              description: description
            }));
          }
        });
      }
      
      // Also check config.variableOperations for backwards compatibility
      if (step.config?.variableOperations && Array.isArray(step.config.variableOperations)) {
        step.config.variableOperations.forEach(operation => {
          if (operation.variableName && operation.operation !== 'clear') {
            // Only add if not already added from top-level variableOperations
            const alreadyAdded = variables.some(v => v.name === operation.variableName && v.source.includes('Variable Operation'));
            if (!alreadyAdded) {
              let variableType = 'string';
              let description = `Created by variable operation: ${operation.operation}`;
              
              // Determine variable type based on the operation value
              if (operation.operation === 'set_value' && operation.value) {
                if (operation.value === '[]') {
                  variableType = 'array';
                  description = 'Empty array created by Variable Operations';
                } else if (operation.value === '{}') {
                  variableType = 'object';
                  description = 'Empty object created by Variable Operations';
                } else if (operation.value.startsWith('[') && operation.value.endsWith(']')) {
                  variableType = 'array';
                  description = 'Array variable created by Variable Operations';
                } else if (operation.value.startsWith('{') && operation.value.endsWith('}')) {
                  variableType = 'object';
                  description = 'Object variable created by Variable Operations';
                } else if (operation.value === 'true' || operation.value === 'false') {
                  variableType = 'boolean';
                  description = 'Boolean variable created by Variable Operations';
                } else if (!isNaN(operation.value)) {
                  variableType = 'number';
                  description = 'Number variable created by Variable Operations';
                }
              } else if (operation.operation === 'set_null') {
                variableType = 'null';
                description = 'Variable set to null by Variable Operations';
              }
              
              variables.push(createVariableEntry({
                name: operation.variableName,
                type: variableType,
                source: `${stepName} (Variable Operation)`,
                description: description
              }));
            }
          }
        });
      }
    });
    
    // Remove duplicates based on variable name
    const uniqueVariables = variables.filter((variable, index, self) => 
      index === self.findIndex(v => v.name === variable.name)
    );
    
    console.log(`Gathered ${uniqueVariables.length} unique variables:`, uniqueVariables.map(v => `${v.name} (${v.source})`));
    
    return uniqueVariables;
  }, [flowData.steps]);

  // Create nodes and edges with proper branching logic
  const createNodesAndEdges = useCallback((flow) => {
    console.log('Creating visual flow from:', flow);
    console.log('Flow steps:', flow?.steps);
    
    const newNodes = [];
    const newEdges = [];
    
    // Calculate layout positions
    const nodeWidth = 200;
    const nodeHeight = 100;
    const horizontalSpacing = 300;
    const verticalSpacing = 150;
    
    let currentY = 100;
    let currentX = 250;

    // Find start and end nodes from actual steps
    let startNodeId = 'start'; // fallback
    let endNodeId = 'end'; // fallback
    let startStep = null;
    let endStep = null;
    const regularSteps = [];

    if (flow.steps && flow.steps.length > 0) {
      flow.steps.forEach(step => {
        if (step.type === 'start') {
          startStep = step;
          startNodeId = step.id || step._id;
        } else if (step.type === 'end') {
          endStep = step;
          endNodeId = step.id || step._id;
        } else {
          regularSteps.push(step);
        }
      });
    }

    // Add start node - use real step if available
    if (startStep) {
      newNodes.push({
        id: startStep.id || startStep._id,
        type: 'startNode',
        position: startStep.position || { x: currentX, y: currentY },
        data: { 
          label: 'Start',
          type: 'start',
          step: startStep,
          onSelect: handleSelectNode
        },
        draggable: true
      });
    } else {
      // Fallback: create virtual start node
      newNodes.push({
        id: 'start',
        type: 'startNode',
        position: { x: currentX, y: currentY },
        data: { 
          label: 'Start',
          type: 'start',
          onSelect: handleSelectNode
        },
        draggable: true
      });
    }

    let previousNodeId = startNodeId;
    currentY += verticalSpacing;
    
    console.log('Created start node. Current nodes:', newNodes.length);

    // Process regular steps and create connections
    if (regularSteps && regularSteps.length > 0) {
      for (let i = 0; i < regularSteps.length; i++) {
        const step = regularSteps[i];
        const nodeId = step.id || step._id || `step_${i}`;
        
        // Add step node
        newNodes.push({
          id: nodeId,
          type: 'stepNode',
          position: step.position || { x: currentX, y: currentY },
          data: {
            label: step.name || `${step.type.replace('_', ' ')} ${i + 1}`,
            type: step.type,
            step: step,
            onSelect: handleSelectNode,
            onEdit: handleEditStep,
            onDelete: handleDeleteStep,
            onDuplicate: handleDuplicateStep
          }
        });

        currentY += verticalSpacing;
      }
    }

    // Add end node - use real step if available
    if (endStep) {
      newNodes.push({
        id: endStep.id || endStep._id,
        type: 'endNode',
        position: endStep.position || { x: currentX, y: currentY },
        data: { 
          label: 'End',
          type: 'end',
          step: endStep,
          onSelect: handleSelectNode
        },
        draggable: true
      });
    } else {
      // Fallback: create virtual end node
      newNodes.push({
        id: 'end',
        type: 'endNode',
        position: { x: currentX, y: currentY },
        data: { 
          label: 'End',
          type: 'end',
          onSelect: handleSelectNode
        },
        draggable: true
      });
    }

    console.log('Created all nodes, now creating edges based on actual connections...');

    // Create edges based on actual nextStepId connections (not sequential order)
    const allSteps = [...(flow.steps || [])];
    
    allSteps.forEach(step => {
      const stepId = step.id || step._id;
      console.log(`Processing connections for step ${step.name} (${stepId}):`, {
        nextStepId: step.nextStepId,
        type: step.type,
        hasNextStepId: !!step.nextStepId,
        nextStepIdType: typeof step.nextStepId,
        isNotBranch: step.type !== 'branch',
        shouldCreateRegularEdge: !!(step.nextStepId && step.type !== 'branch'),
        branchConfig: step.type === 'branch' ? step.config?.branch || step.branch : 'not a branch',
        fullStep: step
      });
      
      // Handle regular nextStepId connections (for non-branch steps)
      if (step.nextStepId && step.type !== 'branch') {
        console.log(`Looking for target step with ID: ${step.nextStepId}`);
        console.log(`Available step IDs:`, allSteps.map(s => ({ name: s.name, id: s.id || s._id, _id: s._id })));
        
        const targetStep = allSteps.find(s => (s.id || s._id) === step.nextStepId);
        if (targetStep) {
          const targetStepId = targetStep.id || targetStep._id;
          console.log(`✅ Creating regular edge: ${step.name} -> ${targetStep.name}`);
          newEdges.push({
            id: `edge-${stepId}-${targetStepId}`,
            source: stepId,
            target: targetStepId,
            type: 'smoothstep',
            animated: false,
            markerEnd: {
              type: MarkerType.ArrowClosed,
            }
          });
        } else {
          console.log(`❌ Target step not found for nextStepId: ${step.nextStepId}`);
          console.log(`Available steps:`, allSteps.map(s => ({ name: s.name, id: s.id, _id: s._id })));
        }
      } else {
        console.log(`⏭️ Skipping regular edge for ${step.name}: nextStepId=${step.nextStepId}, type=${step.type}`);
      }

      // Handle branch logic - check both frontend config.branch and backend branch structures
      if (step.type === 'branch') {
        // Handle frontend structure (step.config.branch) and backend structure (step.branch)
        const branchConfig = step.config?.branch || step.branch;
        
        if (branchConfig) {
          const conditionName = branchConfig.conditionName || 'Branch Condition';
          
          console.log(`Creating edges for branch step ${stepId}:`, {
            conditionName,
            trueNextStepId: branchConfig.trueNextStepId,
            falseNextStepId: branchConfig.falseNextStepId,
            fullBranchConfig: branchConfig,
            stepStructure: step.config ? 'frontend' : 'backend'
          });
          
          // Create TRUE path edge
          if (branchConfig.trueNextStepId) {
            const trueTargetStep = allSteps.find(s => (s.id || s._id) === branchConfig.trueNextStepId);
            if (trueTargetStep) {
              const trueTargetStepId = trueTargetStep.id || trueTargetStep._id;
              console.log(`Creating branch TRUE edge: ${step.name} -> ${trueTargetStep.name}`);
              newEdges.push({
                id: `edge-branch-true-${stepId}-${trueTargetStepId}`,
                source: stepId,
                sourceHandle: 'branch-true',
                target: trueTargetStepId,
                type: 'smoothstep',
                animated: true,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                },
                label: `✓ ${conditionName}`,
                labelStyle: { 
                  fontSize: 12, 
                  fontWeight: 600,
                  fill: '#10b981',
                  backgroundColor: 'white',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #10b981'
                },
                style: { stroke: '#10b981', strokeWidth: 2 }
              });
            } else {
              console.log(`Target step not found for trueNextStepId: ${branchConfig.trueNextStepId}`);
            }
          }

          // Create FALSE path edge
          if (branchConfig.falseNextStepId) {
            const falseTargetStep = allSteps.find(s => (s.id || s._id) === branchConfig.falseNextStepId);
            if (falseTargetStep) {
              const falseTargetStepId = falseTargetStep.id || falseTargetStep._id;
              console.log(`Creating branch FALSE edge: ${step.name} -> ${falseTargetStep.name}`);
              newEdges.push({
                id: `edge-branch-false-${stepId}-${falseTargetStepId}`,
                source: stepId,
                sourceHandle: 'branch-false',
                target: falseTargetStepId,
                type: 'smoothstep',
                animated: false,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                },
                label: `✗ ${conditionName}`,
                labelStyle: { 
                  fontSize: 12, 
                  fontWeight: 600,
                  fill: '#ef4444',
                  backgroundColor: 'white',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid #ef4444'
                },
                style: { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '5,5' }
              });
            } else {
              console.log(`Target step not found for falseNextStepId: ${branchConfig.falseNextStepId}`);
            }
          }
        } else {
          console.log(`Branch step ${step.id} has no branch configuration`);
        }
      }
    });

    console.log('Final visual nodes:', newNodes);
    console.log('Final visual edges:', newEdges);
    console.log('Returning from createNodesAndEdges with', newNodes.length, 'nodes and', newEdges.length, 'edges');

    return { nodes: newNodes, edges: newEdges };
  }, []);

  // Handle branch configuration changes and automatically update edges
  const handleBranchConfigChange = useCallback((updatedStep) => {
    console.log('Branch configuration changed:', updatedStep);
    console.log('Branch config conditions:', updatedStep.config?.branch?.conditions);
    
    if (updatedStep.type === 'branch') {
      // Get the current flow data directly from the callback to avoid stale closures
      onFlowDataChange(currentFlowData => {
        console.log('Current flowData in callback:', currentFlowData);
        
        if (!currentFlowData || !currentFlowData.steps) {
          console.error('No valid flow data available');
          return currentFlowData;
        }
        
        // Update only the specific step in flowData, preserving all other data
        const updatedSteps = currentFlowData.steps.map(step => {
          if (step.id === updatedStep.id) {
            // Only update specific fields, don't spread the entire updatedStep
            const frontendStep = {
              ...step,
              config: updatedStep.config || step.config,
              name: updatedStep.name || step.name,
              type: step.type, // Always preserve the original type
              active: updatedStep.active !== undefined ? updatedStep.active : step.active,
              position: updatedStep.position || step.position,
              variableMapping: updatedStep.variableMapping || step.variableMapping,
              variableOperations: updatedStep.variableOperations || step.variableOperations, // Preserve variable operations
              order: step.order, // Preserve order
              id: step.id // Ensure ID is preserved
            };
            
            console.log('Updated step in flow data (frontend format):', frontendStep);
            console.log('Updated step branch conditions:', frontendStep.config?.branch?.conditions);
            
            // Convert to backend format before saving
            const backendStep = convertFrontendToBackendFormat(frontendStep);
            console.log('Converted to backend format:', backendStep);
            
            return backendStep;
          }
          return step;
        });
        
        const newFlowData = {
          ...currentFlowData,
          steps: updatedSteps
        };
        
        console.log('Updated flow data:', newFlowData);
        console.log('Updated steps count:', updatedSteps.length);
        
        // Update the selected node configuration more safely
        setTimeout(() => {
          setSelectedNodeForConfig(prev => {
            if (prev && prev.nodeId === updatedStep.id) {
              const updatedStepFromData = updatedSteps.find(s => s.id === updatedStep.id);
              return {
                nodeId: updatedStep.id,
                step: {
                  ...updatedStepFromData,
                  onConfigChange: handleBranchConfigChange
                }
              };
            }
            return prev;
          });
        }, 0);
        
        return newFlowData;
      });
    }
  }, [onFlowDataChange]);

  // Handle selecting a node for configuration
  const handleSelectNode = useCallback((nodeId, step) => {
    console.log('Selecting node for configuration:', nodeId, step);
    
    // 🔧 DEBUG: Check variableOperations in the selected step
    if (step && step.name === "Find Hospital in different area?") {
      console.log('🔧 [STEP SELECTION DEBUG] Target step selected:', {
        id: step.id,
        name: step.name,
        variableOperations: step.variableOperations,
        hasVariableOperations: !!step.variableOperations,
        variableOperationsLength: step.variableOperations?.length || 0,
        configVariableOperations: step.config?.variableOperations,
        fullStepObject: step
      });
    }
    
    if (step) {
      setSelectedNodeForConfig({
        nodeId: nodeId,
        step: {
          ...step,
          onConfigChange: step.type === 'branch' ? handleBranchConfigChange : undefined
        }
      });
    }
  }, [handleBranchConfigChange]);

  // Handle editing a step
  const handleEditStep = useCallback((stepId, step) => {
    console.log('Editing step:', stepId, step);
    setEditingStep(step);
    setShowStepEditor(true);
  }, []);

  // Handle deleting a step
  const handleDeleteStep = useCallback((stepId) => {
    console.log('🗑️ DELETE: Attempting to delete step with ID:', stepId);
    
    // Use functional update to get the latest flowData and avoid stale closure issues
    onFlowDataChange(currentFlowData => {
      console.log('🗑️ DELETE: Current flowData.steps in functional update:', currentFlowData.steps);
      
      if (!currentFlowData.steps || currentFlowData.steps.length === 0) {
        console.log('🗑️ DELETE: No steps in current flowData');
        return currentFlowData; // Return unchanged if no steps
      }
      
      // Debug: show all step IDs
      console.log('🗑️ DELETE: All step IDs before deletion:', 
        currentFlowData.steps.map(step => ({ 
          name: step.name, 
          id: step.id, 
          _id: step._id,
          stepIdToMatch: step.id || step._id 
        }))
      );
      
      // Filter should work with either step.id or step._id (whichever exists)
      const updatedSteps = currentFlowData.steps.filter(step => {
        const currentStepId = step.id || step._id;
        const shouldKeep = currentStepId !== stepId;
        console.log(`🗑️ DELETE: Step "${step.name}" (${currentStepId}) ${shouldKeep ? 'KEPT' : 'DELETED'}`);
        return shouldKeep;
      });
      
      console.log('🗑️ DELETE: Steps after filtering:', updatedSteps.length, 'remaining');
      console.log('🗑️ DELETE: Remaining step names:', updatedSteps.map(step => step.name));
      
      const updatedFlowData = {
        ...currentFlowData,
        steps: updatedSteps
      };
      
      console.log('🗑️ DELETE: Returning updated flowData from functional update');
      return updatedFlowData;
    });
  }, [onFlowDataChange]);

  // Handle duplicating a step
  const handleDuplicateStep = useCallback((stepId, stepToDuplicate) => {
    console.log('📋 DUPLICATE: Attempting to duplicate step with ID:', stepId, stepToDuplicate);
    
    if (!stepToDuplicate) {
      console.error('📋 DUPLICATE: No step data provided for duplication');
      return;
    }
    
    // Use functional update to get the latest flowData
    onFlowDataChange(currentFlowData => {
      console.log('📋 DUPLICATE: Current flowData.steps in functional update:', currentFlowData.steps);
      
      if (!currentFlowData.steps) {
        console.log('📋 DUPLICATE: No steps in current flowData');
        return currentFlowData;
      }
      
      // Generate a unique ID for the duplicated step
      const duplicateId = `step_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Create a deep copy of the step to duplicate
      const duplicatedStep = {
        ...stepToDuplicate,
        id: duplicateId,
        name: `${stepToDuplicate.name} (Copy)`,
        order: (currentFlowData.steps.length || 0) + 1,
        // Reset connections for the duplicated step
        nextStepId: null,
        // For branch steps, reset the branch connections
        config: stepToDuplicate.config ? {
          ...stepToDuplicate.config,
          branch: stepToDuplicate.config.branch ? {
            ...stepToDuplicate.config.branch,
            trueNextStepId: null,
            falseNextStepId: null
          } : stepToDuplicate.config.branch
        } : stepToDuplicate.config,
        // Position the duplicated step slightly offset from the original
        position: {
          x: (stepToDuplicate.position?.x || 250) + 300, // Offset to the right
          y: (stepToDuplicate.position?.y || 200) + 50   // Offset down slightly
        },
        // Mark as newly created for visual highlighting
        isNewlyCreated: true
      };
      
      console.log('📋 DUPLICATE: Created duplicated step:', duplicatedStep);
      
      // Add the duplicated step to the flow
      const updatedSteps = [...currentFlowData.steps, duplicatedStep];
      
      const updatedFlowData = {
        ...currentFlowData,
        steps: updatedSteps
      };
      
      console.log('📋 DUPLICATE: Returning updated flowData with duplicated step');
      console.log('📋 DUPLICATE: New step count:', updatedSteps.length);
      
      // Clear the newly created flag after a delay
      setTimeout(() => {
        onFlowDataChange(latestFlowData => {
          const clearedSteps = latestFlowData.steps.map(step => 
            step.id === duplicateId ? { ...step, isNewlyCreated: false } : step
          );
          return { ...latestFlowData, steps: clearedSteps };
        });
      }, 3000); // Clear the highlight after 3 seconds
      
      return updatedFlowData;
    });
  }, [onFlowDataChange]);

  // Initialize nodes when flowData changes
  useEffect(() => {
    console.log('VisualFlowEditor useEffect triggered with flowData:', flowData);
    
    // Check if this is an internal change - if so, skip regeneration
    if (internalChangeRef.current) {
      console.log('🚫 INTERNAL CHANGE DETECTED: Skipping node/edge regeneration');
      internalChangeRef.current = false;
      lastFlowDataRef.current = flowData;
      return;
    }
    
    // Always regenerate when flowData changes to ensure consistency
    // This ensures that branch configuration changes, step name changes, and other
    // modifications are immediately reflected in the visual flow
    console.log('🔄 FLOWDATA CHANGE: FlowData updated, regenerating nodes and edges for consistency');
    
    console.log('✅ FLOWDATA CHANGE: Significant changes detected, regenerating nodes and edges');
    
    // Always create at least start and end nodes, even with empty data
    let safeFlowData = flowData || { name: '', description: '', steps: [] };
    
    // Update the ref
    lastFlowDataRef.current = flowData;
    
    // Convert backend format to frontend format for Visual Flow Editor
    if (safeFlowData.steps && safeFlowData.steps.length > 0) {
      console.log('Converting backend format to frontend format for Visual Flow Editor');
      
      const convertedSteps = safeFlowData.steps.map(step => {
        // Convert backend format to frontend format
        const config = step.config || {};
        
        // Convert direct field access to config structure
        if (step.branch && !config.branch) {
          config.branch = step.branch;
        }
        if (step.apiCall && !config.apiCall) {
          config.apiCall = {
            ...step.apiCall,
            // Extract just the endpoint ID if it's an object, otherwise keep as is
            apiEndpoint: step.apiCall.apiEndpoint && typeof step.apiCall.apiEndpoint === 'object' ? 
              step.apiCall.apiEndpoint._id || step.apiCall.apiEndpoint.id :
              step.apiCall.apiEndpoint,
            // Ensure parameters object exists and is properly formatted
            parameters: step.apiCall.parameters || {}
          };
          console.log('🔧 API ENDPOINT CONVERSION DEBUG:');
          console.log('  - Original step.apiCall.apiEndpoint:', step.apiCall.apiEndpoint);
          console.log('  - Type of endpoint:', typeof step.apiCall.apiEndpoint);
          console.log('  - Converted config.apiCall.apiEndpoint:', config.apiCall.apiEndpoint);
          console.log('  - Full converted config.apiCall:', config.apiCall);
        }
        if (step.collectInput && !config.collectInput) {
          config.collectInput = step.collectInput;
        }
        if (step.message && !config.message) {
          config.message = step.message;
        }
        // 🔧 CRITICAL FIX: Convert Knowledge Lookup from top level to config structure for frontend
        if (step.knowledgeLookup && !config.knowledgeLookup) {
          config.knowledgeLookup = step.knowledgeLookup;
          console.log('🔧 KNOWLEDGE LOOKUP LOAD: Converted knowledgeLookup from top level to config for frontend:', config.knowledgeLookup);
        }
        
        // Convert variableMapping format from backend to frontend
        let convertedVariableMapping = {
          saveFromInput: step.variableMapping?.saveFromInput || [],
          availableVariables: step.variableMapping?.availableVariables || [],
          responseFields: step.variableMapping?.responseFields || [],
          // CRITICAL FIX: Keep saveFromResponse data for display in the UI
          saveFromResponse: step.variableMapping?.saveFromResponse || []
        };
        
        // Convert saveFromResponse to responseFields format for frontend (only if responseFields is empty)
        if (step.variableMapping?.saveFromResponse && Array.isArray(step.variableMapping.saveFromResponse) && 
            (!step.variableMapping?.responseFields || step.variableMapping.responseFields.length === 0)) {
          convertedVariableMapping.responseFields = step.variableMapping.saveFromResponse
            .filter(mapping => mapping.sourcePath !== '$root') // Exclude saveFullResponse entries
            .map(mapping => ({
              field: mapping.sourcePath,
              variableName: mapping.variableName
            }));
        }
        
        // Handle saveFullResponse conversion if it exists
        if (step.variableMapping?.saveFullResponse) {
          convertedVariableMapping.saveFullResponse = step.variableMapping.saveFullResponse;
        }
        
        // Debug conversion if needed (removed excessive logging)
        
        // 🔧 TEMPORARY FIX: Add known variable operations for specific steps
        // This is a workaround until backend serialization issue is fixed
        let tempVariableOperations = step.variableOperations;
        console.log('🔥 TEMP VARIABLE OPERATIONS ASSIGNMENT:');
        console.log('  step.variableOperations before assignment:', step.variableOperations);
        console.log('  tempVariableOperations after assignment:', tempVariableOperations);
        console.log('  Assignment successful:', tempVariableOperations === step.variableOperations);
        
        // Check for potential issues in loading
        // Step data processed
        
        // Temporary fix removed - now we can see the real database state
        
        // Variable operations processed
        
        const finalStep = {
          id: step.id || step._id || `step_${Date.now()}_${Math.random()}`,
          type: step.type || 'message',
          name: step.name || 'Unnamed Step',
          order: step.order || 0,
          active: step.active !== false,
          config: config,
          variableMapping: convertedVariableMapping,
          position: step.position || { x: 250, y: 200 },
          nextStepId: step.nextStepId, // Preserve nextStepId field!
          agentEdited: Boolean(step.agentEdited),
          // 🔧 CRITICAL FIX: Use temporary variable operations (includes backend + temp fix)
          variableOperations: tempVariableOperations || []
        };
        
        // Step conversion completed
        
        if (step.name === "Find Hospital in different area?") {
          console.log('🔥 TARGET STEP FINAL RESULT:', {
            originalStepVariableOperations: step.variableOperations,
            tempVariableOperations: tempVariableOperations,
            finalStepVariableOperations: finalStep.variableOperations,
            originalType: typeof step.variableOperations,
            finalType: typeof finalStep.variableOperations,
            originalLength: step.variableOperations?.length,
            finalLength: finalStep.variableOperations?.length
          });
        }
        
        return finalStep;
      });
      
      safeFlowData = {
        ...safeFlowData,
        steps: convertedSteps
      };
      
      console.log('Converted flow data:', safeFlowData);
    }
    
    const { nodes: newNodes, edges: newEdges } = createNodesAndEdges(safeFlowData);
    
    console.log('Setting nodes:', newNodes);
    console.log('Setting edges:', newEdges);
    
    setNodes(newNodes);
    setEdges(newEdges);
  }, [flowData, createNodesAndEdges, setNodes, setEdges]);

  // Smart positioning function to find the best position for new nodes
  const findOptimalPosition = useCallback(() => {
    try {
      // Get current viewport information
      const viewport = reactFlowInstance.getViewport();
      const zoom = viewport.zoom;
      
      // Try to get actual container dimensions
      let containerWidth = 800;
      let containerHeight = 600;
      
      try {
        const flowContainer = document.querySelector('.react-flow');
        if (flowContainer) {
          const rect = flowContainer.getBoundingClientRect();
          containerWidth = rect.width;
          containerHeight = rect.height;
          console.log('🎯 SMART POSITIONING: Got actual container dimensions:', { containerWidth, containerHeight });
        }
      } catch (error) {
        console.warn('🎯 SMART POSITIONING: Could not get container dimensions, using defaults');
      }
      
      // Calculate the center of the visible area in flow coordinates
      const centerX = (-viewport.x + containerWidth / 2) / zoom;
      const centerY = (-viewport.y + containerHeight / 2) / zoom;
      
      console.log('🎯 SMART POSITIONING: Viewport info:', { viewport, zoom, centerX, centerY });
      
      // Node dimensions (approximate)
      const nodeWidth = 200;
      const nodeHeight = 100;
      const gridSize = 50; // Snap to grid
      const minDistance = 250; // Minimum distance between nodes
      
      // Function to snap to grid
      const snapToGrid = (value) => Math.round(value / gridSize) * gridSize;
      
      // Function to check if a position conflicts with existing nodes
      const hasConflict = (x, y) => {
        return nodes.some(node => {
          const nodeX = node.position.x;
          const nodeY = node.position.y;
          const distance = Math.sqrt(Math.pow(x - nodeX, 2) + Math.pow(y - nodeY, 2));
          return distance < minDistance;
        });
      };
      
      // Try the center position first
      let targetX = snapToGrid(centerX - nodeWidth / 2);
      let targetY = snapToGrid(centerY - nodeHeight / 2);
      
      if (!hasConflict(targetX, targetY)) {
        console.log('🎯 SMART POSITIONING: Using center position:', { x: targetX, y: targetY });
        return { x: targetX, y: targetY };
      }
      
      // If center is occupied, try positions in a spiral pattern around the center
      const maxAttempts = 20;
      const spiralStep = 100;
      
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const radius = attempt * spiralStep;
        const angleStep = Math.PI / 4; // 45 degrees
        
        for (let angle = 0; angle < Math.PI * 2; angle += angleStep) {
          const x = snapToGrid(centerX + Math.cos(angle) * radius);
          const y = snapToGrid(centerY + Math.sin(angle) * radius);
          
          if (!hasConflict(x, y)) {
            console.log(`🎯 SMART POSITIONING: Found position at attempt ${attempt}, angle ${angle}:`, { x, y });
            return { x, y };
          }
        }
      }
      
      // Fallback: find the rightmost position and add to the right
      const rightmostNode = nodes.reduce((rightmost, node) => {
        return node.position.x > rightmost.x ? node.position : rightmost;
      }, { x: 0, y: 0 });
      
      const fallbackX = snapToGrid(rightmostNode.x + minDistance);
      const fallbackY = snapToGrid(rightmostNode.y);
      
      console.log('🎯 SMART POSITIONING: Using fallback position:', { x: fallbackX, y: fallbackY });
      return { x: fallbackX, y: fallbackY };
      
    } catch (error) {
      console.warn('🎯 SMART POSITIONING: Error calculating optimal position, using default:', error);
      // Fallback to simple positioning
      return { x: 250, y: 200 + ((flowData.steps?.length || 0) * 150) };
    }
  }, [reactFlowInstance, nodes, flowData.steps]);

  // Handle adding new step
  const handleAddStep = (stepType) => {
    const newStepId = `step_${Date.now()}`;
    
    // Better step name generation
    const getStepName = (type) => {
      switch (type) {
        case 'message':
          return `Send Message ${(flowData.steps?.length || 0) + 1}`;
        case 'intermediate_message':
          return `Intermediate Message ${(flowData.steps?.length || 0) + 1}`;
        case 'api_call':
          return `API Call ${(flowData.steps?.length || 0) + 1}`;
        case 'collect_input':
          return `Collect Input ${(flowData.steps?.length || 0) + 1}`;
        case 'branch':
          return `Branch Logic ${(flowData.steps?.length || 0) + 1}`;
        case 'variable_operation':
          return `Variable Operation ${(flowData.steps?.length || 0) + 1}`;
        case 'generate_message':
          return `Generate Message ${(flowData.steps?.length || 0) + 1}`;
        case 'knowledge_lookup':
          return `Knowledge Lookup ${(flowData.steps?.length || 0) + 1}`;
        default:
          return `${stepType.replace('_', ' ')} ${(flowData.steps?.length || 0) + 1}`;
      }
    };
    
    // Get optimal position for the new node
    const optimalPosition = findOptimalPosition();
    
    const newStep = {
      id: newStepId,
      type: stepType,
      name: getStepName(stepType),
      order: (flowData.steps?.length || 0) + 1,
      config: getDefaultStepConfig(stepType),
      active: true,
      position: optimalPosition,
      // Add a temporary flag to highlight the new node
      isNewlyCreated: true
    };

    const updatedFlowData = {
      ...flowData,
      steps: [...(flowData.steps || []), newStep]
    };

    onFlowDataChange(updatedFlowData);
    setShowAddMenu(false);
    
    // Remove the highlight after 3 seconds
    setTimeout(() => {
      onFlowDataChange(currentFlowData => {
        if (!currentFlowData || !currentFlowData.steps) return currentFlowData;
        
        const updatedSteps = currentFlowData.steps.map(step => {
          if (step.id === newStepId && step.isNewlyCreated) {
            const { isNewlyCreated, ...stepWithoutFlag } = step;
            return stepWithoutFlag;
          }
          return step;
        });
        
        return {
          ...currentFlowData,
          steps: updatedSteps
        };
      });
    }, 3000);
    
    // Optional: Smoothly pan to the new node if it's outside the visible area
    setTimeout(() => {
      try {
        const viewport = reactFlowInstance.getViewport();
        const zoom = viewport.zoom;
        
        // Check if the new node is visible in the current viewport
        const flowContainer = document.querySelector('.react-flow');
        if (flowContainer) {
          const rect = flowContainer.getBoundingClientRect();
          const nodeScreenX = (optimalPosition.x * zoom) + viewport.x;
          const nodeScreenY = (optimalPosition.y * zoom) + viewport.y;
          
          const isVisible = nodeScreenX >= 0 && nodeScreenX <= rect.width && 
                           nodeScreenY >= 0 && nodeScreenY <= rect.height;
          
          if (!isVisible) {
            // Only center if the node is not visible
            reactFlowInstance.setCenter(optimalPosition.x + 100, optimalPosition.y + 50, { 
              zoom: Math.max(zoom, 0.8), // Don't zoom out too much
              duration: 600 
            });
            console.log('🎯 SMART POSITIONING: Panned to new node (was outside viewport)');
          } else {
            console.log('🎯 SMART POSITIONING: New node is already visible, no panning needed');
          }
        }
      } catch (error) {
        console.warn('🎯 SMART POSITIONING: Could not check node visibility:', error);
      }
    }, 100);
  };

  // Get default config for new steps
  const getDefaultStepConfig = (stepType) => {
    switch (stepType) {
      case 'message':
        return { message: { content: 'Enter your message here...' } };
      case 'intermediate_message':
        return { message: { content: 'Enter your intermediate message here...' } };
      case 'api_call':
        return { 
          apiCall: { 
            apiEndpoint: '', 
            parameters: {},
            errorResponseMapping: [] // Add default empty error response mapping
          } 
        };
      case 'collect_input':
        return { 
          collectInput: { 
            prompt: 'Please provide your input:',
            message: { content: 'Please provide your input:' },
            negativeMessage: { content: 'Sorry, I didn\'t understand your response. Please try again.' }
          } 
        };
      case 'branch':
        return { branch: { conditions: [], trueNextStepId: null, falseNextStepId: null, conditionName: '' } };
      case 'variable_operation':
        return { 
          variableOperation: { 
            operations: []
          } 
        };
      case 'generate_message':
        return { 
          generateMessage: { 
            prompt: 'Enter your prompt for generating a message:',
            message: { content: 'Generated message will appear here.' }
          } 
        };
      case 'knowledge_lookup':
        return {
          knowledgeLookup: {
            query: '',
            resultHandling: {
              selectionType: 'first',
              topN: 5,
              minimumScore: 0.0
            },
            extractVariables: [{
              variableName: '',
              sourcePath: 'metadata.id',
              combineMethod: 'first_only',
              dataType: 'string'
            }],
            fallbackBehavior: 'continue',
            fallbackValues: []
          }
        };
      default:
        return {};
    }
  };

  // Convert frontend format to match flowData.steps format (everything under config)
  const convertFrontendToBackendFormat = useCallback((step) => {
    console.log('=== FRONTEND TO BACKEND CONVERSION START ===');
    console.log('Converting frontend step to flowData.steps format:', step);
    console.log('🔥 STEP TYPE DEBUG:', step.type);
    console.log('Step position:', step.position);
    console.log('Step variableMapping:', step.variableMapping);
    console.log('Step config:', step.config);
    
    // 🔧 DEBUG: End step configuration tracking
    if (step.type === 'end') {
      console.log('🏁 [END STEP CONFIG DEBUG] Processing End step configuration:', {
        stepName: step.name,
        hasConfig: !!step.config,
        hasEndConfig: !!step.config?.end,
        endConfig: step.config?.end,
        persistVariables: step.config?.end?.persistVariables,
        variablesToPersist: step.config?.end?.variablesToPersist,
        manualVariableNames: step.config?.end?.manualVariableNames
      });
    }
    
    // Create base step structure matching flowData.steps format
    const convertedStep = {
      id: step.id,
      name: step.name,
      type: step.type,
      order: step.order,
      active: step.active !== false,
      nextStepId: step.nextStepId || null,
      // Ensure position is always a valid object with x and y coordinates
      position: (step.position && typeof step.position === 'object' && 
                 step.position.x !== undefined && step.position.y !== undefined) 
        ? { x: Number(step.position.x) || 0, y: Number(step.position.y) || 0 }
        : { x: 0, y: 0 }
    };
    
    console.log('🔥 CONVERTED STEP TYPE:', convertedStep.type);

    // Convert variableMapping responseFields back to saveFromResponse format
    let convertedVariableMapping = {
      saveFromInput: [],
      availableVariables: [],
      saveFromResponse: []
    };

    if (step.variableMapping) {
      console.log(`=== VARIABLE MAPPING CONVERSION DEBUG for ${step.type} step ${step.name} ===`);
      console.log('Original step.variableMapping:', step.variableMapping);
      console.log('saveFromInput array:', step.variableMapping.saveFromInput);
      console.log('saveFromInput length:', step.variableMapping.saveFromInput?.length);
      
      // Debug each AI extraction variable to see if extractionModel is present
      if (step.variableMapping.saveFromInput && step.variableMapping.saveFromInput.length > 0) {
        step.variableMapping.saveFromInput.forEach((inputVar, index) => {
          console.log(`🔍 Input Variable ${index + 1}:`, {
            variableName: inputVar.variableName,
            inputType: inputVar.inputType,
            extractionPrompt: inputVar.extractionPrompt,
            extractionModel: inputVar.extractionModel,
            description: inputVar.description
          });
          console.log(`🔍 Input Variable ${index + 1} FULL OBJECT:`, JSON.stringify(inputVar, null, 2));
          console.log(`🔍 Input Variable ${index + 1} extractionModel value:`, inputVar.extractionModel);
          console.log(`🔍 Input Variable ${index + 1} has extractionModel property:`, inputVar.hasOwnProperty('extractionModel'));
        });
      } else {
        console.log('❌ No saveFromInput variables found - this might be why extractionModel is not saving');
      }
      
      // Start with a completely fresh variableMapping to avoid any duplicates
      convertedVariableMapping = {
        saveFromInput: step.variableMapping.saveFromInput || [],
        availableVariables: step.variableMapping.availableVariables || [],
        // DO NOT copy any existing saveFromResponse - we'll rebuild it from responseFields
        saveFromResponse: []
      };
      
      console.log('Initial convertedVariableMapping.saveFromInput:', convertedVariableMapping.saveFromInput);
      
      // 🔥 CRITICAL FIX: Convert BOTH responseFields AND existing saveFromResponse with deduplication
      
      // Collect all variable mappings from different sources
      const allMappings = [];
      
      // First: Add existing saveFromResponse data (NEW FORMAT)
      if (step.variableMapping.saveFromResponse && Array.isArray(step.variableMapping.saveFromResponse)) {
        console.log('🔥 ADDING EXISTING saveFromResponse:', step.variableMapping.saveFromResponse);
        allMappings.push(...step.variableMapping.saveFromResponse);
      }
      
      // Second: Convert legacy responseFields to saveFromResponse format (LEGACY FORMAT)
      if (step.variableMapping.responseFields && Array.isArray(step.variableMapping.responseFields)) {
        console.log('Converting legacy responseFields to saveFromResponse:', step.variableMapping.responseFields);
        
        const legacyConvertedMappings = step.variableMapping.responseFields.map(mapping => ({
          variableName: mapping.variableName,
          sourcePath: mapping.field,
          dataType: 'string',
          description: `Response field mapping for ${mapping.variableName}`,
          arrayHandling: {
            selectionType: 'first'
          }
        }));
        
        allMappings.push(...legacyConvertedMappings);
      }
      
      // Third: Deduplicate all mappings based on variableName + sourcePath combination
      const uniqueMappings = [];
      const seenKeys = new Set();
      
      allMappings.forEach(mapping => {
        const key = `${mapping.variableName}_${mapping.sourcePath}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          uniqueMappings.push(mapping);
          console.log('✅ Adding unique mapping:', mapping.variableName, '→', mapping.sourcePath);
        } else {
          console.log('⚠️ Skipping duplicate mapping:', mapping.variableName, '→', mapping.sourcePath);
        }
      });
      
      // Add saveFullResponse if it exists (before deduplication)
      if (step.variableMapping.saveFullResponse) {
        allMappings.push({
          variableName: step.variableMapping.saveFullResponse,
          sourcePath: '$root',
          dataType: 'object',
          description: `Full API response saved as ${step.variableMapping.saveFullResponse}`,
          arrayHandling: {
            selectionType: 'first'
          }
        });
      }
      
      // Fourth: Final deduplication including saveFullResponse
      const finalUniqueMappings = [];
      const finalSeenKeys = new Set();
      
      allMappings.forEach(mapping => {
        const key = `${mapping.variableName}_${mapping.sourcePath}`;
        if (!finalSeenKeys.has(key)) {
          finalSeenKeys.add(key);
          finalUniqueMappings.push(mapping);
          console.log('✅ Adding unique mapping:', mapping.variableName, '→', mapping.sourcePath);
        } else {
          console.log('⚠️ Skipping duplicate mapping:', mapping.variableName, '→', mapping.sourcePath);
        }
      });
      
      convertedVariableMapping.saveFromResponse = finalUniqueMappings;
      console.log(`Final deduplication: ${allMappings.length} mappings to ${finalUniqueMappings.length} unique mappings`);
      
      console.log('Final converted variableMapping:', convertedVariableMapping);
      console.log('Number of saveFromResponse entries created:', convertedVariableMapping.saveFromResponse.length);
      console.log('=== VARIABLE MAPPING CONVERSION DEBUG END ===');
    }

    // Create config object that contains step-specific configurations
    const config = {
      apiCall: {
        apiEndpoint: step.config?.apiCall?.apiEndpoint || null,
        parameters: step.config?.apiCall?.parameters || {},
        delay: step.config?.apiCall?.delay || 0,
        dynamicParameters: step.config?.apiCall?.dynamicParameters || [],
        // 🔧 POST Body Configuration
        bodyType: step.config?.apiCall?.bodyType || 'none',
        bodyFields: step.config?.apiCall?.bodyFields || [],
        rawBodyTemplate: step.config?.apiCall?.rawBodyTemplate || '',
        rawBodyContentType: step.config?.apiCall?.rawBodyContentType || 'application/json',
        graphqlQuery: step.config?.apiCall?.graphqlQuery || '',
        graphqlVariables: step.config?.apiCall?.graphqlVariables || '',
        // ⚠️ Error Response Mapping
        errorResponseMapping: step.config?.apiCall?.errorResponseMapping || []
      },
      branch: {
        conditions: step.config?.branch?.conditions || [],
        globalLogicOperator: step.config?.branch?.globalLogicOperator || 'AND',
        conditionName: step.config?.branch?.conditionName || '',
        trueNextStepId: step.config?.branch?.trueNextStepId || null,
        falseNextStepId: step.config?.branch?.falseNextStepId || null,
        defaultNextStepId: step.config?.branch?.defaultNextStepId || null
      },
      collectInput: {
        prompt: (() => {
          // Extract primary language content for legacy prompt field
          const messageContent = step.config?.collectInput?.message?.content;
          if (!messageContent) return step.config?.collectInput?.prompt || '';
          
          if (typeof messageContent === 'string') {
            return messageContent;
          } else if (messageContent && typeof messageContent === 'object') {
            // Priority: default > en (English) > id (Indonesian) > first available
            return messageContent.default || messageContent.en || messageContent.id || Object.values(messageContent)[0] || '';
          }
          
          return step.config?.collectInput?.prompt || '';
        })(),
        message: step.config?.collectInput?.message || {},
        negativeMessage: step.config?.collectInput?.negativeMessage || {},
        dynamicPrompt: step.config?.collectInput?.dynamicPrompt || { variableReferences: [] },
        validationRules: step.config?.collectInput?.validationRules || {
          required: true,
          type: 'text',
          choices: []
        },
        validation: step.config?.collectInput?.validation || {
          required: true,
          conditionalRequirements: []
        },
        retryAttempts: step.config?.collectInput?.retryAttempts || 3
      },
      message: {
        content: step.config?.message?.content || '',
        dynamicContent: step.config?.message?.dynamicContent || { variableReferences: [] },
        language: step.config?.message?.language || 'auto',
        variables: step.config?.message?.variables || []
      },
      // Handle intermediate_message the same as message but with different type
      intermediate_message: {
        content: step.config?.message?.content || '',
        dynamicContent: step.config?.message?.dynamicContent || { variableReferences: [] },
        language: step.config?.message?.language || 'auto',
        variables: step.config?.message?.variables || []
      },
      variableOperation: {
        operations: step.config?.variableOperation?.operations || []
      },
      generateMessage: {
        model: step.config?.generateMessage?.model || 'gpt-4o-mini',
        prompt: step.config?.generateMessage?.prompt || '',
        temperature: step.config?.generateMessage?.temperature ?? 0.7,
        maxTokens: step.config?.generateMessage?.maxTokens || 150,
        saveToVariable: step.config?.generateMessage?.saveToVariable || '',
        fallbackMessage: step.config?.generateMessage?.fallbackMessage || 'Thank you for your message.',
        sendImmediately: step.config?.generateMessage?.sendImmediately !== false,
        language: step.config?.generateMessage?.language || 'auto'
      },
      end: {
        persistVariables: step.config?.end?.persistVariables || false,
        variablesToPersist: step.config?.end?.variablesToPersist || [],
        manualVariableNames: step.config?.end?.manualVariableNames || []
      },
      knowledgeLookup: {
        query: step.config?.knowledgeLookup?.query || '',
        resultHandling: {
          selectionType: step.config?.knowledgeLookup?.resultHandling?.selectionType || 'first',
          topN: step.config?.knowledgeLookup?.resultHandling?.topN || 5,
          minimumScore: step.config?.knowledgeLookup?.resultHandling?.minimumScore || 0.0
        },
        extractVariables: step.config?.knowledgeLookup?.extractVariables || [],
        fallbackBehavior: step.config?.knowledgeLookup?.fallbackBehavior || 'continue',
        fallbackValues: step.config?.knowledgeLookup?.fallbackValues || []
      }
    };

    console.log('Converting apiCall config:', step.config?.apiCall, '→', config.apiCall);
    console.log('🔧 BODY CONFIG DEBUG: bodyType preserved:', {
      original: step.config?.apiCall?.bodyType,
      converted: config.apiCall.bodyType,
      bodyFields: config.apiCall.bodyFields?.length || 0
    });
    
    // 🔧 DEBUG: End step configuration conversion tracking
    if (step.type === 'end') {
      console.log('🏁 [END STEP CONFIG DEBUG] End step config conversion:', {
        stepName: step.name,
        originalEndConfig: step.config?.end,
        convertedEndConfig: config.end,
        configObjectKeys: Object.keys(config)
      });
    }

    // 🔧 CRITICAL FIX: Handle variableOperations at the top level (not in config)
    // Prioritize top-level variableOperations over config.variableOperations
    if (step.variableOperations) {
      console.log('🔧 Converting variableOperations from top level:', step.variableOperations);
      convertedStep.variableOperations = step.variableOperations;
    } else if (step.config?.variableOperations) {
      console.log('🔧 Converting variableOperations from config (fallback):', step.config.variableOperations);
      convertedStep.variableOperations = step.config.variableOperations;
    } else {
      // Ensure empty array instead of undefined
      convertedStep.variableOperations = [];
    }
    
    // 🔥 SAVE DEBUG: Log variable operations conversion
    console.log(`🔥 SAVE CONVERSION: Step ${step.name} variableOperations conversion:`, {
      stepId: step.id,
      stepName: step.name,
      hasTopLevel: !!step.variableOperations,
      hasConfigLevel: !!step.config?.variableOperations,
      topLevelCount: step.variableOperations?.length || 0,
      configLevelCount: step.config?.variableOperations?.length || 0,
      finalCount: convertedStep.variableOperations?.length || 0,
      finalOperations: convertedStep.variableOperations
    });

    // Add config to the converted step
    convertedStep.config = config;
    
    // 🔧 CRITICAL FIX: Move step-specific configurations from config to top level where backend expects them
    // Knowledge Lookup configuration should be at top level, not in config
    if (step.type === 'knowledge_lookup' && config.knowledgeLookup) {
      convertedStep.knowledgeLookup = config.knowledgeLookup;
      console.log(`🔧 KNOWLEDGE LOOKUP FIX: Moved knowledgeLookup config to top level for step ${step.name}:`, convertedStep.knowledgeLookup);
    }
    
    // Put variableMapping at ROOT level where backend expects it
    convertedStep.variableMapping = convertedVariableMapping;

    console.log('Converted step (matching flowData.steps format):');
    console.log(convertedStep);
    
    // 🔧 DEBUG: Final End step configuration check
    if (step.type === 'end') {
      console.log('🏁 [END STEP CONFIG DEBUG] Final converted End step:', {
        stepName: convertedStep.name,
        stepType: convertedStep.type,
        hasConfig: !!convertedStep.config,
        hasEndConfig: !!convertedStep.config?.end,
        finalEndConfig: convertedStep.config?.end,
        fullConfig: convertedStep.config
      });
    }
    
    console.log('=== FRONTEND TO BACKEND CONVERSION END ===');
    return convertedStep;
  }, []);

  // Check if connection is valid
  const isValidConnection = useCallback((connection) => {
    // Prevent manual connections from branch nodes - they should only be configured via UI
    const sourceNode = nodes.find(node => node.id === connection.source);
    if (sourceNode && sourceNode.data.type === 'branch') {
      console.log('Manual connections from branch nodes are not allowed - use the configuration panel');
      return false;
    }

    // Top handles (target) can only have one input
    if (connection.targetHandle === null) {
      const existingTargetConnections = edges.filter(edge => 
        edge.target === connection.target && edge.targetHandle === connection.targetHandle
      );
      if (existingTargetConnections.length > 0) {
        console.log('Target already has a connection');
        return false;
      }
    }

    // Bottom handles (source) can only have one output (except for branch nodes)
    if (connection.sourceHandle === null) {
      if (sourceNode && sourceNode.data.type !== 'branch') {
        const existingSourceConnections = edges.filter(edge => 
          edge.source === connection.source && edge.sourceHandle === connection.sourceHandle
        );
        if (existingSourceConnections.length > 0) {
          console.log('Non-branch source already has a connection');
          return false;
        }
      }
    }

    return true;
  }, [edges, nodes]);

  // Handle edge connections (for manual connections)
  const onConnect = useCallback(
    (params) => {
      console.log('🔗 EDGE SYNC: Manual connection:', params);
      
      if (!isValidConnection(params)) {
        console.log('🔗 EDGE SYNC: Connection rejected due to validation rules');
        return;
      }
      
      // Add the edge to the visual representation
      setEdges((eds) => addEdge({
        ...params,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed },
        animated: false
      }, eds));
      
      // Update step configuration - handle ALL types of connections
      const sourceNode = nodes.find(node => node.id === params.source);
      if (sourceNode && params.source !== 'start') {
        const sourceStep = flowData.steps.find(step => step.id === params.source);
        if (sourceStep) {
          let updatedStep = { ...sourceStep };
          
          if (sourceNode.data.type === 'branch') {
            // Handle branch node connections
            if (params.sourceHandle === 'branch-true' || params.sourceHandle === 'branch-false') {
              console.log(`🔗 EDGE SYNC: Updating branch ${params.sourceHandle} connection: ${params.source} -> ${params.target}`);
              updatedStep = {
                ...updatedStep,
                config: {
                  ...updatedStep.config,
                  branch: {
                    ...updatedStep.config?.branch,
                    trueNextStepId: params.sourceHandle === 'branch-true' ? params.target : updatedStep.config?.branch?.trueNextStepId,
                    falseNextStepId: params.sourceHandle === 'branch-false' ? params.target : updatedStep.config?.branch?.falseNextStepId,
                    conditionName: updatedStep.config?.branch?.conditionName || 'Manual Connection'
                  }
                }
              };
            }
          } else {
            // Handle regular step connections (non-branch)
            console.log(`🔗 EDGE SYNC: Updating regular step connection: ${params.source} -> ${params.target}`);
            updatedStep = {
              ...updatedStep,
              nextStepId: params.target === 'end' ? null : params.target
            };
          }
          
          // Convert to backend format and update the flow data
          const backendFormattedStep = convertFrontendToBackendFormat(updatedStep);
          const updatedSteps = flowData.steps.map(step => 
            step.id === updatedStep.id ? backendFormattedStep : step
          );
          
          const newFlowData = {
            ...flowData,
            steps: updatedSteps
          };
          
          console.log('🔗 EDGE SYNC: Updated flowData with new connection:', {
            stepId: updatedStep.id,
            nextStepId: updatedStep.nextStepId,
            branchConfig: updatedStep.config?.branch
          });
          
          // Mark as internal change to prevent useEffect regeneration
          internalChangeRef.current = true;
          onFlowDataChange(newFlowData);
          
          // Update selected node configuration if it's the same node
          if (selectedNodeForConfig && selectedNodeForConfig.nodeId === params.source) {
            setSelectedNodeForConfig({
              nodeId: updatedStep.id,
              step: {
                ...updatedStep,
                onConfigChange: sourceNode.data.type === 'branch' ? handleBranchConfigChange : undefined
              }
            });
          }
        }
      }
    },
    [setEdges, isValidConnection, nodes, flowData, onFlowDataChange, selectedNodeForConfig, handleBranchConfigChange]
  );

  // Handle edge deletion (for manual edge removal)
  const onEdgesDelete = useCallback(
    (edgesToDelete) => {
      console.log('🔗 EDGE SYNC: Edges being deleted:', edgesToDelete);
      
      edgesToDelete.forEach(edge => {
        const sourceNode = nodes.find(node => node.id === edge.source);
        if (sourceNode && edge.source !== 'start') {
          const sourceStep = flowData.steps.find(step => step.id === edge.source);
          if (sourceStep) {
            let updatedStep = { ...sourceStep };
            
            if (sourceNode.data.type === 'branch') {
              // Handle branch node edge deletion
              if (edge.sourceHandle === 'branch-true' || edge.sourceHandle === 'branch-false') {
                console.log(`🔗 EDGE SYNC: Removing branch ${edge.sourceHandle} connection from ${edge.source}`);
                updatedStep = {
                  ...updatedStep,
                  config: {
                    ...updatedStep.config,
                    branch: {
                      ...updatedStep.config?.branch,
                      trueNextStepId: edge.sourceHandle === 'branch-true' ? null : updatedStep.config?.branch?.trueNextStepId,
                      falseNextStepId: edge.sourceHandle === 'branch-false' ? null : updatedStep.config?.branch?.falseNextStepId
                    }
                  }
                };
              }
            } else {
              // Handle regular step edge deletion
              console.log(`🔗 EDGE SYNC: Removing regular step connection from ${edge.source}`);
              updatedStep = {
                ...updatedStep,
                nextStepId: null
              };
            }
            
            // Convert to backend format and update the flow data
            const backendFormattedStep = convertFrontendToBackendFormat(updatedStep);
            const updatedSteps = flowData.steps.map(step => 
              step.id === updatedStep.id ? backendFormattedStep : step
            );
            
            const newFlowData = {
              ...flowData,
              steps: updatedSteps
            };
            
            console.log('🔗 EDGE SYNC: Updated flowData after edge deletion:', {
              stepId: updatedStep.id,
              nextStepId: updatedStep.nextStepId,
              branchConfig: updatedStep.config?.branch
            });
            
            // Mark as internal change to prevent useEffect regeneration
            internalChangeRef.current = true;
            onFlowDataChange(newFlowData);
            
            // Update selected node configuration if it's the same node
            if (selectedNodeForConfig && selectedNodeForConfig.nodeId === edge.source) {
              setSelectedNodeForConfig({
                nodeId: updatedStep.id,
                step: {
                  ...updatedStep,
                  onConfigChange: sourceNode.data.type === 'branch' ? handleBranchConfigChange : undefined
                }
              });
            }
          }
        }
      });
    },
    [nodes, flowData, onFlowDataChange, selectedNodeForConfig, handleBranchConfigChange]
  );

  // Save step edits
  // Force sync all current node positions to flowData
  const syncAllPositions = useCallback(() => {
    console.log('🎯 POSITION SYNC: Forcing sync of all node positions');
    
    if (!flowData || !flowData.steps || !nodes) {
      console.log('🎯 POSITION SYNC: No data to sync');
      return;
    }
    
    // Get current positions from visual nodes
    const positionUpdates = [];
    nodes.forEach(node => {
      if (node.id !== 'start' && node.id !== 'end' && node.position) {
        positionUpdates.push({
          id: node.id,
          position: node.position
        });
      }
    });
    
    if (positionUpdates.length > 0) {
      console.log('🎯 POSITION SYNC: Syncing positions for nodes:', positionUpdates);
      
      onFlowDataChange(currentFlowData => {
        const updatedSteps = currentFlowData.steps.map(step => {
          const positionUpdate = positionUpdates.find(update => update.id === step.id);
          if (positionUpdate) {
            console.log(`🎯 POSITION SYNC: Syncing position for step ${step.id}:`, {
              old: step.position,
              new: positionUpdate.position
            });
            return {
              ...step,
              position: positionUpdate.position
            };
          }
          return step;
        });
        
        // Mark as internal change to prevent useEffect regeneration
        internalChangeRef.current = true;
        
        // Reset flag after a brief delay to ensure it doesn't interfere with saving
        setTimeout(() => {
          internalChangeRef.current = false;
        }, 50);
        
        return {
          ...currentFlowData,
          steps: updatedSteps
        };
      });
    }
  }, [flowData, nodes, onFlowDataChange]);

  // Force sync all current edge connections to flowData
  const syncAllEdges = useCallback(() => {
    console.log('🔗 EDGE SYNC: Forcing sync of all edge connections');
    
    if (!flowData || !flowData.steps || !edges || !nodes) {
      console.log('🔗 EDGE SYNC: No data to sync');
      return;
    }
    
    // Build connection map from current edges
    const connectionUpdates = new Map();
    
    // Initialize all steps with no connections
    flowData.steps.forEach(step => {
      connectionUpdates.set(step.id, {
        id: step.id,
        nextStepId: null,
        branchConnections: {
          trueNextStepId: null,
          falseNextStepId: null
        }
      });
    });
    
    // Process current edges to build connection map
    edges.forEach(edge => {
      if (edge.source === 'start' || edge.source === 'end') return;
      
      const sourceNode = nodes.find(n => n.id === edge.source);
      if (!sourceNode) return;
      
      const update = connectionUpdates.get(edge.source);
      if (!update) return;
      
      if (sourceNode.data.type === 'branch') {
        // Handle branch connections
        if (edge.sourceHandle === 'branch-true') {
          update.branchConnections.trueNextStepId = edge.target === 'end' ? null : edge.target;
        } else if (edge.sourceHandle === 'branch-false') {
          update.branchConnections.falseNextStepId = edge.target === 'end' ? null : edge.target;
        }
      } else {
        // Handle regular step connections
        update.nextStepId = edge.target === 'end' ? null : edge.target;
      }
    });
    
    console.log('🔗 EDGE SYNC: Connection updates to apply:', Array.from(connectionUpdates.values()));
    
    // Apply connection updates to flowData
    onFlowDataChange(currentFlowData => {
      const updatedSteps = currentFlowData.steps.map(step => {
        const connectionUpdate = connectionUpdates.get(step.id);
        if (!connectionUpdate) return step;
        
        const sourceNode = nodes.find(n => n.id === step.id);
        let updatedStep = { ...step };
        
        if (sourceNode?.data.type === 'branch') {
          // Update branch connections
          console.log(`🔗 EDGE SYNC: Updating branch step ${step.id} connections:`, connectionUpdate.branchConnections);
          updatedStep = {
            ...updatedStep,
            config: {
              ...updatedStep.config,
              branch: {
                ...updatedStep.config?.branch,
                trueNextStepId: connectionUpdate.branchConnections.trueNextStepId,
                falseNextStepId: connectionUpdate.branchConnections.falseNextStepId
              }
            }
          };
        } else {
          // Update regular step connection
          if (updatedStep.nextStepId !== connectionUpdate.nextStepId) {
            console.log(`🔗 EDGE SYNC: Updating step ${step.id} nextStepId:`, {
              old: updatedStep.nextStepId,
              new: connectionUpdate.nextStepId
            });
            updatedStep.nextStepId = connectionUpdate.nextStepId;
          }
        }
        
        return updatedStep;
      });
      
      // Mark as internal change to prevent useEffect regeneration
      internalChangeRef.current = true;
      
      // Reset flag after a brief delay to ensure it doesn't interfere with saving
      setTimeout(() => {
        internalChangeRef.current = false;
      }, 50);
      
      return {
        ...currentFlowData,
        steps: updatedSteps
      };
    });
  }, [flowData, edges, nodes, onFlowDataChange]);

  // Function to reset the internal change flag
  const resetInternalChangeFlag = useCallback(() => {
    console.log('🔒 RESET: Resetting internalChangeRef flag for save operation');
    internalChangeRef.current = false;
  }, []);

  // 🎯 ENHANCED APPROACH: Apply current configuration changes to flowData immediately
  // This ensures that any pending changes in the configuration panel are committed to the main flow data
  const applyCurrentConfigChanges = useCallback(() => {
    console.log('🔒 [APPLY CONFIG] Function called');
    console.log('🔒 [APPLY CONFIG] selectedNodeForConfig exists:', !!selectedNodeForConfig);
    console.log('🔒 [APPLY CONFIG] selectedNodeForConfig.step exists:', !!selectedNodeForConfig?.step);
    
    if (selectedNodeForConfig && selectedNodeForConfig.step) {
      // Convert frontend format to backend format
      const backendFormattedStep = convertFrontendToBackendFormat(selectedNodeForConfig.step);
      
              // Update the corresponding step in flowData.steps
        const currentFlowData = flowData;
        const updatedSteps = currentFlowData.steps?.map(step => {
          if (step.id === selectedNodeForConfig.step.id) {
            return backendFormattedStep;
          }
          return step;
        }) || [];
      
      const updatedFlowData = {
        ...currentFlowData,
        steps: updatedSteps
      };
      
              // Update both flowData and nodes array
        const currentNodes = nodes;
        const updatedNodes = currentNodes.map(node => {
          if (node.id === selectedNodeForConfig.nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                step: selectedNodeForConfig.step
              }
            };
          }
          return node;
        });
        
        setNodes(updatedNodes);
        
        // 🎯 CRITICAL: Set a flag to prevent the useEffect from overriding these changes
        // during the save operation
        internalChangeRef.current = true;
        onFlowDataChange(updatedFlowData);
              return true; // Return true to indicate changes were applied
      } else {
        return false; // Return false to indicate no changes were applied
      }
  }, [selectedNodeForConfig, flowData, nodes, onFlowDataChange, setNodes]);

  // Store the latest state in refs for the window functions to access
  const latestFlowDataRef = useRef(flowData);
  const latestNodesRef = useRef(nodes);
  const latestSelectedNodeForConfigRef = useRef(selectedNodeForConfig);
  
  // Update refs when state changes
  useEffect(() => {
    latestFlowDataRef.current = flowData;
  }, [flowData]);
  
  useEffect(() => {
    latestNodesRef.current = nodes;
  }, [nodes]);
  
  useEffect(() => {
    latestSelectedNodeForConfigRef.current = selectedNodeForConfig;
  }, [selectedNodeForConfig]);

  // Create a stable version of applyCurrentConfigChanges that doesn't depend on state
  const stableApplyCurrentConfigChanges = useCallback(() => {
    console.log('🔒 [STABLE APPLY CONFIG] Function called');
    const currentSelectedNodeForConfig = latestSelectedNodeForConfigRef.current;
    const currentFlowData = latestFlowDataRef.current;
    const currentNodes = latestNodesRef.current;
    
    console.log('🔒 [STABLE APPLY CONFIG] selectedNodeForConfig exists:', !!currentSelectedNodeForConfig);
    console.log('🔒 [STABLE APPLY CONFIG] selectedNodeForConfig.step exists:', !!currentSelectedNodeForConfig?.step);
    
    if (currentSelectedNodeForConfig && currentSelectedNodeForConfig.step) {
      
      // Convert frontend format to backend format
      const backendFormattedStep = convertFrontendToBackendFormat(currentSelectedNodeForConfig.step);
      
      // Update the corresponding step in flowData.steps
              const updatedSteps = currentFlowData.steps?.map(step => {
          if (step.id === currentSelectedNodeForConfig.step.id) {
            return backendFormattedStep;
          }
          return step;
        }) || [];
      
      const updatedFlowData = {
        ...currentFlowData,
        steps: updatedSteps
      };
      
      // Update both flowData and nodes array
              const updatedNodes = currentNodes.map(node => {
          if (node.id === currentSelectedNodeForConfig.nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                step: currentSelectedNodeForConfig.step
              }
            };
          }
          return node;
        });
      setNodes(updatedNodes);
      
      // 🎯 CRITICAL: Set a flag to prevent the useEffect from overriding these changes
      // during the save operation
      internalChangeRef.current = true;
      onFlowDataChange(updatedFlowData);
      
      return true; // Return true to indicate changes were applied
    } else {
      return false; // Return false to indicate no changes were applied
    }
  }, [setNodes, onFlowDataChange]);

  // Expose sync functions globally so the parent FlowEditor can call them before saving
  useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log('🔧 [WINDOW SETUP] Setting up window functions');
      window.syncVisualFlowPositions = syncAllPositions;
      window.syncVisualFlowEdges = syncAllEdges;
      window.resetInternalChangeFlag = resetInternalChangeFlag;
      window.applyCurrentConfigChanges = stableApplyCurrentConfigChanges;
      
      console.log('🔧 [WINDOW SETUP] Functions exposed to window:', {
        syncVisualFlowPositions: typeof window.syncVisualFlowPositions,
        syncVisualFlowEdges: typeof window.syncVisualFlowEdges,
        resetInternalChangeFlag: typeof window.resetInternalChangeFlag,
        applyCurrentConfigChanges: typeof window.applyCurrentConfigChanges
      });
    }
    
    return () => {
      if (typeof window !== 'undefined') {
        console.log('🔧 [WINDOW CLEANUP] Cleaning up window functions');
        delete window.syncVisualFlowPositions;
        delete window.syncVisualFlowEdges;
        delete window.resetInternalChangeFlag;
        delete window.applyCurrentConfigChanges;
      }
    };
  }, [syncAllPositions, syncAllEdges, resetInternalChangeFlag, stableApplyCurrentConfigChanges]);

    // Update selectedNodeForConfig when flowData changes after a save operation
  // This useEffect ensures that the configuration panel always shows the most up-to-date
  // data from flowData, which is critical when the flow is saved while the panel is open.
  // Without this, the configuration panel would still show the old data after saving,
  // and any new changes would be lost when closing the panel.
  useEffect(() => {
    // 🎯 CRITICAL: Don't update during internal changes (like when applying config changes)
    // to prevent race conditions during save operations
    if (internalChangeRef.current) {
      console.log('⚙️ [CONFIG PANEL] Skipping selectedNodeForConfig update due to internal change flag');
      return;
    }
    
    if (selectedNodeForConfig && selectedNodeForConfig.nodeId) {
      // Find the updated step data from flowData
      const updatedStep = flowData.steps?.find(step => step.id === selectedNodeForConfig.nodeId);
      
      if (updatedStep) {
        console.log('⚙️ [CONFIG PANEL] Updating selectedNodeForConfig with latest data from flowData');
        
                            // 🔥 CRITICAL FIX: Preserve temporary UI state when updating from flowData
                    const currentTempCondition = selectedNodeForConfig.step?._tempCondition;
                    const currentTempMapping = selectedNodeForConfig.step?._tempMapping;
                    const currentTempVariableOperation = selectedNodeForConfig.step?._tempVariableOperation;
                    const currentIsEditingOperation = selectedNodeForConfig.step?._isEditingOperation;
                    console.log('⚙️ [CONFIG PANEL] Preserving _tempCondition:', currentTempCondition);
                    console.log('⚙️ [CONFIG PANEL] Preserving _tempMapping:', currentTempMapping);
                    console.log('⚙️ [CONFIG PANEL] Preserving _tempVariableOperation:', currentTempVariableOperation);
                    console.log('⚙️ [CONFIG PANEL] Preserving _isEditingOperation:', currentIsEditingOperation);
        
                  setSelectedNodeForConfig({
            nodeId: selectedNodeForConfig.nodeId,
            step: {
              ...updatedStep,
              _tempCondition: currentTempCondition, // Preserve temporary UI state for branch conditions
              _tempMapping: currentTempMapping, // Preserve temporary UI state for response field mappings
              _tempVariableOperation: currentTempVariableOperation, // Preserve temporary UI state for variable operations
              _isEditingOperation: currentIsEditingOperation, // Preserve edit mode state for variable operations
              onConfigChange: updatedStep.type === 'branch' ? handleBranchConfigChange : undefined
            }
          });
      }
    }
  }, [flowData, handleBranchConfigChange]);

  const handleSaveStepEdit = (updatedStep) => {
    console.log('🎯 STEP EDIT SAVE: handleSaveStepEdit called with:', updatedStep);
    console.log('🎯 STEP EDIT SAVE: Step position before conversion:', updatedStep.position);
    
    // 🔧 DEBUG: End step save tracking
    if (updatedStep.type === 'end') {
      console.log('🏁 [END STEP SAVE DEBUG] Saving End step configuration:', {
        stepName: updatedStep.name,
        hasConfig: !!updatedStep.config,
        hasEndConfig: !!updatedStep.config?.end,
        endConfigBeforeSave: updatedStep.config?.end,
        persistVariables: updatedStep.config?.end?.persistVariables,
        variablesToPersist: updatedStep.config?.end?.variablesToPersist,
        manualVariableNames: updatedStep.config?.end?.manualVariableNames
      });
    }
    
    // Convert frontend format to backend format before saving
    const backendFormattedStep = convertFrontendToBackendFormat(updatedStep);
    console.log('🎯 STEP EDIT SAVE: Step position after conversion:', backendFormattedStep.position);
    
    // 🔧 DEBUG: End step after conversion
    if (updatedStep.type === 'end') {
      console.log('🏁 [END STEP SAVE DEBUG] End step after conversion:', {
        stepName: backendFormattedStep.name,
        hasConfig: !!backendFormattedStep.config,
        hasEndConfig: !!backendFormattedStep.config?.end,
        endConfigAfterSave: backendFormattedStep.config?.end
      });
    }
    
    const updatedSteps = flowData.steps?.map(step => 
      step.id === updatedStep.id ? backendFormattedStep : step
    ) || [];
    
    const updatedFlowData = {
      ...flowData,
      steps: updatedSteps
    };
    
    console.log('🎯 STEP EDIT SAVE: Final updatedFlowData steps with positions:', updatedFlowData.steps.map(s => ({ id: s.id, name: s.name, position: s.position })));
    
    // 🔗 FORCE REGENERATION: Always allow full regeneration for step edits
    // This ensures that any changes (connections, names, configurations) are immediately reflected
    console.log('🔗 STEP EDIT SAVE: Forcing edge and node regeneration for step edit');
    // Do NOT set internalChangeRef.current = true here
    
    onFlowDataChange(updatedFlowData);
    setShowStepEditor(false);
    setEditingStep(null);
  };

  return (
    <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Flow Information Panel */}
      {showFlowInfo && (
        <Paper sx={{ p: 2, mb: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" color="primary">
              Visual Flow Builder
            </Typography>
            <IconButton size="small" onClick={() => setShowFlowInfo(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Flow Name"
                value={flowData?.name || ''}
                onChange={(e) => onFlowDataChange({ ...flowData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={flowData?.description || ''}
                onChange={(e) => onFlowDataChange({ ...flowData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Linked Intents</InputLabel>
                <Select
                  multiple
                  value={flowData?.linkedIntents || []}
                  onChange={(e) => onFlowDataChange({ 
                    ...flowData, 
                    linkedIntents: e.target.value 
                  })}
                  input={<OutlinedInput label="Linked Intents" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const intent = availableIntents.find(i => i._id === value);
                        return (
                          <Chip 
                            key={value} 
                            label={intent?.name || 'Unknown'} 
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {availableIntents.map((intent) => (
                    <MenuItem key={intent._id} value={intent._id}>
                      {intent.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ 
                border: '1px solid #e0e0e0', 
                borderRadius: 1, 
                p: 1.5, 
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                  Flow Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ 
                    color: !flowData?.active ? '#6b7280' : '#9ca3af',
                    fontSize: '0.75rem'
                  }}>
                    Inactive
                  </Typography>
                  <Switch
                    size="small"
                    checked={flowData?.active !== false}
                    onChange={(e) => onFlowDataChange({ 
                      ...flowData, 
                      active: e.target.checked 
                    })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#10b981',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#10b981',
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ 
                    color: flowData?.active !== false ? '#10b981' : '#9ca3af',
                    fontSize: '0.75rem'
                  }}>
                    Active
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip 
              label={`${flowData?.steps?.length || 0} Steps`} 
              size="small" 
              variant="outlined" 
            />
            <Chip 
              label={`${flowData?.linkedIntents?.length || 0} Intents`} 
              size="small" 
              variant="outlined" 
            />
            <Chip 
              label={flowData?.active !== false ? 'Active' : 'Inactive'}
              size="small" 
              variant="filled"
              sx={{
                backgroundColor: flowData?.active !== false ? '#10b981' : '#ef4444',
                color: 'white',
                fontWeight: 500
              }}
            />
            <Chip 
              label="Click nodes to edit • Drag to connect • Delete key to remove edges" 
              size="small" 
              variant="outlined"
              color="info"
            />
          </Box>
        </Paper>
      )}

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: '500px' }}>
        {/* Visual Flow Canvas */}
        <Box sx={{ flex: selectedNodeForConfig ? 2 : 1, position: 'relative' }}>
          <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgesDelete={onEdgesDelete}
          onNodeDragStop={onNodeDragStop}
          isValidConnection={isValidConnection}
          nodeTypes={nodeTypes}
          fitView
          style={{ backgroundColor: '#f8fafc', width: '100%', height: '100%' }}
          defaultEdgeOptions={{
            type: 'smoothstep',
            markerEnd: { type: MarkerType.ArrowClosed },
          }}
          connectionMode="loose"
          snapToGrid
          snapGrid={[15, 15]}
        >
          <Background color="#e2e8f0" gap={20} />
          <Controls />
          <MiniMap 
            style={{ backgroundColor: '#f1f5f9' }}
            maskColor="rgba(100, 116, 139, 0.1)"
          />
        </ReactFlow>

        {/* Instructions Panel */}
        <Paper 
          sx={{ 
            position: 'absolute', 
            top: 20, 
            left: 20, 
            p: 2,
            maxWidth: 280,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            fontSize: '12px',
            borderLeft: '4px solid #2563eb'
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ color: '#2563eb', fontWeight: 600 }}>
            Flow Builder Controls
          </Typography>
          <div style={{ lineHeight: '1.4' }}>
            <div>• <strong>Click nodes</strong> to edit configuration</div>
            <div>• <strong>Drag from handles</strong> to connect steps</div>
            <div>• <strong>Select edge + Delete key</strong> to remove</div>
            <div>• <strong>+ Button</strong> to add new steps</div>
          </div>
        </Paper>

        {/* Add Step Button */}
        <Tooltip 
          title="Add new step - will be positioned in the center of your current view!"
          placement="left"
          arrow
        >
          <Fab
            color="primary"
            sx={{ position: 'absolute', bottom: 20, right: 20 }}
            onClick={() => setShowAddMenu(!showAddMenu)}
          >
            <AddIcon />
          </Fab>
        </Tooltip>

        {/* Add Step Menu */}
        {showAddMenu && (
          <Paper 
            sx={{ 
              position: 'absolute', 
              bottom: 80, 
              right: 20, 
              p: 2,
              minWidth: 200,
              boxShadow: 4
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Add New Step
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280', mb: 2, display: 'block' }}>
              🎯 Smart positioning: New steps appear in your current view
            </Typography>
            {stepTypes.map((stepType) => (
              <Button
                key={stepType.type}
                fullWidth
                variant="outlined"
                startIcon={stepType.icon}
                onClick={() => handleAddStep(stepType.type)}
                sx={{ 
                  mb: 1, 
                  justifyContent: 'flex-start',
                  borderColor: stepType.color,
                  color: stepType.color,
                  '&:hover': {
                    backgroundColor: `${stepType.color}15`,
                    borderColor: stepType.color
                  }
                }}
              >
                {stepType.label}
              </Button>
            ))}
          </Paper>
        )}

        {/* Show Flow Info Button (when hidden) */}
        {!showFlowInfo && (
          <Fab
            size="small"
            color="secondary"
            sx={{ position: 'absolute', top: 20, right: 20 }}
            onClick={() => setShowFlowInfo(true)}
          >
            <InfoIcon />
          </Fab>
        )}
        </Box>

        {/* Node Configuration Panel */}
        {selectedNodeForConfig && (
          <Box sx={{ 
            width: 450, 
            borderLeft: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                  {selectedNodeForConfig.step ? 'Step Configuration' : 'Node Details'}
                </Typography>
                <Chip 
                  label="💾 Changes auto-sync on save" 
                  size="small" 
                  sx={{ 
                    backgroundColor: '#10b981', 
                    color: '#065f46',
                    fontSize: '0.7rem',
                    height: 20
                  }} 
                />
              </Box>
              <IconButton 
                size="small" 
                onClick={() => {
                  console.log('🔒 CLOSING CONFIG PANEL: Applying changes from selectedNodeForConfig.step to flowData');
                  
                  // Apply changes from selectedNodeForConfig.step back to flowData.steps
                  if (selectedNodeForConfig.step) {
                    const updatedStep = selectedNodeForConfig.step;
                    console.log('🔒 Step to apply:', updatedStep);
                    console.log(updatedStep);
                    
                    // Convert frontend format to backend format
                    const backendFormattedStep = convertFrontendToBackendFormat(updatedStep);

                    console.log("FlowData.Steps: ", flowData.steps);
                    
                    // Update the corresponding step in flowData.steps
                    const updatedSteps = flowData.steps?.map(step => 
                      step.id === updatedStep.id ? backendFormattedStep : step
                    ) || [];


                    console.log("Updated Steps: ", updatedSteps);
                    
                    const updatedFlowData = {
                      ...flowData,
                      steps: updatedSteps
                    };

                    console.log("Nodes: ", nodes);
                    
                    // 🔒 CRITICAL: Also update the corresponding node in the nodes array
                    const updatedNodes = nodes.map(node => {
                      if (node.id === selectedNodeForConfig.nodeId) {
                        console.log('🔒 UPDATING NODE DATA for node:', node.id);
                        console.log('🔒 Old node.data.step:', node.data.step);
                        console.log('🔒 New node.data.step:', updatedStep);
                        
                        return {
                          ...node,
                          data: {
                            ...node.data,
                            step: updatedStep
                          }
                        };
                      }
                      return node;
                    });
                    
                    console.log('🔒 NODES UPDATED - Setting new nodes array');
                    setNodes(updatedNodes);
                    
                    console.log('🔒 APPLYING FINAL CHANGES to flowData before closing panel');
                    console.log("Updated Flow Data: ",updatedFlowData);
                    
                    // Ensure the flag doesn't interfere with the save operation
                    internalChangeRef.current = false;
                    onFlowDataChange(updatedFlowData);
                  }
                  
                  setSelectedNodeForConfig(null);
                }}
                sx={{ color: '#6b7280' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Configuration Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              {selectedNodeForConfig.step ? (
                <>
                  <Box sx={{ 
                    mb: 2, 
                    p: 2, 
                    backgroundColor: '#f0f9ff', 
                    border: '1px solid #0ea5e9',
                    borderRadius: 1
                  }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#0369a1', fontWeight: 500 }}>
                      💡 <strong>Pro tip:</strong> Click "Save Flow" anytime - your configuration changes will be automatically included!
                    </Typography>
                  </Box>
                  <NodeConfigurationForm 
                  step={selectedNodeForConfig.step}
                  onStepChange={(updatedStep) => {
                    console.log('🎯 [VisualFlowEditor] onStepChange called - updating selectedNodeForConfig.step immediately');
                    console.log('🎯 [VisualFlowEditor] Updated step received:', {
                      id: updatedStep.id,
                      name: updatedStep.name,
                      messageContent: updatedStep.config?.message?.content,
                      apiCall: updatedStep.config?.apiCall?.apiEndpoint,
                      responseFields: updatedStep.variableMapping?.responseFields?.length || 0,
                      tempCondition: updatedStep._tempCondition // DEBUG: Track temp condition
                    });
                    
                    // Remove duplicates from responseFields if they exist
                    if (updatedStep.variableMapping?.responseFields) {
                      const uniqueFields = [];
                      const seen = new Set();
                      updatedStep.variableMapping.responseFields.forEach(field => {
                        const key = `${field.variableName}_${field.field}`;
                        if (!seen.has(key)) {
                          seen.add(key);
                          uniqueFields.push(field);
                        }
                      });
                      
                      if (uniqueFields.length !== updatedStep.variableMapping.responseFields.length) {
                        console.log('🧹 Removing duplicate responseFields');
                        updatedStep = {
                          ...updatedStep,
                          variableMapping: {
                            ...updatedStep.variableMapping,
                            responseFields: uniqueFields
                          }
                        };
                      }
                    }
                    
                    // 🔥 CRITICAL FIX: Preserve temporary UI state during state updates
                    // Get the current temporary state from selectedNodeForConfig.step
                    const currentTempCondition = selectedNodeForConfig?.step?._tempCondition;
                    const currentTempMapping = selectedNodeForConfig?.step?._tempMapping;
                    const currentTempVariableOperation = selectedNodeForConfig?.step?._tempVariableOperation;
                    const currentIsEditingOperation = selectedNodeForConfig?.step?._isEditingOperation;
                    const incomingTempCondition = updatedStep._tempCondition;
                    const incomingTempMapping = updatedStep._tempMapping;
                    const incomingTempVariableOperation = updatedStep._tempVariableOperation;
                    const incomingIsEditingOperation = updatedStep._isEditingOperation;
                    
                    // Use the incoming temp state if provided, otherwise preserve current one
                    const preservedTempCondition = incomingTempCondition || currentTempCondition;
                    const preservedTempMapping = incomingTempMapping || currentTempMapping;
                    const preservedTempVariableOperation = incomingTempVariableOperation || currentTempVariableOperation;
                    const preservedIsEditingOperation = incomingIsEditingOperation !== undefined ? incomingIsEditingOperation : currentIsEditingOperation;
                    
                    console.log('🔥 TEMP STATE PRESERVATION:', {
                      tempCondition: {
                        current: currentTempCondition,
                        incoming: incomingTempCondition,
                        preserved: preservedTempCondition
                      },
                      tempMapping: {
                        current: currentTempMapping,
                        incoming: incomingTempMapping,
                        preserved: preservedTempMapping
                      },
                      tempVariableOperation: {
                        current: currentTempVariableOperation,
                        incoming: incomingTempVariableOperation,
                        preserved: preservedTempVariableOperation
                      },
                      isEditingOperation: {
                        current: currentIsEditingOperation,
                        incoming: incomingIsEditingOperation,
                        preserved: preservedIsEditingOperation
                      }
                    });
                    
                    const stepWithPreservedTempState = {
                      ...updatedStep,
                      _tempCondition: preservedTempCondition,
                      _tempMapping: preservedTempMapping,
                      _tempVariableOperation: preservedTempVariableOperation,
                      _isEditingOperation: preservedIsEditingOperation
                    };
                    
                    // 🎯 IMMEDIATE UPDATE: Always keep selectedNodeForConfig.step current with latest changes
                    setSelectedNodeForConfig(prev => ({
                      ...prev,
                      step: stepWithPreservedTempState
                    }));
                    
                    console.log('✅ selectedNodeForConfig.step updated immediately with latest changes');
                    
                    // 🎯 CRITICAL FIX: Also immediately update the main flowData.steps and nodes
                    // This ensures that changes are reflected in the main flow data without needing to close the panel
                    console.log('🔄 IMMEDIATE SYNC: Applying changes to main flowData immediately');
                    
                    const backendFormattedStep = convertFrontendToBackendFormat(stepWithPreservedTempState);
                    
                    // Update flowData.steps
                    const updatedSteps = flowData.steps?.map(step => 
                      step.id === stepWithPreservedTempState.id ? backendFormattedStep : step
                    ) || [];
                    
                    const updatedFlowData = {
                      ...flowData,
                      steps: updatedSteps
                    };
                    
                    // Update nodes - preserve temporary UI state in node data too
                    const updatedNodes = nodes.map(node => {
                      if (node.id === selectedNodeForConfig.nodeId) {
                        return {
                          ...node,
                          data: {
                            ...node.data,
                            step: stepWithPreservedTempState
                          }
                        };
                      }
                      return node;
                    });
                    
                    // Apply updates immediately
                    internalChangeRef.current = true;
                    setNodes(updatedNodes);
                    onFlowDataChange(updatedFlowData);
                    
                    console.log('✅ Main flowData and nodes updated immediately with configuration changes');
                  }}
                  availableApiEndpoints={availableApiEndpoints}
                  availableVariables={gatherAvailableVariables()}
                  flowSteps={flowData.steps || []}
                />
                </>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Select a step node to configure its settings.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Step Editor Dialog */}
      <Dialog 
        open={showStepEditor} 
        onClose={() => setShowStepEditor(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Step: {editingStep?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Step Type: {editingStep?.type?.replace('_', ' ')?.toUpperCase()}
          </Typography>
          <Typography variant="body2" color="info.main">
            For detailed configuration, please use the List View editor.
            This dialog will be enhanced in future updates.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStepEditor(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Wrapper component that provides ReactFlow context
const VisualFlowEditorWithProvider = (props) => {
  return (
    <ReactFlowProvider>
      <VisualFlowEditor {...props} />
    </ReactFlowProvider>
  );
};

export default VisualFlowEditorWithProvider; 